// JavaScript source code
// GOOGLE SHEETS INTEGRATION
class GoogleSheetsIntegration {
	constructor() {
		this.webAppUrl =
			"https://script.google.com/macros/s/AKfycbwru2A0dbJ4lYu9URlcboI3mBn9hKKBllHrmMCeDfzj6VXWKv6q0lFQpmlYPY7IoJam/exec";
	}

	async saveOrderToGoogleSheets(orderData) {
		try {
			console.log("Sending order to Google Sheets:", orderData);

			// Try fetch first
			try {
				const response = await fetch(this.webAppUrl, {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded"
					},
					body: new URLSearchParams({
						action: "saveOrder",
						data: JSON.stringify(orderData)
					})
				});

				console.log("Fetch request completed");
				return { success: true, message: "Order sent successfully" };
			} catch (fetchError) {
				console.log("Fetch failed, trying form method:", fetchError);

				// Fallback to your original form method but with better error handling
				return this.fallbackFormSubmission(orderData);
			}
		} catch (error) {
			console.error("Error saving to Google Sheets:", error);
			throw error;
		}
	}

	fallbackFormSubmission(orderData) {
		return new Promise((resolve) => {
			const form = document.createElement("form");
			form.method = "POST";
			form.action = this.webAppUrl;
			form.target = "hidden_iframe_" + Date.now();

			const actionField = document.createElement("input");
			actionField.name = "action";
			actionField.value = "saveOrder";
			form.appendChild(actionField);

			const dataField = document.createElement("input");
			dataField.name = "data";
			dataField.value = JSON.stringify(orderData);
			form.appendChild(dataField);

			const iframe = document.createElement("iframe");
			iframe.name = form.target;
			iframe.style.display = "none";
			document.body.appendChild(iframe);
			document.body.appendChild(form);

			form.submit();

			setTimeout(() => {
				document.body.removeChild(form);
				document.body.removeChild(iframe);
				resolve({ success: true, message: "Form submitted" });
			}, 2000);
		});
	}
}

class RailwayAPI {
    constructor() {
        // Requests now go through Shopify's App Proxy (same-origin, on this
        // store's own domain) instead of directly to Railway — Shopify signs
        // each request server-to-server, so there's no API key for the
        // browser to hold at all anymore. Same proxy config as skips.js.
        this.baseUrl = '/apps/skip-api';
    }

    async saveSelection(orderData) {
        try {
            console.log('💾 Saving to Railway:', orderData.weekId);
			console.log('📋 Full payload:', orderData); 
            
            const response = await fetch(`${this.baseUrl}/meal-selection`, {  
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(orderData)
            });

            const result = await response.json();
            
            if (result.success) {
                console.log('✅ Railway save successful');
                return { success: true, data: result };
            } else {
                throw new Error('Railway save failed: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('❌ Railway save error:', error);
            return { success: false, error: error.message };
        }
    }

    async loadSelections(customerId, weeks = 4) {
        try {
            console.log(`☁️ Loading from Railway: customer ${customerId}, ${weeks} weeks`);
            
            const response = await fetch(`${this.baseUrl}/meal-selection/${customerId}?weeks=${weeks}`, { 
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            
            if (result.success) {
                console.log('✅ Railway load successful:', Object.keys(result.selections).length, 'weeks');
                return { success: true, selections: result.selections };
            } else {
                throw new Error('Railway load failed');
            }
        } catch (error) {
            console.error('❌ Railway load error:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteSelection(customerId, weekId) {
        try {
            console.log(`🗑️ Deleting from Railway: customer ${customerId}, week ${weekId}`);
            
            const response = await fetch(
                `${this.baseUrl}/meal-selection/${customerId}/${weekId}`, 
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            const result = await response.json();
            
            if (result.success) {
                console.log('✅ Railway delete successful');
                return { success: true, data: result };
            } else {
                throw new Error('Railway delete failed');
            }
        } catch (error) {
            console.error('❌ Railway delete error:', error);
            return { success: false, error: error.message };
        }
    }

    // SIDES (à la carte) — catalog is public read, no api key needed
    async getAlaCarteCatalog(weekId) {
        try {
            const response = await fetch(`${this.baseUrl}/ala-carte/${weekId}`);
            const result = await response.json();
            if (result.success) {
                return { success: true, items: result.items || [] };
            }
            throw new Error('Failed to load sides catalog');
        } catch (error) {
            console.error('❌ Sides catalog load error:', error);
            return { success: false, items: [], error: error.message };
        }
    }

    async saveAlaCarteSelection(payload) {
        try {
            const response = await fetch(`${this.baseUrl}/ala-carte-selection`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (result.success) {
                return { success: true, data: result };
            }
            throw new Error('Sides save failed: ' + (result.error || 'Unknown error'));
        } catch (error) {
            console.error('❌ Sides save error:', error);
            return { success: false, error: error.message };
        }
    }

    async getAlaCarteSelection(customerId, deliveryDate) {
        try {
            const response = await fetch(
                `${this.baseUrl}/ala-carte-selection/${customerId}/${deliveryDate}`
            );
            const result = await response.json();
            if (result.success) {
                return { success: true, selections: result.selections || [] };
            }
            throw new Error('Failed to load existing sides selection');
        } catch (error) {
            console.error('❌ Sides selection load error:', error);
            return { success: false, selections: [], error: error.message };
        }
    }

    // Railway-authoritative meal plan size / subscription status — used to
    // correct the initial tag-based guess made at construction time, since
    // Shopify tags are only ever a fallback on the backend (see
    // ExtractMealPlanFromTags in program.cs) and shouldn't be primary here
    // either. Same auth model as everything else customer-facing.
    async getCustomer(customerId) {
        try {
            const response = await fetch(`${this.baseUrl}/customer/${customerId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            return await response.json();
        } catch (error) {
            console.error('❌ Get customer error:', error);
            return { success: false };
        }
    }
}

class LoyaltyAPI {
    constructor() {
        this.baseUrl = '/apps/skip-api';
    }

    async getBalance(customerId) {
        try {
            const response = await fetch(`${this.baseUrl}/loyalty/balance/${customerId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            return await response.json();
        } catch (error) {
            console.error('❌ Loyalty balance error:', error);
            return { success: false };
        }
    }

    async redeem(customerId, tierId) {
        try {
            const response = await fetch(`${this.baseUrl}/loyalty/redeem`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ customerId, tierId })
            });
            return await response.json();
        } catch (error) {
            console.error('❌ Loyalty redeem error:', error);
            return { success: false };
        }
    }
}

class LoyaltyPortal {
    constructor(customerId) {
        this.customerId = customerId;
        this.api = new LoyaltyAPI();
        this.balance = 0;
        this.pendingRedemptions = [];
        this.loaded = false;

        this.TIERS = [
            // Listed in order of points cost (ids stay the same — the backend uses them)
            { id: 2, points: 450,  meals: 0, proteinBalls: true,  desc: "1 order of protein balls", tags: ["8 protein balls added"] },
            { id: 1, points: 500,  meals: 1, proteinBalls: false, desc: "1 free meal",           tags: ["1 meal added to delivery"] },
            { id: 3, points: 1300, meals: 3, proteinBalls: false, desc: "3 free meals",    		tags: ["3 meals added to delivery"] }, //save: "Save 200 pts vs. separate" },
            { id: 4, points: 2500, meals: 6, proteinBalls: false, desc: "6 free meals",           tags: ["6 meals added to delivery"] }
        ];

        // Check if we're in the blocked redemption window (Tue 4PM - Wed 7AM Pacific)
        this.isBlockedWindow = this.checkBlockedWindow();
    }

    checkBlockedWindow() {
        const now = new Date();
        // Convert to Pacific time
        const pacificTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
        const day = pacificTime.getDay(); // 0=Sun, 2=Tue, 3=Wed
        const hour = pacificTime.getHours();
        return (day === 2 && hour >= 16) || (day === 3 && hour < 7);
    }

    switchTab(tab) {
        // Update tab buttons
        document.querySelectorAll('.portal-tab').forEach(t => t.classList.remove('portal-tab--active'));
        document.getElementById(`tab-${tab}`).classList.add('portal-tab--active');

        // Show/hide panels
        document.getElementById('panel-meals').style.display = tab === 'meals' ? 'block' : 'none';
        document.getElementById('panel-rewards').style.display = tab === 'rewards' ? 'block' : 'none';

        if (tab === 'rewards' && !this.loaded) {
            this.load();
        }
    }

    openTab() {
        this.switchTab('rewards');
    }

    async load() {
        const container = document.getElementById('loyalty-portal-container');
        if (!container) return;

        container.innerHTML = '<p style="text-align:center; padding: 2rem;">Loading your rewards...</p>';

        const result = await this.api.getBalance(this.customerId);
        if (!result.success) {
            container.innerHTML = '<p style="text-align:center; color:#666;">Could not load rewards. Please try again.</p>';
            return;
        }

        this.balance = result.balance;
        this.pendingRedemptions = result.pendingRedemptions || [];
        this.loaded = true;

        // Update teaser stat card
        const teaser = document.getElementById('loyalty-points-teaser');
        if (teaser) teaser.textContent = `${this.balance.toLocaleString()} pts`;

        this.render(container, result);
    }

    render(container, data) {
        const history = data.history || [];
        
        container.innerHTML = `
            <div style="max-width: 680px; margin: 0 auto;">

                <!-- Points Hero -->
                <div style="background: #f8f9fa; border-radius: 12px; border: 1px solid #e1e3e5; padding: 1.5rem; margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;">
                    <div>
                        <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #666; margin-bottom: 4px;">Your rewards balance</div>
                        <div style="font-size: 42px; font-weight: 500; color: #1a1a1a; line-height: 1;">${this.balance.toLocaleString()}<span style="font-size: 16px; color: #666; margin-left: 6px;">pts</span></div>
                        <div style="margin-top: 8px; display: inline-flex; align-items: center; gap: 6px; background: #d4edda; color: #155724; font-size: 12px; font-weight: 500; padding: 4px 10px; border-radius: 10px;">
                            $1 spent = 1 point
                        </div>
                    </div>
                    <div style="text-align: right;">
                        <div style="font-size: 12px; color: #666; margin-bottom: 4px;">Points cap</div>
                        <div style="font-size: 15px; font-weight: 500; color: #1a1a1a;">7,000 pts max</div>
                        <div style="font-size: 11px; color: #999; margin-top: 4px;">${Math.max(0, 7000 - this.balance).toLocaleString()} pts until cap</div>
                    </div>
                </div>

                <!-- Blocked Window Notice -->
                ${this.isBlockedWindow ? `
                <div style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 1rem; font-size: 13px; color: #856404;">
                    ⏳ Redemptions are temporarily unavailable while this week's orders are being processed. Check back Wednesday morning after 7 AM.
                </div>` : ''}

                <!-- Rules Box -->
                <div style="border-left: 3px solid #ccc; border-radius: 0 8px 8px 0; padding: 12px 14px; margin-bottom: 1.25rem; background: #f8f9fa; font-size: 13px; color: #666;">
                    <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 6px;">How rewards work</div>
                    <ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px;">
                        <li style="display: flex; gap: 8px; line-height: 1.5;"><span style="color: #999;">·</span> Redeemed meals and protein balls are added to your next delivery in addition to your existing meal plan. Your regular meal plan remains unchanged and will still be billed as usual.</li>
                        <li style="display: flex; gap: 8px; line-height: 1.5;"><span style="color: #999;">·</span> Redemptions apply to your next upcoming delivery and cannot be moved to a future date.</li>
                        <li style="display: flex; gap: 8px; line-height: 1.5;"><span style="color: #999;">·</span> Points are awarded automatically after each successful billing.</li>
                    </ul>
                </div>

                <!-- Pending Redemptions -->
                ${this.pendingRedemptions.length > 0 ? `
                <div style="background: #d4edda; border: 1px solid #c3e6cb; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 1.25rem;">
                    <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #155724; margin-bottom: 8px;">Pending Redemptions</div>
                    ${this.pendingRedemptions.map(r => `
                        <div style="font-size: 13px; color: #155724; display: flex; justify-content: space-between; padding: 4px 0;">
                            <span>🎁 ${r.mealsQuantity > 0 ? `${r.mealsQuantity} free meal${r.mealsQuantity > 1 ? 's' : ''}` : ''}${r.mealsQuantity > 0 && r.includeProteinBalls ? ' + ' : ''}${r.includeProteinBalls ? 'Protein balls' : ''}</span>
                            <span style="color: #666; font-size: 12px;">Applied to next delivery</span>
                        </div>
                        ${r.mealsQuantity > 0 ? `
                        <div style="font-size: 13px; color: #155724; background: #ffffff; border: 1px solid #c3e6cb; border-radius: 6px; padding: 8px 10px; margin: 2px 0 6px;">
                            👉 Select your <strong>${r.mealsQuantity} extra meal${r.mealsQuantity > 1 ? 's' : ''}</strong> from the menu for your next delivery, on top of your regular plan. If you don't, our chef will choose ${r.mealsQuantity > 1 ? 'them' : 'it'} for you.
                        </div>` : ''}
                    `).join('')}
                </div>` : ''}

                <!-- Tiers -->
                <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: #666; margin-bottom: 10px;">Redeem your points</div>
                <div id="loyalty-tiers" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-bottom: 1.5rem;"></div>

                <!-- Redeem Bar -->
                <div id="loyalty-redeem-bar" style="background: #f8f9fa; border: 1px solid #e1e3e5; border-radius: 12px; padding: 1rem 1.25rem; text-align: center; color: #666; font-size: 14px; margin-bottom: 1.5rem;">
                    Select a reward above to redeem your points
                </div>

                <!-- Activity History -->
                <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: #666; margin-bottom: 10px;">Recent activity</div>
                ${history.length === 0 ? '<p style="color: #666; font-size: 13px;">No activity yet — points will appear here after your next billing.</p>' : 
                    history.map((h, i) => `
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: ${i < history.length - 1 ? '1px solid #e1e3e5' : 'none'}; font-size: 13px;">
                            <div>
                                <div style="color: #1a1a1a;">${h.description}</div>
                                <div style="color: #999; font-size: 12px; margin-top: 2px;">${new Date(h.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                            </div>
                            <div style="font-weight: 500; color: ${h.points > 0 ? '#155724' : '#666'};">${h.points > 0 ? '+' : ''}${h.points}</div>
                        </div>
                    `).join('')}
            </div>
        `;

        this.renderTiers();
    }

    selectedTierId = null;

    renderTiers() {
        const grid = document.getElementById('loyalty-tiers');
        if (!grid) return;

        grid.innerHTML = '';
        this.TIERS.forEach(tier => {
            const unlocked = this.balance >= tier.points;
            const isSelected = this.selectedTierId === tier.id;
            const isBlocked = this.isBlockedWindow;

            // Check for conflicts with pending redemptions
            const hasConflict = this.pendingRedemptions.some(r => 
                (tier.meals > 0 && r.mealsQuantity > 0) || 
                (tier.proteinBalls && r.includeProteinBalls)
            );

            const card = document.createElement('div');
            card.style.cssText = `
                background: ${isSelected ? '#e8f4fd' : '#fff'};
                border: ${isSelected ? '2px solid #0078d4' : '1px solid #e1e3e5'};
                border-radius: 12px;
                padding: 1rem;
                cursor: ${unlocked && !isBlocked && !hasConflict ? 'pointer' : 'not-allowed'};
                opacity: ${!unlocked || hasConflict ? '0.5' : '1'};
                transition: border-color 0.15s;
                position: relative;
            `;

            card.innerHTML = `
                <div style="font-size: 13px; font-weight: 500; color: ${isSelected ? '#0078d4' : '#666'}; margin-bottom: 6px;">${tier.points.toLocaleString()} pts</div>
                <div style="font-size: 15px; font-weight: 500; color: #1a1a1a; line-height: 1.3;">${tier.desc}</div>
                <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px;">
                    ${tier.tags.map(tag => `<span style="font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500; background: ${isSelected ? '#fff' : '#f0f0f0'}; color: ${isSelected ? '#0078d4' : '#666'}; border: 1px solid ${isSelected ? '#0078d4' : '#e1e3e5'};">${tag}</span>`).join('')}
                </div>
                ${tier.save ? `<div style="display: inline-block; font-size: 10px; font-weight: 500; padding: 2px 7px; border-radius: 10px; background: #d4edda; color: #155724; margin-top: 8px;">${tier.save}</div>` : ''}
                ${!unlocked ? `<div style="font-size: 11px; color: #999; margin-top: 6px;">Need ${(tier.points - this.balance).toLocaleString()} more pts</div>` : ''}
                ${hasConflict ? `<div style="font-size: 11px; color: #856404; margin-top: 6px;">Already have a pending redemption</div>` : ''}
                ${!unlocked ? `<svg style="position:absolute;top:10px;right:10px;width:16px;height:16px;opacity:0.4;" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="2" stroke="currentColor" stroke-width="1.2"/><path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>` : ''}
            `;

            if (unlocked && !isBlocked && !hasConflict) {
                card.addEventListener('click', () => {
                    this.selectedTierId = isSelected ? null : tier.id;
                    this.renderTiers();
                    this.renderRedeemBar();
                });
            }

            grid.appendChild(card);
        });

        this.renderRedeemBar();
    }

    renderRedeemBar() {
        const bar = document.getElementById('loyalty-redeem-bar');
        if (!bar) return;

        if (!this.selectedTierId) {
            bar.innerHTML = '<span style="color: #666; font-size: 14px;">Select a reward above to redeem your points</span>';
            return;
        }

        const tier = this.TIERS.find(t => t.id === this.selectedTierId);
        const remaining = this.balance - tier.points;

        bar.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem;">
                <div style="text-align: left;">
                    <div style="font-size: 14px; color: #1a1a1a;">Redeeming <strong>${tier.desc}</strong> for <strong>${tier.points.toLocaleString()} pts</strong></div>
                    <div style="font-size: 12px; color: #666; margin-top: 3px;">${remaining.toLocaleString()} pts remaining · Added to next delivery on top of your plan</div>
                </div>
                <button type="button" id="loyalty-confirm-btn" style="background: #910F3F; color: white; border: none; border-radius: 8px; padding: 10px 20px; font-size: 14px; font-weight: 500; cursor: pointer; white-space: nowrap; font-family: inherit;">
                    Confirm redemption
                </button>
            </div>
        `;

        document.getElementById('loyalty-confirm-btn').addEventListener('click', () => this.confirmRedemption(tier));
    }

    async confirmRedemption(tier) {
        const btn = document.getElementById('loyalty-confirm-btn');
        if (btn) {
            btn.textContent = 'Processing...';
            btn.disabled = true;
        }

        const result = await this.api.redeem(this.customerId, tier.id);

        if (result.success) {
            this.balance = result.newBalance;
            this.selectedTierId = null;
            this.loaded = false; // Force reload to get updated pending redemptions

            // Notify the meal selector so the redemption banner and save-time
            // reminder go live immediately, instead of only appearing after a
            // page reload. loadPendingRedemptions() re-fetches /loyalty/balance
            // and updates pendingMealRedemptions; renderRedemptionBanner() and
            // updateOrderSummary() then reflect that on-screen right away if a
            // week is currently selected.
            if (window.mealSelector) {
                try {
                    await window.mealSelector.loadPendingRedemptions();
                    window.mealSelector.renderRedemptionBanner();
                    window.mealSelector.updateOrderSummary();
                } catch (error) {
                    console.warn('Could not refresh meal selector after redemption:', error);
                }
            }

            const bar = document.getElementById('loyalty-redeem-bar');
            if (bar) {
                bar.style.background = '#d4edda';
                bar.style.border = '1px solid #c3e6cb';
                bar.innerHTML = `
                    <div style="text-align: center; padding: 0.5rem;">
                        <div style="font-size: 16px; font-weight: 500; color: #155724; margin-bottom: 4px;">✅ Redemption confirmed!</div>
                        <div style="font-size: 13px; color: #155724; opacity: 0.8;">Your reward will be added to your next delivery on top of your existing meal plan.</div>
                    </div>
                `;
            }

            // Update teaser
            const teaser = document.getElementById('loyalty-points-teaser');
            if (teaser) teaser.textContent = `${this.balance.toLocaleString()} pts`;

            // Reload after 2 seconds to show updated state
            setTimeout(() => this.load(), 2000);
        } else {
            alert(result.error || 'Redemption failed. Please try again.');
            if (btn) {
                btn.textContent = 'Confirm redemption';
                btn.disabled = false;
            }
        }
    }
}

class SyncLogger {
	constructor(customerId) {
		this.customerId = customerId;
		this.storageKey = `sync_logs_${customerId}`;
		this.maxLogs = 100; 
	}

	log(event, data = {}) {
		try {
			const logEntry = {
				timestamp: new Date().toISOString(),
				event: event,
				customerId: this.customerId,
				...data
			};

			// Console log for debugging
			const emoji = this.getEventEmoji(event);
			console.log(`${emoji} [SYNC LOG] ${event}:`, data);

			// Store in localStorage
			this.saveToStorage(logEntry);

			// Send critical events to Google Sheets for monitoring
			if (this.isCriticalEvent(event)) {
				this.sendToSheets(logEntry);
			}
		} catch (error) {
			console.error("Failed to log event:", error);
		}
	}

	getEventEmoji(event) {
		const emojiMap = {
			load_local_start: "📂",
			load_local_success: "✅",
			load_local_error: "❌",
			load_cloud_start: "☁️",
			load_cloud_success: "✅",
			load_cloud_error: "❌",
			load_cloud_timeout: "⏰",
			sync_merge_start: "🔀",
			sync_merge_success: "✅",
			sync_conflict_detected: "⚠️",
			save_local_start: "💾",
			save_local_success: "✅",
			save_local_error: "❌",
			save_cloud_start: "☁️",
			save_cloud_success: "✅",
			save_cloud_error: "❌",
			background_sync_start: "🔄",
			background_sync_success: "✅",
			background_sync_error: "❌",
			data_cleanup: "🧹",
			version_mismatch: "⚠️",
			storage_quota_exceeded: "💥"
		};
		return emojiMap[event] || "📝";
	}

	isCriticalEvent(event) {
		const criticalEvents = [
			"save_cloud_error",
			"load_cloud_error",
			"sync_conflict_detected",
			"storage_quota_exceeded",
			"data_cleanup"
		];
		return criticalEvents.includes(event);
	}

	saveToStorage(logEntry) {
		try {
			let logs = [];
			const existing = localStorage.getItem(this.storageKey);

			if (existing) {
				logs = JSON.parse(existing);
			}

			logs.push(logEntry);

			// Keep only the most recent logs
			if (logs.length > this.maxLogs) {
				logs = logs.slice(-this.maxLogs);
			}

			localStorage.setItem(this.storageKey, JSON.stringify(logs));
		} catch (error) {
			console.error("Failed to save log to storage:", error);
		}
	}

	async sendToSheets(logEntry) {
		try {
			// Send critical logs to Google Sheets for monitoring
			const payload = {
				action: "logSyncEvent",
				data: JSON.stringify(logEntry)
			};

			// Fire and forget - don't wait for response
			const form = document.createElement("form");
			form.method = "POST";
			form.action =
				"https://script.google.com/macros/s/AKfycbxJUyhzeGXvHY7ZyznEW8IVhREaScHIb2S0yNHK9xq2MNr0byx_96f_gkYfW8WWO1NM/exec";
			form.target = `log_iframe_${Date.now()}`;
			form.style.display = "none";

			Object.keys(payload).forEach((key) => {
				const input = document.createElement("input");
				input.type = "hidden";
				input.name = key;
				input.value = payload[key];
				form.appendChild(input);
			});

			const iframe = document.createElement("iframe");
			iframe.name = form.target;
			iframe.style.display = "none";

			document.body.appendChild(iframe);
			document.body.appendChild(form);
			form.submit();

			setTimeout(() => {
				if (document.body.contains(form)) document.body.removeChild(form);
				if (document.body.contains(iframe)) document.body.removeChild(iframe);
			}, 2000);
		} catch (error) {
			console.error("Failed to send log to sheets:", error);
		}
	}

	getLogs(filter = {}) {
		try {
			const logs = JSON.parse(localStorage.getItem(this.storageKey) || "[]");

			if (filter.event) {
				return logs.filter((log) => log.event === filter.event);
			}

			if (filter.startDate) {
				const startDate = new Date(filter.startDate);
				return logs.filter((log) => new Date(log.timestamp) >= startDate);
			}

			return logs;
		} catch (error) {
			console.error("Failed to get logs:", error);
			return [];
		}
	}

	exportLogs() {
		const logs = this.getLogs();
		const dataStr = JSON.stringify(logs, null, 2);
		const dataBlob = new Blob([dataStr], { type: "application/json" });

		const url = URL.createObjectURL(dataBlob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `sync-logs-${this.customerId}-${
			new Date().toISOString().split("T")[0]
		}.json`;
		link.click();

		URL.revokeObjectURL(url);
	}

	clearLogs() {
		localStorage.removeItem(this.storageKey);
		console.log("🧹 Sync logs cleared");
	}
}

// Make logger available globally for debugging
window.SyncLogger = SyncLogger;

class SyncStatusManager {
	constructor() {
		this.indicator = null;
		this.currentStatus = null;
		this.hideTimeout = null;
	}

	init() {
		this.indicator = document.getElementById("sync-status-indicator");
		if (!this.indicator) {
			console.warn("Sync status indicator element not found");
		}
	}

	show(status, message, duration = 3000) {
		if (!this.indicator) return;

		// Clear any existing hide timeout
		if (this.hideTimeout) {
			clearTimeout(this.hideTimeout);
		}

		// Update status
		this.currentStatus = status;

		// Remove all status classes
		this.indicator.className = "sync-status-indicator";

		// Add new status class
		this.indicator.classList.add(`sync-status-indicator--${status}`);

		// Update text
		const textElement = this.indicator.querySelector(".sync-text");
		if (textElement) {
			textElement.textContent = message;
		}

		// Show indicator
		this.indicator.style.display = "block";

		// Auto-hide after duration (except for syncing state)
		if (status !== "syncing" && duration > 0) {
			this.hideTimeout = setTimeout(() => {
				this.hide();
			}, duration);
		}
	}

	hide() {
		if (!this.indicator) return;

		this.indicator.style.animation = "slideOutRight 0.3s ease-in";

		setTimeout(() => {
			this.indicator.style.display = "none";
			this.indicator.style.animation = "";
			this.currentStatus = null;
		}, 300);
	}

	showSyncing() {
		this.show("syncing", "Syncing your meals...", 0);
	}

	showSuccess(message = "Synced successfully") {
		this.show("success", message, 3000);
	}

	showError(message = "Sync failed - saved locally") {
		this.show("error", message, 5000);
	}

	showOffline() {
		this.show("offline", "Offline - will sync when connected", 5000);
	}

	getCurrentStatus() {
		return this.currentStatus;
	}
}

// CUSTOMER PORTAL
class CustomerPortal {
	constructor(mealSelector) {
		this.mealSelector = mealSelector;
		this.customerId = mealSelector.customer.id;
		this.customerEmail = mealSelector.customer.email;
		this.emailInProgress = false;
		this.emailHistoryHandler = null;
		this.refreshHandler = null;
		this.init();
	}

	init() {
		// Small delay to ensure meal selector is fully initialized
		setTimeout(() => {
			this.loadPortalData();
		}, 100);
		this.bindPortalEvents();
		console.log("Customer Portal initialized");
	}

	bindPortalEvents() {
		const emailBtn = document.getElementById("download-history");
		const clearBtn = document.getElementById("clear-data");

		console.log("📧 Binding events - email button found:", !!emailBtn);

		if (emailBtn) {
			if (this.emailHistoryHandler) {
				emailBtn.removeEventListener("click", this.emailHistoryHandler);
			}

			this.emailHistoryHandler = (e) => {
				e.preventDefault();
				e.stopPropagation();
				console.log("📧 EMAIL BUTTON CLICKED!");
				this.emailHistory();
			};
			emailBtn.addEventListener("click", this.emailHistoryHandler);
		}

		if (clearBtn) {
			clearBtn.style.display = "inline-flex";

			if (this.refreshHandler) {
				clearBtn.removeEventListener("click", this.refreshHandler);
			}

			this.refreshHandler = (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.refreshStaleData();
			};
			clearBtn.addEventListener("click", this.refreshHandler);
		}
		// Delegate click handler for cancel buttons (handles dynamically rendered cards)
		const historyContainer = document.getElementById(
			"customer-meal-history-portal"
		);
		if (historyContainer) {
			historyContainer.addEventListener("click", (e) => {
				const btn = e.target.closest(".cancel-delivery-btn");
				if (!btn || btn.disabled) return;

				const weekId = btn.dataset.weekId;
				const displayName = btn.dataset.display;
				this.cancelDelivery(weekId, displayName);
			});
		}
	}
	clearActiveSelections() {
		console.log("🔄 Clear active selections requested");

		// Get the currently selected week
		const selectedWeek = this.mealSelector.selectedWeek;

		if (!selectedWeek) {
			alert("Please select a delivery week first.");
			return;
		}

		// Don't allow clearing past deadline weeks
		if (selectedWeek.isDeadlinePassed) {
			alert(
				"Cannot clear - this delivery deadline has passed.\n\nYou can only clear selections for upcoming deliveries."
			);
			return;
		}

		const weekId = selectedWeek.id;

		// Check if there's anything to clear
		const currentQuantities = this.mealSelector.mealQuantities[weekId] || {};
		const hasMeals = Object.values(currentQuantities).some((qty) => qty > 0);
		const hasProteinBalls = Object.values(
			this.mealSelector.proteinBallSelections[weekId] || {}
		).some(sel => sel && sel !== "none");

		if (!hasMeals && !hasProteinBalls) {
			alert(
				"Nothing to clear - your selections are already empty for " +
					selectedWeek.display
			);
			return;
		}

		const confirmed = confirm(
			"Clear your current selections for " +
				selectedWeek.display +
				"?\n\n" +
				"✓ This will reset what you see on screen\n" +
				"✓ Your saved order will remain until you save new selections\n\n" +
				"Continue?"
		);

		if (!confirmed) return;

		// Clear UI only (doesn't touch localStorage or Google Sheets)
		this.mealSelector.mealQuantities[weekId] = {};
		this.mealSelector.mealOptions[weekId] = {};
		this.mealSelector.proteinBallSelections[weekId] = {};

		// Refresh display
		this.mealSelector.renderMeals();
		this.mealSelector.updateOrderSummary();

		// Scroll to top
		window.scrollTo({ top: 0, behavior: "smooth" });

		console.log("✅ UI cleared for", weekId);

		// Show success message
		setTimeout(() => {
			alert(
				"✅ Selections cleared for " +
					selectedWeek.display +
					"!\n\nYou can now start fresh with your meal choices."
			);
		}, 300);
	}
	async loadPortalData() {
		try {
			console.log("Loading portal data...");

			const weeklySelections = this.mealSelector.weeklySelections || {};
			const mealQuantities = this.mealSelector.mealQuantities || {};
			const mealOptions = this.mealSelector.mealOptions || {};

			const portalData = {};

			Object.keys(weeklySelections).forEach((weekId) => {
				if (
					weeklySelections[weekId] &&
					(weeklySelections[weekId].meals || weeklySelections[weekId].proteinBalls)
				) {
					portalData[weekId] = weeklySelections[weekId];
				}
			});
			const availableWeeks = this.mealSelector.getAvailableWeeks();
			console.log("Available weeks from selector:", availableWeeks);

			// Process each available week instead of hardcoded weeks
			availableWeeks.forEach((week) => {
				const weekId = week.id;

				if (!portalData[weekId]) {
					const weekQuantities = mealQuantities[weekId] || {};
					const weekOptions = mealOptions[weekId] || {};

					const hasMeals = Object.values(weekQuantities).some((qty) => qty > 0);

					if (hasMeals) {
						const convertedData = this.convertSelectorDataToPortalFormat(
							{ [weekId]: { hasSelection: true } },
							{ [weekId]: weekQuantities },
							{ [weekId]: weekOptions },
							week // Pass the week object for date/display info
						);

						if (convertedData[weekId]) {
							portalData[weekId] = convertedData[weekId];
						}
					}
				}
			});

			if (Object.keys(portalData).length > 0) {
				console.log("🔍 Final portal data keys:", Object.keys(portalData));
				console.log("📊 About to call renderPortalStats with data:", portalData);
				await this.prefetchSidesForPortal(Object.keys(portalData));
				this.renderPortalStats(portalData);
				this.renderMealHistory(portalData);
			} else {
				this.renderEmptyPortal();
			}
		} catch (error) {
			console.error("Error loading portal data:", error);
			this.renderEmptyPortal();
		}
	}

	// Fetches this customer's saved Sides selection for every upcoming Sunday
	// delivery date, caching by deliveryDate so the synchronous HTML builders
	// below (renderMealsList) can just look it up rather than needing to be
	// rewritten as async themselves. Sides only ever apply to Sunday, so
	// Wednesday weekIds are skipped entirely.
	async prefetchSidesForPortal(weekIds) {
		this.sidesForDelivery = this.sidesForDelivery || {};
		const customerId = this.mealSelector.customer && this.mealSelector.customer.id;
		if (!customerId) return;

		const sundayWeekIds = weekIds.filter((id) => id.startsWith("sunday"));
		await Promise.all(sundayWeekIds.map(async (weekId) => {
			const deliveryDate = this.mealSelector.getDeliveryDateFromWeekId(weekId);
			if (!deliveryDate) return;
			try {
				const result = await this.mealSelector.railwayAPI.getAlaCarteSelection(customerId, deliveryDate);
				this.sidesForDelivery[deliveryDate] = (result.selections || []).filter(s => s.quantity > 0);
			} catch (error) {
				console.warn(`Could not load sides for ${deliveryDate}:`, error);
				this.sidesForDelivery[deliveryDate] = [];
			}
		}));
	}

	// FILTER DATA TO ONLY INCLUDE UPCOMING WEEKS FOR STATS
	getUpcomingWeeksOnly(data) {
		const upcomingData = {};
		const availableWeeks = this.mealSelector.getAvailableWeeks();

		// Get list of week IDs that are NOT past deadline
		const upcomingWeekIds = availableWeeks
			.filter((week) => !week.isDeadlinePassed)
			.map((week) => week.id);

		// Only include data for upcoming weeks
		Object.entries(data).forEach(([weekId, weekData]) => {
			if (weekId === "lastUpdated" || weekId === "customerInfo") return;

			if (upcomingWeekIds.includes(weekId)) {
				upcomingData[weekId] = weekData;
			}
		});

		return upcomingData;
	}

	// UPDATE in CustomerPortal class - renderPortalStats method
	renderPortalStats(data) {
		console.log("📊 renderPortalStats called with data keys:", Object.keys(data));

		try {
			const subscriptionEl = document.getElementById("meal-subscription");
			const totalMealsEl = document.getElementById("total-meals");
			const extraChargesEl = document.getElementById("extra-charges");

			// Check if user has a subscription
			if (!this.mealSelector.mealPlan) {
				// No subscription case
				if (subscriptionEl) {
					subscriptionEl.textContent = "No Active Subscription";
					subscriptionEl.style.color = "#856404";
				}
				if (totalMealsEl) {
					totalMealsEl.textContent = "N/A";
				}
				if (extraChargesEl) {
					extraChargesEl.textContent = "N/A";
				}

				console.log("📊 No subscription - stats display updated");
				return;
			}

			// Original code for users WITH subscription continues...
			const upcomingWeeks = this.getUpcomingWeeksOnly(data);
			console.log(
				"📊 Upcoming weeks after filtering:",
				Object.keys(upcomingWeeks)
			);

			let totalMeals = 0;
			let totalAddOnCharges = 0;

			Object.entries(upcomingWeeks).forEach(([weekId, weekData]) => {
				console.log(`📊 Processing week ${weekId}:`, weekData);

				if (weekData.meals) {
					const weekMealCount = weekData.meals.reduce(
						(sum, meal) => sum + meal.quantity,
						0
					);
					totalMeals += weekMealCount;

					// meal.totalAddOnCost doesn't exist on meals loaded from Railway
					// (they carry raw optionDetails, not a precomputed total) — so this
					// always summed to 0, silently dropping extra protein/sauce/etc.
					// Compute it the same way renderMealsList() does: sum each
					// selected option's price × the meal's quantity.
					const calculatedAddOnCost = weekData.meals.reduce((sum, meal) => {
						const optionDetails = meal.optionDetails || meal.OptionDetails;
						if (!optionDetails) return sum;
						const mealAddOnCost = Object.values(optionDetails).reduce((optSum, option) => {
							return option.isSelected ? optSum + (option.price || 0) * (meal.quantity || 0) : optSum;
						}, 0);
						return sum + mealAddOnCost;
					}, 0);
					totalAddOnCharges += calculatedAddOnCost;

					console.log(
						`📊 Week ${weekId}: ${weekMealCount} meals, $${calculatedAddOnCost} add-ons`
					);
				}
				if (weekData.proteinBalls && weekData.proteinBalls.price > 0) {
					totalAddOnCharges += weekData.proteinBalls.price;
					console.log(
						`📊 Week ${weekId}: +$${weekData.proteinBalls.price} protein balls`
					);
				}

				// Sides — same weekId-to-deliveryDate lookup used in renderMealsList().
				// Sides are Sunday-only, so a Wednesday weekId simply finds nothing.
				if (weekId.startsWith("sunday")) {
					const deliveryDate = this.mealSelector.getDeliveryDateFromWeekId(weekId);
					const sidesForThisDelivery = deliveryDate && this.sidesForDelivery
						? (this.sidesForDelivery[deliveryDate] || [])
						: [];
					const sidesCost = sidesForThisDelivery.reduce(
						(sum, side) => sum + (side.price_at_order || 0) * (side.quantity || 0),
						0
					);
					if (sidesCost > 0) {
						totalAddOnCharges += sidesCost;
						console.log(`📊 Week ${weekId}: +$${sidesCost} sides`);
					}
				}
			});

			console.log(
				`📊 Final totals: ${totalMeals} meals, $${totalAddOnCharges} add-ons`
			);

			if (subscriptionEl) {
				subscriptionEl.textContent =
					this.mealSelector.mealPlan?.planName || "Not Subscribed";
				subscriptionEl.style.color = ""; // Reset color to default
			}
			if (totalMealsEl) {
				totalMealsEl.textContent = totalMeals;
			}
			if (extraChargesEl) {
				extraChargesEl.textContent = "$" + totalAddOnCharges.toFixed(2);
			}

			console.log("📊 Stats updated successfully");
		} catch (error) {
			console.error("📊 Error in renderPortalStats:", error);
		}
	}

	renderMealHistory(data) {
		console.log("🔍 renderMealHistory called with data keys:", Object.keys(data));

		const container = document.getElementById("customer-meal-history-portal");
		if (!container) {
			console.log("❌ Container not found: customer-meal-history-portal");
			return;
		}

		const orders = [];
		const now = new Date();

		Object.entries(data).forEach(([weekId, weekData]) => {
			if (weekId === "lastUpdated" || weekId === "customerInfo") return;

			console.log(`🔍 Processing weekId: ${weekId}`, weekData);

			const weekInfo = this.mealSelector.getWeekInfo(weekId);
			let deliveryDate;
			try {
				deliveryDate = new Date(weekData.deliveryDate);
				if (isNaN(deliveryDate.getTime())) {
					deliveryDate = new Date(weekInfo.date + "T00:00:00");
				}
			} catch (error) {
				deliveryDate = new Date(weekInfo.date + "T00:00:00");
			}

			let deliveryDisplay = weekData.deliveryDisplay;
			if (!deliveryDisplay) {
				if (weekInfo && weekInfo.display) {
					deliveryDisplay = weekInfo.display;
				} else {
					// Fallback: create display from weekId
					const parts = weekId.split("-");
					if (parts.length === 4) {
						const dayType = parts[0];
						const year = parts[1];
						const month = parts[2];
						const day = parts[3];

						const date = new Date(year, month - 1, day);
						const dayName = dayType.charAt(0).toUpperCase() + dayType.slice(1);
						const monthNames = [
							"January",
							"February",
							"March",
							"April",
							"May",
							"June",
							"July",
							"August",
							"September",
							"October",
							"November",
							"December"
						];
						const monthName = monthNames[date.getMonth()];
						const dayNum = date.getDate();

						deliveryDisplay = `${dayName} ${monthName} ${dayNum}`;
					}
				}
			}

			const hasMeals = weekData.meals && weekData.meals.length > 0;
			const hasProteinBalls =
				weekData.proteinBalls && weekData.proteinBalls.selectedOption !== "none";
			const hasAnySelection = hasMeals || hasProteinBalls;

			const order = {
				weekId,
				deliveryDate,
				deliveryDisplay: deliveryDisplay,
				submissionTime: weekData.submissionTime, 
				meals: weekData.meals || [],
				proteinBalls: weekData.proteinBalls || null,
				proteinBallsArray: weekData.proteinBallsArray || null,
				totals: weekData.totals || {
					baseMeals: 0,
					totalAddOnCost: 0,
					addOnDetails: []
				},
				status: deliveryDate > now ? "upcoming" : "delivered",
				hasSelection: hasAnySelection
			};

			console.log(`🔍 Created order for ${weekId}:`, order);
			orders.push(order);
		});

		console.log("🔍 Total orders created:", orders.length);

		if (orders.length === 0) {
			console.log("🔍 No orders found, showing empty message");
			container.innerHTML = `
        <div style="text-align: center; padding: 3rem; color: #666;">
          <h3>No meal selections yet</h3>
          <p>Use the meal selector above to start customizing your weekly meals!</p>
        </div>
      `;
			return;
		}

		// Get available weeks to check deadline status
		const availableWeeks = this.mealSelector.getAvailableWeeks();
		console.log(
			"🔍 Available weeks from selector:",
			availableWeeks.map((w) => ({
				id: w.id,
				isDeadlinePassed: w.isDeadlinePassed
			}))
		);

		// Filter to only show upcoming orders (not past deadline)
		const upcomingOrders = orders.filter((order) => {
			const matchingWeek = availableWeeks.find((week) => week.id === order.weekId);
			const isUpcoming = matchingWeek && !matchingWeek.isDeadlinePassed;

			console.log(
				`🔍 Order ${
					order.weekId
				}: matchingWeek=${!!matchingWeek}, isDeadlinePassed=${
					matchingWeek?.isDeadlinePassed
				}, isUpcoming=${isUpcoming}`
			);

			return isUpcoming;
		});

		console.log(
			"🔍 Upcoming orders after filtering:",
			upcomingOrders.length,
			"out of",
			orders.length
		);

		// Sort: Chronological by date extracted from weekId (e.g. sunday-2026-05-10)
		upcomingOrders.sort((a, b) => {
			// Extract date string from weekId — format is 'sunday-YYYY-MM-DD' or 'wednesday-YYYY-MM-DD'
			const extractDate = (weekId) => {
				const parts = weekId.split('-');
				// parts[0] = 'sunday'/'wednesday', parts[1..3] = YYYY, MM, DD
				if (parts.length >= 4) return new Date(`${parts[1]}-${parts[2]}-${parts[3]}`);
				return new Date(0);
			};
			const dateA = extractDate(a.weekId);
			const dateB = extractDate(b.weekId);

			// Primary sort: chronological by date
			if (dateA.getTime() !== dateB.getTime()) {
				return dateA - dateB;
			}

			// Secondary sort: within same date, Sunday before Wednesday
			const aIsSunday = a.weekId.startsWith('sunday');
			const bIsSunday = b.weekId.startsWith('sunday');
			if (aIsSunday && !bIsSunday) return -1;
			if (!aIsSunday && bIsSunday) return 1;
			return 0;
		});

		let html = "";

		if (upcomingOrders.length > 0) {
			console.log(
				"🔍 Rendering upcoming orders:",
				upcomingOrders.map((o) => o.weekId)
			);
			html +=
				'<h4 style="color: #333; margin-bottom: 2rem; margin-top: 1rem;">🗓️ Upcoming Deliveries</h4>';
			html += upcomingOrders
				.map((order) => this.renderHistoryCard(order))
				.join("");
		} else {
			console.log("🔍 No upcoming orders, showing no deliveries message");
			html += `
        <div style="text-align: center; padding: 3rem; color: #666;">
          <h3>No upcoming customizable deliveries</h3>
          <p>All current meal selection deadlines have passed. Check back for future weeks!</p>
        </div>
      `;
		}

		console.log("🔍 Setting container HTML, length:", html.length);
		container.innerHTML = html;
	}
	renderHistoryCard(order) {
		if (!order.hasSelection) {
			return `
				<div class="history-card">
					<div class="history-header">
						<div class="delivery-date">${order.deliveryDisplay}</div>
						<div class="delivery-status status-chef-choice">Chef's Choice</div>
					</div>
					<div style="text-align: center; padding: 2rem; color: #666;">
						<p>👨‍🍳 No meal selection was made by the deadline.</p>
						<p>You received a curated selection from our chef.</p>
					</div>
				</div>
			`;
		}

		let submissionText = "Selected";
		try {
			if (order.submissionTime) {
				const date = new Date(order.submissionTime);
				if (!isNaN(date.getTime())) {
					const dateStr = date.toLocaleDateString();
					const timeStr = date.toLocaleTimeString([], {
						hour: "2-digit",
						minute: "2-digit"
					});
					submissionText = `Selected: ${dateStr} at ${timeStr}`;
				}
			}
		} catch (error) {
			submissionText = "Selected";
		}

		return `
			<div class="history-card" data-card-week-id="${order.weekId}">
				<div class="history-header">
					<div class="delivery-date">${order.deliveryDisplay}</div>
					<div class="delivery-status" style="font-size: 1.1rem; color: #666; font-weight: normal;">
						${submissionText}
					</div>
				</div>
				${this.renderMealsList(
					order.meals,
					order.totals,
					order.proteinBalls,
					order.weekId,
					order.proteinBallsArray
				)}
			</div>
		`;
	}
	
	async cancelDelivery(weekId, displayName) {
		// Double-check the week isn't past deadline before proceeding
		const availableWeeks = this.mealSelector.getAvailableWeeks();
		const matchingWeek = availableWeeks.find((w) => w.id === weekId);

		if (!matchingWeek || matchingWeek.isDeadlinePassed) {
			alert("This delivery's deadline has passed and can no longer be cancelled.");
			return;
		}

		const confirmed = confirm(
			`Cancel your ${displayName} delivery?\n\n` +
			`This will permanently delete all meal selections for this day only. ` +
			`Your other deliveries will not be affected.\n\n` +
			`This action cannot be undone.`
		);

		if (!confirmed) return;

		const btn = document.querySelector(
			`.cancel-delivery-btn[data-week-id="${weekId}"]`
		);

		try {
			if (btn) {
				btn.disabled = true;
				btn.textContent = "⏳ Cancelling...";
			}

			this.mealSelector.syncStatus.showSyncing();

			const result = await this.mealSelector.railwayAPI.deleteSelection(
				this.customerId,
				weekId
			);

			if (result.success) {
				console.log(`✅ Deleted delivery for ${weekId}`);

				// Clear from frontend state
				delete this.mealSelector.weeklySelections[weekId];
				delete this.mealSelector.mealQuantities[weekId];
				delete this.mealSelector.mealOptions[weekId];
				delete this.mealSelector.proteinBallSelections[weekId];

				// Remove the card from the DOM immediately (no full reload needed)
				const card = document.querySelector(
					`.history-card[data-card-week-id="${weekId}"]`
				);
				if (card) {
					card.style.transition = "opacity 0.3s ease";
					card.style.opacity = "0";
					setTimeout(() => card.remove(), 300);
				}

				// Re-sync localStorage
				await this.mealSelector.saveToLocalStorage({
					weekId: weekId,
					meals: [],
					proteinBalls: null,
					isReplacement: true,
					customerId: this.customerId,
					customerEmail: this.mealSelector.customer.email,
					customerName: this.mealSelector.customer.name,
					deliveryDate: matchingWeek.deliveryDate.toISOString(),
					deliveryDay: weekId.startsWith("sunday") ? "Sunday" : "Wednesday",
					submissionTime: new Date().toISOString()
				});

				// Refresh stats and week cards
				this.loadPortalData();
				this.mealSelector.renderWeeks();

				this.mealSelector.syncStatus.showSuccess(
					`${displayName} delivery cancelled`
				);

				// If the cancelled week is currently selected in the meal UI, reset it
				if (
					this.mealSelector.selectedWeek &&
					this.mealSelector.selectedWeek.id === weekId
				) {
					this.mealSelector.clearAllWorkingSelections();
					this.mealSelector.updateOrderSummary();
				}
			} else {
				throw new Error(result.error || "Delete failed");
			}
		} catch (error) {
			console.error("❌ Cancel delivery error:", error);
			this.mealSelector.syncStatus.showError("Cancellation failed");
			alert(
				"❌ Could not cancel this delivery. Please try again or contact support."
			);

			if (btn) {
				btn.disabled = false;
				btn.textContent = "🗑️ Cancel Delivery";
			}
		}
	}

	renderMealsList(meals, totals, proteinBalls = null, weekId = null, proteinBallsArray = null) {
		let html = `
    	<div class="billing-summary">
    	<h5 style="margin: 0 0 1rem 0; font-size: 1.4rem; color: #3D081B;">Complete Meal Selection</h5>`;

		const groupedMeals = {};

		meals.forEach((meal) => {
			const mealKey = meal.name;

			if (!groupedMeals[mealKey]) {
				groupedMeals[mealKey] = {
					name: meal.name,
					totalQuantity: 0,
					addOns: {},
					totalAddOnCost: 0
				};
			}

			groupedMeals[mealKey].totalQuantity += meal.quantity;

			// USE THE PARSED OPTION DETAILS FROM CLOUD
			const optionDetails = meal.optionDetails || meal.OptionDetails;
			if (optionDetails) {
				Object.values(optionDetails).forEach((option) => {
					if (option.isSelected) {
						const optionKey = option.label;

						if (!groupedMeals[mealKey].addOns[optionKey]) {
							groupedMeals[mealKey].addOns[optionKey] = {
								label: option.label,
								price: option.price || 0,
								quantity: 0,
								totalCost: 0
							};
						}

						groupedMeals[mealKey].addOns[optionKey].quantity += meal.quantity;
						groupedMeals[mealKey].addOns[optionKey].totalCost +=
							(option.price || 0) * meal.quantity;
					}
				});
			}
		});

		Object.values(groupedMeals).forEach((mealGroup) => {
			mealGroup.totalAddOnCost = Object.values(mealGroup.addOns).reduce(
				(sum, addOn) => sum + addOn.totalCost,
				0
			);
		});

		// Calculate meal add-on costs
		let mealAddOnCost = Object.values(groupedMeals).reduce(
			(sum, mealGroup) => sum + mealGroup.totalAddOnCost,
			0
		);

		Object.values(groupedMeals).forEach((mealGroup) => {
			const hasAddOns = Object.keys(mealGroup.addOns).length > 0;

			html += `
      <div class="billing-row">
      <span style="display: flex; flex-direction: column; align-items: flex-start;">
        <span style="font-weight: 600; font-size: 1.3rem;">${mealGroup.name} (×${mealGroup.totalQuantity})</span>`;

			if (hasAddOns) {
				Object.values(mealGroup.addOns).forEach((addOn) => {
					html += `
          <span style="font-size: 1.1rem; color: #666; font-style: italic; margin-top: 0.2rem; margin-left: 1rem;">
          • ${addOn.label}`;
					if (addOn.price > 0) {
						html += ` (+$${addOn.price.toFixed(2)} each × ${
							addOn.quantity
						} = $${addOn.totalCost.toFixed(2)})`;
					} else {
						html += ` (×${addOn.quantity})`;
					}
					html += `</span>`;
				});
			}

			html += `
        </span>
        <span style="font-weight: 600; color: #3D081B;">
          ${mealGroup.totalAddOnCost > 0 ? `+$${mealGroup.totalAddOnCost.toFixed(2)}`: ""}
        </span>
        </div>
      `;
		});

		// ADD PROTEIN BALLS - supports multiple protein ball types
		let proteinBallCost = 0;
		const pbList = proteinBallsArray && proteinBallsArray.length > 0
			? proteinBallsArray
			: (proteinBalls && proteinBalls.selectedOption && proteinBalls.selectedOption !== 'none' ? [proteinBalls] : []);

		pbList.forEach(pb => {
			const cost = pb.price || 0;
			proteinBallCost += cost;
			html += `
				<div class="billing-row">
				<span style="display: flex; flex-direction: column; align-items: flex-start;">
					<span style="font-weight: 600; font-size: 1.3rem;">${pb.name} </span>
					<span style="font-size: 1.1rem; color: #666; font-style: italic; margin-top: 0.2rem; margin-left: 1rem;">
					• ${pb.optionLabel}
					</span>
				</span>
				<span style="font-weight: 600; color: #3D081B;">
					${cost > 0 ? `+$${cost.toFixed(2)}` : ''}
				</span>
				</div>
    		`;
		});

		// ADD SIDES — looked up from the prefetch done in loadPortalData(),
		// since this function itself stays synchronous. Sides are Sunday-only,
		// so a Wednesday weekId will simply find nothing here.
		let sidesCost = 0;
		if (weekId && weekId.startsWith("sunday")) {
			const deliveryDate = this.mealSelector.getDeliveryDateFromWeekId(weekId);
			const sidesForThisDelivery = deliveryDate && this.sidesForDelivery
				? (this.sidesForDelivery[deliveryDate] || [])
				: [];

			sidesForThisDelivery.forEach((side) => {
				const lineCost = (side.price_at_order || 0) * (side.quantity || 0);
				sidesCost += lineCost;
				html += `
					<div class="billing-row">
					<span style="display: flex; flex-direction: column; align-items: flex-start;">
						<span style="font-weight: 600; font-size: 1.3rem;">${side.item_name} (×${side.quantity})</span>
					</span>
					<span style="font-weight: 600; color: #3D081B;">
						${lineCost > 0 ? `+$${lineCost.toFixed(2)}` : ''}
					</span>
					</div>
	    		`;
			});
		}

		// CORRECT TOTAL INCLUDING PROTEIN BALLS AND SIDES
		const grandTotalAddOnCost = mealAddOnCost + proteinBallCost + sidesCost;

		if (grandTotalAddOnCost > 0) {
			html += `
			<div class="billing-row billing-total">
			<span><strong>Total Add-on Charges:</strong></span>
			<span><strong>$${grandTotalAddOnCost.toFixed(2)}</strong></span>
			</div>
			`;
		}

		if (weekId) {
			const availableWeeks = this.mealSelector.getAvailableWeeks();
			const matchingWeek = availableWeeks.find((w) => w.id === weekId);
			const canCancel = matchingWeek && !matchingWeek.isDeadlinePassed;

			if (canCancel) {
				html += `
					<div style="margin-top: 1.5rem; padding-top: 0.7rem; padding-bottom: 0.15rem; border-top: 1px solid #e1e3e5; text-align: right;">
						<button
							type="button"
							class="cancel-delivery-btn"
							data-week-id="${weekId}"
							data-display="${matchingWeek.display}"
							title="Delete all meal selections for ${matchingWeek.display}">
							Delete Selections
						</button>
					</div>
				`;
			}
		}

		html += "</div>";
		return html;
	}

	convertSelectorDataToPortalFormat(
		weeklySelections,
		mealQuantities,
		mealOptions,
		weekInfo = null
	) {
		const converted = {};

		// NEW: Use the actual available weeks instead of hardcoded ones
		const availableWeeks = this.mealSelector.getAvailableWeeks();

		availableWeeks.forEach((week) => {
			const weekId = week.id;
			const weekQuantities = mealQuantities[weekId] || {};
			const weekOptions = mealOptions[weekId] || {};
			const weekSelection = weeklySelections[weekId] || {};

			const hasMeals = Object.values(weekQuantities).some((qty) => qty > 0);

			if (hasMeals || weekSelection.hasSelection) {
				const meals = [];
				const mealDatabase = this.getMealDatabase();

				Object.entries(weekQuantities).forEach(([mealId, quantity]) => {
					if (quantity > 0) {
						const mealInfo = mealDatabase[mealId] || {
							name: "Unknown Meal",
							description: ""
						};
						const selectedOptions = weekOptions[mealId] || {};
						const optionDetails = {};
						let totalAddOnCost = 0;

						Object.entries(selectedOptions).forEach(([optionKey, isSelected]) => {
							if (isSelected && mealInfo.options && mealInfo.options[optionKey]) {
								const option = mealInfo.options[optionKey];
								optionDetails[optionKey] = {
									label: option.label,
									price: option.price || 0,
									isSelected: true
								};
								totalAddOnCost += (option.price || 0) * quantity;
							}
						});

						meals.push({
							id: mealId,
							name: mealInfo.name,
							description: mealInfo.description,
							quantity: quantity,
							selectedOptions: selectedOptions,
							optionDetails: optionDetails,
							totalAddOnCost: totalAddOnCost
						});
					}
				});

				if (meals.length > 0) {
					converted[weekId] = {
						deliveryDate: week.deliveryDate.toISOString(),
						deliveryDisplay: week.display,
						submissionTime: weekSelection.submissionTime || new Date().toISOString(),
						meals: meals,
						totals: {
							baseMeals: meals.reduce((sum, meal) => sum + meal.quantity, 0),
							totalAddOnCost: meals.reduce(
								(sum, meal) => sum + meal.totalAddOnCost,
								0
							),
							addOnDetails: meals
								.filter((meal) => meal.totalAddOnCost > 0)
								.map((meal) => ({
									mealName: meal.name,
									optionName: Object.values(meal.optionDetails)
										.filter((opt) => opt.isSelected && opt.price > 0)
										.map((opt) => opt.label)
										.join(", "),
									quantity: meal.quantity,
									unitPrice: meal.totalAddOnCost / meal.quantity,
									totalPrice: meal.totalAddOnCost
								}))
						}
					};
				}
			}
		});

		return converted;
	}

	getMealDatabase() {
		if (this.mealSelector.weeklyMenus) {
			const allMeals = {};
			Object.values(this.mealSelector.weeklyMenus).forEach((weekMeals) => {
				weekMeals.forEach((meal) => {
					allMeals[meal.id] = meal;
				});
			});
			return allMeals;
		}
		return {};
	}

	async emailHistory() {
		console.log("📧 emailHistory() called - timestamp:", Date.now());
		console.trace("📧 Call stack");

		const emailBtn = document.getElementById("download-history");
		if (!emailBtn) {
			console.error("Email button not found");
			return;
		}
		if (emailBtn.disabled || this.emailInProgress) {
			console.log("📧 Email already in progress, ignoring duplicate call");
			return;
		}
		// Set flag and disable button immediately
		this.emailInProgress = true;
		emailBtn.disabled = true;
		const originalText = emailBtn.textContent;
		emailBtn.textContent = "📧 Sending...";

		try {
			const weeklySelections = this.mealSelector.weeklySelections || {};
			// FILTER OUT OLD FORMAT ENTRIES and duplicates
			const currentSelections = {};
			Object.entries(weeklySelections).forEach(([weekId, weekData]) => {
				if (
					weekId === "lastUpdated" ||
					weekId === "customerInfo" ||
					weekId === "migrationTimestamp"
				)
					return;

				// SKIP OLD FORMAT ENTRIES (they should be cleaned up, but just in case)
				if (weekId.match(/^(sunday|wednesday)-[01]$/)) {
					console.log(`📧 Skipping old format entry: ${weekId}`);
					return;
				}

				// Only include if it has actual meal data
				if (weekData && (weekData.meals || weekData.proteinBalls)) {
					currentSelections[weekId] = weekData;
				}
			});

			console.log(
				"📧 Current selections for email:",
				Object.keys(currentSelections)
			);

			if (Object.keys(currentSelections).length === 0) {
				console.log("📧 No current meal selections found");
				alert("No meal history found to email.");
				return;
			}

			console.log("📧 Building email content with current selections");
			const emailContent = this.buildEmailContent(currentSelections);
			await this.sendHistoryEmail(emailContent);
		} catch (error) {
			console.error("❌ Email error:", error);
			alert("❌ Email sending failed. Please contact support.");
		} finally {
			// Always reset the button state
			this.emailInProgress = false;
			emailBtn.disabled = false;
			emailBtn.textContent = originalText;
		}
	}

	buildEmailContent(data) {
		const orders = [];
		const now = new Date();

		// Process the data same as renderMealHistory
		Object.entries(data).forEach(([weekId, weekData]) => {
			if (weekId === "lastUpdated" || weekId === "customerInfo") return;

			let deliveryDate;
			let deliveryDisplay = weekData.deliveryDisplay;

			const availableWeeks = this.mealSelector.getAvailableWeeks();
			const weekInfo = availableWeeks.find((week) => week.id === weekId);

			try {
				deliveryDate = new Date(weekData.deliveryDate);
				if (isNaN(deliveryDate.getTime())) {
					deliveryDate = weekInfo ? weekInfo.deliveryDate : new Date();
				}
			} catch (error) {
				deliveryDate = weekInfo ? weekInfo.deliveryDate : new Date();
			}

			if (!deliveryDisplay && weekInfo) {
				deliveryDisplay = weekInfo.display;
			}

			const hasMeals = weekData.meals && weekData.meals.length > 0;
			const hasProteinBalls =
				weekData.proteinBalls && weekData.proteinBalls.selectedOption !== "none";
			const hasAnySelection = hasMeals || hasProteinBalls;

			orders.push({
				weekId,
				deliveryDate,
				deliveryDisplay: deliveryDisplay || weekId,
				meals: weekData.meals || [],
				proteinBalls: weekData.proteinBalls || null,
				totals: weekData.totals || {
					baseMeals: 0,
					totalAddOnCost: 0,
					addOnDetails: []
				},
				status: deliveryDate > now ? "upcoming" : "delivered",
				hasSelection: hasAnySelection
			});
		});

		// Sort chronologically (same as display)
		orders.sort((a, b) => a.deliveryDate - b.deliveryDate);

		let emailHtml = `
		<style>
			/* Light mode logo (default) */
			.logo-light {
				display: block !important;
				max-width: 200px;
				height: auto;
				margin-bottom: 5px;
				border-radius: 5px;
				background-color: #ffffff;
				padding: 5px;
			}

			.logo-dark {
				display: none !important;
				max-width: 200px;
				height: auto;
				margin-bottom: 5px;
				border-radius: 5px;
				background-color: transparent;
				padding: 5px;
			}

			/* Dark mode - swap the logos */
			@media (prefers-color-scheme: dark) {
				.logo-light {
					display: none !important;
				}

				.logo-dark {
					display: block !important;
				}

				.email-content {
					background-color: transparent !important;
					color: #ffffff !important;
				}

				.logo-container {
					background-color: transparent !important;
					border-color: #444444 !important;
				}

				.logo-text {
					color: #ffffff !important;
				}
			}

			/* Outlook fallback */
			[data-ogsc] .logo-light {
				display: block !important;
			}

			[data-ogsc] .logo-dark {
				display: none !important;
			}
		</style>

		<div class="email-content" style="background-color: #ffffff; color: #333333; font-family: Arial, sans-serif; line-height: 1.6; max-width: 600px; margin: 0 auto; padding: 20px;">

			<p style="font-size: 16px; line-height: 1.5; color: #333333; margin: 0 0 15px 0;">Hello ${
				this.mealSelector.customer.firstName || "Valued Customer"
			},</p>
			<p style="font-size: 16px; line-height: 1.5; color: #333333; margin: 0 0 20px 0;">Here's a complete summary of your recent meal selections:</p>
			`;
		// Add upcoming deliveries
		const upcomingOrders = orders.filter((o) => o.status === "upcoming");
		if (upcomingOrders.length > 0) {
			emailHtml +=
				'<h3 style="color: #3D081B; margin: 20px 0 15px 0; font-size: 18px;">🗓️ Upcoming Deliveries</h3>';
			upcomingOrders.forEach((order) => {
				emailHtml += this.buildEmailOrderSection(order);
			});
		}

		// Add past deliveries
		const pastOrders = orders.filter((o) => o.status === "delivered");
		if (pastOrders.length > 0) {
			emailHtml +=
				'<h3 style="color: #3D081B; margin: 20px 0 15px 0; font-size: 18px;">📦 Past Deliveries</h3>';
			pastOrders.forEach((order) => {
				emailHtml += this.buildEmailOrderSection(order);
			});
		}

		emailHtml += `
		<br>
		<p style="color: #333333; margin: 20px 0; font-size: 16px;">Thank you for choosing Primal Balance Nutrition!</p>
		<p style="color: #333333; margin: 10px 0; font-size: 16px;">If you have any questions about your meal selections, please don't hesitate to <a href="mailto:support@primalbalancenutrition.com" style="color: #3D081B; text-decoration: none; font-weight: 600; border-bottom: 1px solid #3D081B;">contact us</a>.</p>
		<br>
		<p style="color: #333333; margin: 20px 0; font-size: 16px;">Best regards,<br>The Primal Balance Nutrition Team</p>

		<!-- Logo container with both light and dark versions -->
		<div style="background-color: #ffffff; padding: 25px; text-align: left; border-radius: 10px; margin: 20px 0; border: 2px solid #f0f0f0;">

			<!-- Light mode logo (default - always visible) -->
			<img src="https://cdn.shopify.com/s/files/1/0845/3564/0380/files/rollies_1.jpg" alt="Primal Balance Nutrition" style="max-width: 200px !important; width: 200px !important; height: auto !important; margin-bottom: 5px; border-radius: 5px; background-color: #ffffff; padding: 5px; display: block;">

			<!-- Dark mode logo (hidden by default, shown only in dark mode) -->
			<!--[if !mso]><!-->
			<div style="display: none;">
				<style>
					@media (prefers-color-scheme: dark) {
						.light-only {
							display: none !important;
						}

						.dark-only {
							display: block !important;
						}

						.logo-container-dark {
							background-color: transparent !important;
							border-color: #444 !important;
						}

						.logo-text-dark {
							color: #ffffff !important;
						}
					}
				</style>
				<img class="dark-only" src="https://www.primalbalancenutrition.com/cdn/shop/files/Green_Minimalist_Aesthetic_Fashion_Studio_Style_Guide_Brand_Board_1.png" alt="Primal Balance Nutrition" style="max-width: 200px !important; width: 200px !important; height: auto !important; margin-bottom: 5px; border-radius: 5px; padding: 5px; display: none;">
			</div>
			<!--<![endif]-->

			<p style="margin: 5px 0 0 0; color: #333333; font-size: 14px;">Fresh, Balanced, Delicious</p>
		</div>
		`;

		return emailHtml;
	}
	buildEmailOrderSection(order) {
		if (!order.hasSelection) {
			return `
		<div style="border: 1px solid #ddd; padding: 15px; margin: 10px 0; border-radius: 5px; background-color: #f9f9f9 !important; color: #333333 !important;">
			<h4 style="color: #3D081B !important; margin: 0 0 10px 0;">${order.deliveryDisplay}</h4>
			<p style="color: #333333 !important; margin: 5px 0;">👨‍🍳 Chef's Choice - No specific meals were selected for this delivery.</p>
		</div>
		`;
		}

		let html = `
		<div style="border: 1px solid #ddd; padding: 15px; margin: 10px 0; border-radius: 5px; background-color: #f9f9f9 !important; color: #333333 !important;">
			<h4 style="color: #3D081B !important; margin: 0 0 10px 0;">${order.deliveryDisplay}</h4>
			<h5 style="color: #333333 !important; margin: 0 0 10px 0;">Complete Meal Selection:</h5>
			`;

		// Group meals by name (same logic as display)
		const groupedMeals = {};

		(order.meals || []).forEach((meal) => {
			const mealKey = meal.name;

			if (!groupedMeals[mealKey]) {
				groupedMeals[mealKey] = {
					name: meal.name,
					totalQuantity: 0,
					addOns: {},
					totalAddOnCost: 0
				};
			}

			groupedMeals[mealKey].totalQuantity += meal.quantity;

			if (meal.optionDetails) {
				Object.values(meal.optionDetails).forEach((option) => {
					if (option.isSelected) {
						const optionKey = option.label;

						if (!groupedMeals[mealKey].addOns[optionKey]) {
							groupedMeals[mealKey].addOns[optionKey] = {
								label: option.label,
								price: option.price || 0,
								quantity: 0,
								totalCost: 0
							};
						}

						groupedMeals[mealKey].addOns[optionKey].quantity += meal.quantity;
						groupedMeals[mealKey].addOns[optionKey].totalCost +=
							(option.price || 0) * meal.quantity;
					}
				});
			}
		});

		Object.values(groupedMeals).forEach((mealGroup) => {
			mealGroup.totalAddOnCost = Object.values(mealGroup.addOns).reduce(
				(sum, addOn) => sum + addOn.totalCost,
				0
			);
		});

		// Display each meal with inline styles
		Object.values(groupedMeals).forEach((mealGroup) => {
			html += `<div style="margin: 10px 0; padding: 10px; background-color: #ffffff !important; border-radius: 5px; border: 1px solid #e0e0e0; color: #333333 !important;">`;
			html += `<strong style="color: #3D081B !important;">${mealGroup.name} (×${mealGroup.totalQuantity})</strong>`;

			if (Object.keys(mealGroup.addOns).length > 0) {
				html += `<ul style="margin: 5px 0 0 20px; color: #333333 !important; padding-left: 0;">`;
				Object.values(mealGroup.addOns).forEach((addOn) => {
					html += `<li style="color: #333333 !important; margin: 2px 0;">${addOn.label}`;
					if (addOn.price > 0) {
						html += ` (+${addOn.price.toFixed(2)} each × ${
							addOn.quantity
						} = ${addOn.totalCost.toFixed(2)})`;
					} else {
						html += ` (×${addOn.quantity})`;
					}
					html += `</li>`;
				});
				html += `</ul>`;
			}
			html += `</div>`;
		});

		// ADDED: Include protein balls in email with inline styles
		let proteinBallCost = 0;
		if (
			order.proteinBalls &&
			order.proteinBalls.selectedOption &&
			order.proteinBalls.selectedOption !== "none"
		) {
			proteinBallCost = order.proteinBalls.price || 0;

			html += `<div style="margin: 10px 0; padding: 10px; background-color: #ffffff !important; border-radius: 5px; border: 1px solid #e0e0e0; color: #333333 !important;">`;
			html += `<strong style="color: #3D081B !important;">${order.proteinBalls.name} </strong>`; /*🍪🍫*/
			html += `<ul style="margin: 5px 0 0 20px; color: #333333 !important; padding-left: 0;">`;
			html += `<li style="color: #333333 !important; margin: 2px 0;">${order.proteinBalls.optionLabel}`;
			if (proteinBallCost > 0) {
				html += ` (+${proteinBallCost.toFixed(2)})`;
			}
			html += `</li>`;
			html += `</ul>`;
			html += `</div>`;
		}
		const mealAddOnCost = Object.values(groupedMeals).reduce(
			(sum, mealGroup) => sum + mealGroup.totalAddOnCost,
			0
		);
		const totalAddOnCost = mealAddOnCost + proteinBallCost;

		if (totalAddOnCost > 0) {
			html += `<div style="margin-top: 15px; padding: 10px; background-color: #e8f5e8 !important; font-weight: bold; border-radius: 5px; color: #333333 !important;">`;
			html += `Total Add-on Charges: ${totalAddOnCost.toFixed(2)}`;
			html += `</div>`;
		}

		html += `</div>`;
		return html;
	}

	async sendHistoryEmail(emailContent) {
		const emailData = {
			action: "sendEmail",
			data: {
				to: this.customerEmail,
				customerName:
					this.mealSelector.customer.firstName || this.mealSelector.customer.name,
				subject: "Your Meal History - Primal Balance Nutrition",
				htmlContent: emailContent,
				timestamp: new Date().toISOString()
			}
		};

		return new Promise((resolve, reject) => {
			try {
				console.log("📧 Sending email with data:", emailData);

				if (!this.mealSelector.googleSheets) {
					this.mealSelector.googleSheets = new GoogleSheetsIntegration();
				}

				const form = document.createElement("form");
				form.method = "POST";
				form.action = this.mealSelector.googleSheets.webAppUrl;
				form.target = "email_iframe_" + Date.now();
				form.style.display = "none";

				const actionField = document.createElement("input");
				actionField.type = "hidden";
				actionField.name = "action";
				actionField.value = "sendEmail";
				form.appendChild(actionField);

				const dataField = document.createElement("input");
				dataField.type = "hidden";
				dataField.name = "data";
				dataField.value = JSON.stringify(emailData.data);
				form.appendChild(dataField);

				const iframe = document.createElement("iframe");
				iframe.name = form.target;
				iframe.style.display = "none";

				document.body.appendChild(iframe);
				document.body.appendChild(form);

				// Submit form ONCE
				form.submit();
				console.log("✅ Email form submitted");

				// Show success message after delay
				setTimeout(() => {
					alert(
						"📧 Your meal history has been emailed to " +
							this.customerEmail +
							"!\n\nPlease check your inbox in a few minutes."
					);
					resolve(); // Resolve the promise
				}, 2000);

				// Cleanup
				setTimeout(() => {
					if (document.body.contains(form)) document.body.removeChild(form);
					if (document.body.contains(iframe)) document.body.removeChild(iframe);
				}, 5000);
			} catch (error) {
				console.error("❌ Email send failed:", error);
				reject(error);
			}
		});
	}

	clearLocalData() {
		if (
			confirm(
				"⚠️ This will clear all your local meal selection data. This cannot be undone. Are you sure?"
			)
		) {
			const storageKey = `meal_selections_${this.customerId}`;
			localStorage.removeItem(storageKey);
			alert("🗑️ Local data cleared. The page will refresh.");
			window.location.reload();
		}
	}

	refreshStaleData() {
		if (this.refreshInProgress) {
			return;
		}
		this.refreshInProgress = true;

		if (
			confirm(
				"Clear active selections? This will clear any unsaved selections for upcoming deliveries but keep your saved meals from past deadlines."
			)
		) {
			// Get available weeks to determine which are still active
			const availableWeeks = this.mealSelector.getAvailableWeeks();
			const activeWeekIds = availableWeeks
				.filter((week) => !week.isDeadlinePassed)
				.map((week) => week.id);

			console.log("Active (clearable) weeks:", activeWeekIds);
			console.log("Preserving past deadline weeks");

			// Clear working selections for ALL weeks (this affects the current UI)
			this.mealSelector.mealQuantities = {};
			this.mealSelector.mealOptions = {};
			this.mealSelector.proteinBallSelections = {};

			// Clear saved selections ONLY for active weeks (preserve past deadline saves)
			activeWeekIds.forEach((weekId) => {
				if (this.mealSelector.weeklySelections[weekId]) {
					console.log(`Clearing saved selection for active week: ${weekId}`);
					delete this.mealSelector.weeklySelections[weekId];
				}
			});

			// Update localStorage with the filtered data
			const storageKey = `meal_selections_${this.mealSelector.customer.id}`;
			const currentData = JSON.parse(localStorage.getItem(storageKey) || "{}");

			// Remove active weeks from localStorage too
			activeWeekIds.forEach((weekId) => {
				if (currentData[weekId]) {
					delete currentData[weekId];
				}
			});

			currentData.lastUpdated = new Date().toISOString();
			localStorage.setItem(storageKey, JSON.stringify(currentData));

			// Refresh displays
			this.mealSelector.renderWeeks();
			this.loadPortalData();

			alert("Active selections cleared successfully.");
		}

		this.refreshInProgress = false;
	}

	renderEmptyPortal() {
		const subscriptionEl = document.getElementById("meal-subscription");
		const totalMealsEl = document.getElementById("total-meals");
		const extraChargesEl = document.getElementById("extra-charges");

		if (subscriptionEl) {
			subscriptionEl.textContent =
				this.mealSelector.mealPlan?.planName || "No Active Subscription";
		}
		if (totalMealsEl) {
			totalMealsEl.textContent = "0";
		}
		if (extraChargesEl) {
			extraChargesEl.textContent = "$0.00";
		}

		const container = document.getElementById("customer-meal-history-portal");
		if (container) {
			container.innerHTML = `
                <div style="text-align: center; padding: 3rem; color: #666;">
                <h3>No meal selections yet</h3>
                <p>Use the meal selector above to start customizing your weekly meals!</p>
                </div>
                `;
		}
	}
}

// MAIN WEEKLY MEAL SELECTOR CLASS - ***** only need to update these dates/times *****
class WeeklyMealSelector {
	
	constructor() {
		this.dataVersion = "2026-04-20";

		// weekConfig is loaded dynamically from Railway in init() via /menu/current
		// This empty placeholder is replaced before renderWeeks() is called
		this.weekConfig = {
			version: this.dataVersion,
			availableWeeks: []
		};

		this.timezone = {
			name: "Pacific",
			utcOffset: -8, // PST
			dstOffset: -7 // PDT
		};
		this.customer = window.customerData || {};

		// Core state management
		this.selectedWeek = null;
		this.weeklySelections = {};
		this.mealQuantities = {};
		this.mealOptions = {};
		this.proteinBallSelections = {};
		this.pendingMealRedemptions = 0;
		this.pendingRedemptionWeekId = null;

		// Sides (à la carte) state — kept entirely separate from meal quantities/totals
		this.sidesCatalog = {};        // weekId -> [{item_id, item_name, category, unit_type, price}]
		this.sidesQuantities = {};     // weekId -> { itemId: quantity }
		this.sidesExistingSave = {};   // weekId -> true if a saved sides selection was loaded for this date
		this.activeMenuTab = "menu";


		// For guests, create a temporary ID and basic info
		if (this.customer.isGuest) {
			console.log("Guest user detected - showing read-only interface");
			this.customer.id = this.customer.id || "guest_" + Date.now();
			this.customer.email = this.customer.email || "guest@temp.com";
			this.customer.name = "Guest";
			this.customer.tags = [];
		}

		this.weeklyMenus = this.initializeMenus();
		this.mealPlan = this.getMealPlanFromCustomer();

		// Maintenance mode: menu stays visible to everyone, but ordering is
		// paused for everyone too — including real subscribers. Reuses the
		// existing read-only ("non-subscriber") rendering path rather than
		// building a second one, and keeps track of whether someone is a
		// REAL subscriber separately, so the messaging can tell a paused
		// subscriber apart from an actual guest.
		this.maintenanceMode = !!(window.mealSelectorSettings && window.mealSelectorSettings.orderingMaintenanceMode);
		this.isActualSubscriber = !this.checkIfNonSubscriber();
		this.isNonSubscriber = this.maintenanceMode || !this.isActualSubscriber;

		if (this.isNonSubscriber) {
			this.enableNonSubscriberMode();
		}

		this.planPricing = {
			minimumOrderWarning: 4
		};
		console.log("Customer meal plan detected:", this.mealPlan);

		this.googleSheets = new GoogleSheetsIntegration();
		this.railwayAPI = new RailwayAPI();
		this.syncLogger = new SyncLogger(this.customer.id);
		this.syncStatus = new SyncStatusManager();

		// Initialize sync status indicator
		setTimeout(() => {
			this.syncStatus.init();
		}, 100);

		window.addEventListener("online", () => {
			console.log("📶 Back online - ready to sync");
			this.syncStatus.showSuccess("Back online!");
		});

		window.addEventListener("offline", () => {
			console.log("📵 Offline mode - will sync when connected");
			this.syncStatus.showOffline();
		});

		this.forceMobileCleanup();

		// Make init async-aware
		this.init().catch(error => {
			console.error('❌ Init error:', error);
			// Ensure UI still renders even if init fails
			this.renderWeeks();
			this.bindEvents();
		});
		this.debouncedUpdateSummary = this.debounce(
			this.updateOrderSummary.bind(this),
			150
		);
		if (
			!this.customer.isGuest &&
			!localStorage.getItem(`cleanup_completed_${this.customer.id}`)
		) {
			this.performLegacyDataCleanup();
			localStorage.setItem(`cleanup_completed_${this.customer.id}`, "true");
		}

		if (!this.customer.isGuest && this.customer.id) {
			window.loyaltyPortal = new LoyaltyPortal(this.customer.id);
			
			// Load teaser balance immediately
			window.loyaltyPortal.api.getBalance(this.customer.id).then(result => {
				if (result.success) {
					window.loyaltyPortal.balance = result.balance;
					const teaser = document.getElementById('loyalty-points-teaser');
					if (teaser) teaser.textContent = `${result.balance.toLocaleString()} pts`;
				}
			});
		}
	}

	performLegacyDataCleanup() {
		try {
			console.log("Performing automatic legacy data cleanup...");

			const storageKey = `meal_selections_${this.customer.id}`;
			const data = JSON.parse(localStorage.getItem(storageKey) || "{}");

			// Check if cleanup is needed
			const hasLegacyData = Object.keys(data).some(
				(key) =>
					key.match(/^(sunday|wednesday)-[01]$/) || key === "migrationTimestamp"
			);

			if (!hasLegacyData) {
				console.log("No legacy data found, cleanup not needed");
				return;
			}

			console.log("Legacy data detected, cleaning up...");

			const cleanData = {};

			// Keep metadata
			if (data.lastUpdated) cleanData.lastUpdated = data.lastUpdated;
			if (data.customerInfo) cleanData.customerInfo = data.customerInfo;
			
			Object.entries(data).forEach(([key, value]) => {
				if (
					key !== "lastUpdated" &&
					key !== "customerInfo" &&
					key !== "migrationTimestamp" &&
					!key.match(/^(sunday|wednesday)-[01]$/) &&
					!cleanData[key]
				) {
					cleanData[key] = value;
				}
			});

			// Save cleaned data
			cleanData.lastUpdated = new Date().toISOString();
			cleanData.cleanupPerformed = new Date().toISOString();
			localStorage.setItem(storageKey, JSON.stringify(cleanData));

			this.loadExistingSelections();

			console.log("Legacy data cleanup completed successfully");
		} catch (error) {
			console.error("Error during legacy data cleanup:", error);
		}
	}
	// Helper method for display names
	getDisplayForDate(dateStr) {
		const date = new Date(dateStr);
		const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
		const monthName = date.toLocaleDateString("en-US", { month: "long" });
		const dayNum = date.getDate();
		return `${dayName} ${monthName} ${dayNum}`;
	}

	getWeekInfo(weekId) {
		const parts = weekId.split("-");
		const dayType = parts[0];
		const dateStr = parts.slice(1).join("-");

		let weekConfig = this.weekConfig.availableWeeks.find((week) => {
			return week.sundayDate === dateStr || week.wednesdayDate === dateStr;
		});

		if (!weekConfig) {
			console.warn(`Week config not found for ${weekId}`);
			return {
				date: dateStr,
				display: `${dayType} ${dateStr}`,
				deadline: { date: new Date().toISOString(), displayText: "Unknown" },
				weekNumber: 0
			};
		}

		const date =
			dayType === "sunday" ? weekConfig.sundayDate : weekConfig.wednesdayDate;
		const display =
			dayType === "sunday" ? weekConfig.sundayDisplay : weekConfig.wednesdayDisplay;

		return {
			date: date,
			display: display,
			deadline: {
				date: weekConfig.deadline,
				displayText: weekConfig.deadlineDisplay
			},
			weekNumber: 0
		};
	}

	debounce(func, wait) {
		let timeout;
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	}

	initDOMCache() {
		this.domCache = {
			weekGrid: document.querySelector("[data-week-grid]"),
			mealSection: document.querySelector("[data-meal-section]"),
			mealsGrid: document.querySelector("[data-meals-grid]"),
			summaryContent: document.querySelector("[data-order-summary]"),
			totalSection: document.querySelector("[data-order-total]"),
			totalCount: document.querySelector("[data-total-count]"),
			saveBtn: document.querySelector("[data-save-order]"),
			weekTitle: document.querySelector("[data-week-title]"),
			deadlineNotice: document.querySelector("[data-deadline-notice]")
		};
	}
	isProteinBallMeal(mealId) {
		return mealId.startsWith("prb");
	}
	isKidsMeal(mealId) {
		return mealId.startsWith("kds");
	}
	initializeMenus() {
		return {
        	"sunday-2026-06-28": [
                {
                        id: "bef113",
                        name: "Grass-Fed Beef Stuffed Bell Pepper",
                        cuisineType: "Mexican",
                        description: "stuffed with sweet onions, peas, Cape Grim grass-fed/finished beef, and mushrooms. Served with Spanish rice and topped with our house made Portuguese sauce (P:45g F:30g C:45g)= 630 calories<h4>**contains eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Beef-stuffed-bell-pepper-800.jpg?v=1757070155",
                        shopifyHandle: "bef113",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 }
                        }
                },
                {
                        id: "ckn154",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Mediterranean Chicken Summer Salad",
                        cuisineType: "Mediterranean",
                        description: "Tender cubes of Rosie’s organic chicken breast seasoned with warming harissa spices and roasted to perfection, served over a vibrant Mediterranean-inspired salad of roasted butternut squash, garbanzo beans, crisp cucumbers, cherry tomatoes, fresh dill, and red onions. Finished with a creamy house-made tahini dressing, juicy pomegranate arils, and nutrient-dense hemp hearts for a refreshing, protein-packed summer meal bursting with bold flavor and texture (P:42g F:22g C:42g) = 550 calories/11g fiber<h4>**contains Sesame, hemp seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Mediterranean_summer_salad.jpg?v=1780910233",
                        shopifyHandle: "ckn154",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub Harissa Tempeh" }
                        }
                },
                {
                        id: "ckn155",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Creamy Mushroom Farfalle with Herb Roasted Chicken",
                        cuisineType: "Northern Italian",
                        description: "Rosie’s chicken breast, white mushrooms, and Jovial gluten-free farfalle tossed together in a rich, dairy-free mushroom cream sauce infused with shallots, garlic, white wine, thyme, and sage. Served with roasted asparagus and blistered cherry tomatoes for a hearty, comforting meal packed with flavor. (P:42g F:22g C:42g) = 550 calories/11g fiber<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Creamy_Mushroom_Farfalle_with_Herb_Roasted_Chicken.jpg?v=1780910233",
                        shopifyHandle: "ckn155",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "prk107",
                        name: "Coffee Rubbed Braised Pork",
                        cuisineType: "New American",
                        description: "White quinoa, carrots, chickpeas, cucumber, red bell pepper, red onion, parsley, garlic, Healthy On You coffee rub spice blend, pork shoulder, raw local honey, rosemary white wine sauce (P: 44g F: 34g C: 45g) = 660 calories<h4>**contains pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/coffeeRubbedPork.jpg?v=1777402919",
                        shopifyHandle: "prk107",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 }
                        }
                },
                {
                        id: "trk114",
                        name: "Italian Turkey Burger",
                        cuisineType: "Italian",
                        description: "with rosemary yams, sauté yellow squash, mushrooms, sweet onions, Diestel organic ground turkey, and topped with our house made vinaigrette (P:38g F:25g C:40g)= 537 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ItalianTurkeyBurger-800.jpg?v=1762157179",
                        shopifyHandle: "trk114",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub Italian garbanzo beans" }
                        }
                },
                {
                        id: "bef134",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Braised Grass-Fed Ossobuco",
                        cuisineType: "Portuguese",
                        description: "Tender Cape Grim grass-fed, grass-finished beef slow-braised for hours in a rich ossobuco sauce of red wine, tomatoes, garlic, shallots, fresh herbs, and beef broth until fork-tender. Served with golden polenta cakes alongside garlic olive oil broccoli for a comforting, rustic meal inspired by Northern Italian flavors (P: 42g F: 33g C: 36g)= 620 calories/10g fiber",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Ossobuco_29af1691-22cb-4316-b3d9-cf75fe5ae607.jpg?v=1780910233",
                        shopifyHandle: "bef134",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "fsh109",
                        name: "Maple Glazed Wild Isles Salmon",
                        cuisineType: "New American",
                        description: "with roasted butternut squash, saute green kale, mushrooms, blistered cherry tomatoes, Purefish wild isles salmon, and glazed in our house made maple Dijon glaze (P:35g F:22g C:30g)= 458 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Maple-glazed-salmon-800.jpg?v=1757072689",
                        shopifyHandle: "fsh109",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk126",
                        name: "Southwest Breakfast Burrito",
                        cuisineType: "Mexican",
                        description: "Wrapped in a warm Coyota’s burrito wrap, this hearty breakfast burrito is stuffed with our San Diego–style scramble of pasture-raised eggs, Niman Ranch pork bacon, sweet onions, and red bell peppers. Finished with our creamy Southwest chipotle mayo inside for a bold, smoky kick comforting, satisfying, and truly heavenly (P:31g F:34g C:36g)= 574 calories<h4>**contains eggs, pork, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Southwest_breakfast_burrito.jpg?v=1780910233",
                        shopifyHandle: "brk126",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk123",
                        name: "Pumpkin Spice Overnight Oats",
                        cuisineType: "New American",
                        description: "with chia seeds, fresh coconut milk, Organifi complete vanilla vegan protein powder, cinnamon, no sugar pumpkin pure, local raw San Diego honey, sunflower seed butter, banana slices, and topped with coconut flakes (P:37g F:26g C:30g)=502 calories<h4>**contains coconut, pumpkin seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Pumpkin_spice_oats.jpg?v=1780910233",
                        shopifyHandle: "brk123"
                },
                {
                        id: "prb101",
                        name: "Birthday Cake Protein Balls",
                        cuisineType: "New American",
                        description: "with Enjoy Life white chocolate chips, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:12g F:10g C:15g)= 200 calories per two balls<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/BirthdayCakeProteinBalls.jpg?v=1765229906",
                        shopifyHandle: "prb101",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],
        "wednesday-2026-07-01": [
                {
                        id: "ckn156",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Kimchi Chicken Stir Fry",
                        cuisineType: "Asian",
                        description: "Tender Rosie’s chicken thighs tossed in a sweet and savory house made Korean-inspired stir-fry sauce. Served over rice noodles with broccoli and carrots, then finished with tangy kimchi, fresh scallions, and toasted sesame seeds for a bold, flavor-packed meal with the perfect balance of sweet, savory, and spicy (P:42g F:26g C:50g) = 602 calories<h4>**contains coconut, sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Kimchi_Chicken_Stir_Fry.jpg?v=1780910233",
                        shopifyHandle: "ckn156",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "prk110",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Ismael’s Mojo De Ajo Pork Chop",
                        cuisineType: "Mexican",
                        menuNote: "Paleo",
                        description: "Tender, seared Room to Roam pork chop served over caramelized Cuban-style plantains with a colorful medley of roasted corn, zucchini, and bell peppers. Finished with Ismael's signature Mojo de Ajo sauce made with garlic, citrus, Mexican oregano, and arbol chilies, then topped with bright pickled red onions for a bold Cuban-inspired balance of savory, smoky, and tangy flavors (P:45g F:30g C:47g)= 638 calories<h4>**contains Pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Ismael_s_Mojo_De_Ajo_Pork_Chop.jpg?v=1780910233",
                        shopifyHandle: "prk110",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "bef106",
                        name: "Erik's Grass-Fed Beef Birria",
                        cuisineType: "Mexican",
                        description: "with Spanish rice, sweet onions, carrots, peas, and grass-fed regenerative farmed carbon neutral short rib slow cooked for 10 hours in our house made birria sauce (P:38g F:30g C:45g)= 602 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Beef-Birria-800.jpg?v=1759134081",
                        shopifyHandle: "bef106",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "ckn134",
                        name: "Pesto Chicken Pasta",
                        cuisineType: "Italian",
                        description: "with Jovial gluten free pasta, sauté green kale, blistered cherry tomatoes, Rosie's organic chicken breast, and topped with our house made pesto sauce (P:41g F:15g C:35g)= 489 calories<h4>**contains Pumpkin seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/pesto_chicken_pasta.jpg?v=1780910234",
                        shopifyHandle: "ckn134",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "bef126",
                        name: "Portuguese Grass-Fed Beef Bowl",
                        cuisineType: "Portuguese",
                        menuNote: "Paleo",
                        description: "with roasted butternut squash, garlic yellow squash, blistered cherry tomatoes, New Zealand grass-fed beef, and topped with our house made Portuguese sauce (P:40g F:22g C:37g)= 506 calories<h4>**contains Eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Portuguese_grass-fed_beef_bowl.jpg?v=1781269438",
                        shopifyHandle: "bef126",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                vegan: { label: "Vegan — Sub Portuguese Lentils" }
                        }
                },
                {
                        id: "fsh112",
                        name: "Salmon Power Bowl",
                        cuisineType: "Asian",
                        description: "A nourishing, protein-packed bowl built on Lundberg brown rice with sautéed sweet onions, peas, mushrooms, and carrots. Topped with pan-seared, cubed Sky Steelhead from Pure Fish and finished with our house-made sweet & spicy mayo for the perfect balance of heat and richness (P:42g F:25g C:47g)= 581 calories<h4>**contains eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Salmon_power_bowl.jpg?v=1780910269",
                        shopifyHandle: "fsh112",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "brk137",
                        name: "Protein Scramble",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "Fluffy pastured eggs scrambled with crispy bacon, served with roasted yams and fresh scallions, seasoned simply with garlic and spices for a hearty, balanced, and satisfying. (P: 28g F: 25g C: 40g)= 497 calories<h4>**contains eggs, pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ProteinScramble.jpg?v=1781440716",
                        shopifyHandle: "brk137",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk112",
                        name: "Lemon Blueberry Protein Pancakes",
                        cuisineType: "New American",
                        description: "with 3 lemon blueberry protein pancakes, 2 pastured over easy San Diego eggs, maple chicken sausage patties, and maple syrup on the side (P:35g F:20g C:65g)= 580 calories<h4>**contains eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/PumpkinPancakes.jpg?v=1762787742",
                        shopifyHandle: "brk112"
                },
                {
                        id: "prb101",
                        name: "Birthday Cake Protein Balls",
                        cuisineType: "New American",
                        description: "with Enjoy Life white chocolate chips, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:12g F:10g C:15g)= 200 calories per two balls<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/BirthdayCakeProteinBalls.jpg?v=1765229906",
                        shopifyHandle: "prb101",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],
        "sunday-2026-07-05": [
                {
                        id: "bef105",
                        name: "Chimichurri Grass-Fed Skirt Steak",
                        cuisineType: "Argentinian",
                        menuNote: "Paleo • Low Carb",
                        description: "with gold baby potatoes, roasted broccolini, Roam grass-fed/finished regenerative farmed steak, and topped with our house made chimichurri sauce (P:42g F:20g C:45g)= 423 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ChimichurriSteak-800.jpg?v=1762783545",
                        shopifyHandle: "bef105",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub chimichurri tempeh" }
                        }
                },
                {
                        id: "ckn137",
                        name: "Pumpkin Coconut Curry",
                        cuisineType: "Indian",
                        menuNote: "Low Carb",
                        description: "A rich, fragrant pumpkin coconut curry simmered with tender butternut squash, sautéed zucchini, and Rosie's Organic Chicken Breast. Slow-cooked in our house-made blend of coriander, cumin, cinnamon, and curry spices. Served over a fluffy bed of organic white jasmine rice. (P:46g F:14g C:50g)= 510 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ChickenCurry-800.jpg?v=1768796394",
                        shopifyHandle: "ckn137",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub tofu" }
                        }
                },
                {
                        id: "ckn118",
                        name: "Creamy Garlic Sage Chicken",
                        cuisineType: "Italian",
                        menuNote: "Paleo • Naturally Low Carb",
                        description: "Roasted spaghetti squash, zucchini, and yellow squash served with Rosie's Organic chicken breast, pan-seared and coated in our velvety house-made creamy garlic sauce. (P:43g F:20g C:30g)= 472 calories<h4>**contains coconut, parsley**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/creamy_garlic_sage_chicken.jpg?v=1781269462",
                        shopifyHandle: "ckn118",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "prk103",
                        name: "Chorizo Meatballs",
                        cuisineType: "Mexican",
                        menuNote: "Low Carb",
                        description: "with Spanish rice, sauté sweet onions, red bell peppers, sauté mushrooms, Chorizo pork meatballs, and topped with our house made avocado crema sauce (P:35g F:40g C:40g)= 660 calories<h4>**contains coconut, eggs, pork, cilantro**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/chorizoMeatballs.jpg?v=1781438162",
                        shopifyHandle: "prk103",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub chorizo tempeh" }
                        }
                },
                {
                        id: "ckn135",
                        name: "Pesto Chicken Thighs",
                        cuisineType: "Italian",
                        menuNote: "Paleo • Naturally Low Carb",
                        description: "with roasted butternut squash, sauté kale, sauté mushrooms, blistered cherry tomatoes, Rosie's chicken thighs, and topped with our house made pesto sauce (P:41g F:25g C:35g)= 529 calories<h4>**contains Pumpkin seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Pesto-chicken-thighs-800.jpg?v=1756102965",
                        shopifyHandle: "ckn135",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "bef118",
                        name: "Green Goddess Grass-Fed Beef Bowl",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "with Japanese yams, garlic olive oil asparagus, organic grass-fed New Zealand beef, blistered cherry tomatoes, and topped with our house made green goddess dressing (P:45g F:20g C:40g)= 520 calories<h4>**contains coconut, eggs**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/GreenGoddessBeefBowl-800.jpg?v=1757650741",
                        shopifyHandle: "bef118",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "fsh105",
                        name: "Greek Wild Isles Salmon",
                        cuisineType: "Greek",
                        menuNote: "Paleo",
                        description: "with roasted gold potatoes, fresh sprouts, cucumbers, blistered cherry tomatoes, wild isles salmon from Purefish, and topped with our Greek dressing (P:37g F:20g C:40g)= 488 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/GreekSalmon-800.jpg?v=1760826920",
                        shopifyHandle: "fsh105",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk111",
                        name: "Huevos Rancheros",
                        cuisineType: "Mexican",
                        description: "with two corn tortillas, chipotle black beans, 3 poached eggs, and cooked in our house made red sauce. Topped with fresh mango salsa. (P:25g F:22g C:40g)= 458 calories<h4>**contains eggs**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Huevos-Rancheros-800.jpg",
                        shopifyHandle: "brk111",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk129",
                        name: "Sweet & Savory Breakfast Burrito",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "wrapped in a Coyotas burrito wrap stuffed with pastured San Diego eggs, red bell peppers, onions, house made maple chicken sausage, and our honey chipotle mayo sauce (P:34g F:30g C:45g)= 586 calories<h4>**contains eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/savory-sweet-breakfast-burrito-800.jpg?v=1757080782",
                        shopifyHandle: "brk129",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "prb107",
                        name: "Primal Protein Balls",
                        cuisineType: "New American",
                        description: "with Enjoy Life dark chocolate chips, cinnamon, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:30g F:30g C:70g)= 670 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/PrimalProteinBalls.jpg?v=1756101853",
                        shopifyHandle: "prb107",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],
        "wednesday-2026-07-08": [
                {
                        id: "ckn158",
                        name: "Primal Fried Chicken",
                        cuisineType: "Asian",
                        menuNote: "Low Carb",
                        description: "with jasmine brown rice, roasted garlic olive oil broccoli, roasted carrots, and Rosie's organic chicken breast glazed in our house made stir fry sauce (P:42g F:15g C:50g)= 503 calories<h4>**contains coconut, sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Orange-chicken-thighs-800.jpg?v=1758348309",
                        shopifyHandle: "ckn158",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                vegan: { label: "Vegan — Sub tofu" }
                        }
                },
                {
                        id: "trk103",
                        name: "Honey Ginger Turkey Bowl",
                        cuisineType: "Asian",
                        menuNote: "Low Carb",
                        description: "with white jasmine rice, garlic green beans, orange cauliflower, Diestel organic turkey, and topped with our house made honey ginger sauce (P:50g F:20g C:40g)= 540 calories<h4>**contains coconut, sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/HoneyGingerTurkeyBowl-800.jpg?v=1757070988",
                        shopifyHandle: "trk103",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub honey ginger tempeh" }
                        }
                },
                {
                        id: "bef120",
                        name: "Hot Honey Grass-Fed Beef Bowl",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "with roasted yams, garlic olive oil asparagus, Cape Grim grass-fed/finished beef, and topped with our house made hot honey sauce and avocado on top (P:42g F:25g C:45g)= 573 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/HotHoney-BeefBowl-800.jpg?v=1769587709",
                        shopifyHandle: "bef120",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                vegan: { label: "Vegan — Sub garbanzo beans" }
                        }
                },
                {
                        id: "ckn126",
                        name: "Jamaican Jerk Chicken Thighs",
                        cuisineType: "Jamaican",
                        menuNote: "Paleo",
                        description: "with fire roasted cinammon yams, roasted cauliflower, and Jamaican jerk Rosie's organic chicken thighs (P:41g F:15g C:46g) = 483 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Jamaiccan_jerk_chx.jpg?v=1781269469",
                        shopifyHandle: "ckn126",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "ckn124",
                        name: "Herb Crusted Chicken",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "Rosie's organic chicken breast served with a medley of roasted organic yams, asparagus, and roma tomatoes, seasoned with rosemary, dill, basil, oregano, garlic powder, and smoked paprika. Finished with a vibrant garlic herb sauce (P:43g F:14g C:42g)= 466 calories<h4>**contains sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Herb-crusted-chicken-800_54cb6e01-ca25-4bd6-af89-5c42394f1100.jpg?v=1759131545",
                        shopifyHandle: "ckn124",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub Mediterranean Lentils" }
                        }
                },
                {
                        id: "brk103",
                        name: "Blueberry Maple Sausage and Eggs",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "with 2 house made blueberry maple sausage patties, 3 poached eggs, roasted gold potatoes, sauté sweet onions, sauté kale, and blistered cherry tomatoes (P:34g F:30g C:42g)= 574 calories<h4>**contains eggs, pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/blueberryMapleSausage.jpg?v=1779842616",
                        shopifyHandle: "brk103",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk118",
                        name: "Peaches & Cream Overnight Oats",
                        cuisineType: "New American",
                        description: "with chia seeds, fresh coconut milk, Organifi complete vanilla vegan protein powder, cinnamon, local raw San Diego honey, sunflower seed butter, fresh peaches, and topped with coconut flakes (P:30g F:20g C:50g)= 500 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/peaches-n-cream-800.jpg?v=1762777939",
                        shopifyHandle: "brk118"
                },
                {
                        id: "prb107",
                        name: "Primal Protein Balls",
                        cuisineType: "New American",
                        description: "with Enjoy Life dark chocolate chips, cinnamon, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:30g F:30g C:70g)= 670 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/PrimalProteinBalls.jpg?v=1756101853",
                        shopifyHandle: "prb107",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],
			"sunday-2026-07-12": [
			{
				id: "ckn159",
				name: "Pesto Hummus Chicken",
				cuisineType: "New American",
				menuNote: "Paleo",
				description: "Rosie's chicken breast, grilled and sliced, served with roasted yams and a Mediterranean-inspired salad of fresh tomatoes, cucumber, pickled red onions, and fire-roasted sweet corn. Finished with our creamy house-made pesto hummus sauce. P:42g F:27g C:42g = 590 cal<h4>**contains Sesame, pumpkin seeds**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/pesto_hummus_chicken.jpg?v=1782848372",
				shopifyHandle: "ckn159",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					extraSauce: { label: "Extra sauce (on side)", price: 2.0 },
					vegan: { label: "Vegan — Sub Mediterranean Tempeh" }
				}
			},
			{
				id: "ckn160",
				name: "Chicken Veggie Vessel Bowl",
				cuisineType: "New American",
				description: "Tender herb-roasted Rosie's chicken thighs served over fluffy quinoa with caramelized roasted yams, grilled zucchini, and char-grilled red bell peppers. Finished with our creamy house-made tahini green sauce. P:42g F:22g C:45g = 550 cal",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/chicken_veggie_vessel_bowl.jpg?v=1782849160",
				shopifyHandle: "ckn160",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					extraSauce: { label: "Extra sauce (on side)", price: 2.0 },
					vegan: { label: "Vegan — Sub Mediterranean Garbanzo Beans" }
				}
			},
			{
				id: "bef136",
				name: "Hannah's Loaded Grass-Fed Beef Burger",
				cuisineType: "New American",
				menuNote: "Paleo",
				description: "Our signature Cape Grim grass-fed, grass-finished beef burger is seasoned and grilled to perfection, then layered over roasted Japanese yams and crisp gem lettuce. Topped with blistered cherry tomatoes, pickled red onions, and our creamy house-made secret burger sauce inspired by classic burger stand flavors. Served alongside grilled asparagus for a satisfying, veggie-packed take on the ultimate burger. P:42g F:32g C:42g = 624 cal<h4>**contains eggs**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Hannahs_burger.jpg?v=1782848830",
				shopifyHandle: "bef136",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					noSauce: { label: "No sauce", price: 0.0 },
					extraSauce: { label: "Extra sauce (on side)", price: 2.0 }
				}
			},
			{
				id: "lmb108",
				name: "Coconut Braised Lamb with Turmeric Rice & Agave-Glazed Cumin Carrots",
				cuisineType: "Moroccan",
				description: "Tender Atkins Ranch grass-fed, grass-finished lamb shoulder slow-braised in our aromatic house-made coconut sauce featuring tomato, coconut milk, fresh ginger, garlic, shallots, mint, cilantro, serrano chili, and warming garam masala. Served alongside fragrant turmeric rice and agave-glazed cumin carrots, then finished with a cooling coconut yogurt raita made with cucumber, fresh herbs, lemon, and spices. P:40g F:40g C:48g = 718 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Coconut_lamb.jpg?v=1782848800",
				shopifyHandle: "lmb108",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					lowCarb: { label: "Low carb (extra veggies)", price: 2.0 }
				}
			},
			{
				id: "trk113",
				name: "Turkey Ragu",
				cuisineType: "Italian",
				description: "with gluten free Jovial penne, Diestel turkey, roasted garlic broccoli, blistered cherry tomatoes, and cooked in our house made dairy free ragu sauce P:40g F:16g C:52g = 512 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/turkey-Ragu-800.jpg?v=1769944713",
				shopifyHandle: "trk113",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 }
				}
			},
			{
				id: "ckn161",
				name: "Jerk Chicken",
				cuisineType: "Jamaican",
				menuNote: "Paleo",
				description: "Rosie's chicken breast marinated in our bold, house-made Jamaican jerk marinade featuring scotch bonnet peppers, garlic, scallions, citrus, fresh ginger, warming spices, and local honey, then grilled to perfection. Served with crispy Cajun sweet potato fries and grilled garlic olive oil asparagus, then finished with our housemade creamy cilantro lime crema. P:43g F:25g C:42g = 565 cal<h4>**contains coconut, eggs**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Jerk_chicken.jpg?v=1782848855",
				shopifyHandle: "ckn161",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					vegan: { label: "Vegan — Sub Garlic tofu" }
				}
			},
			{
				id: "fsh102",
				name: "Blackened Wild Isles Salmon",
				cuisineType: "New American (Louisiana)",
				menuNote: "Paleo",
				description: "with fire roasted cinnamon maple yams and blackened cauliflower P:35g F:20g C:42g = 565 cal",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/CajunSalmon-800.jpg",
				shopifyHandle: "fsh102",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 }
				}
			},
			{
				id: "brk143",
				name: "Savory Breakfast Bowl",
				cuisineType: "Latin",
				menuNote: "Paleo",
				description: "Crispy roasted gold potatoes and slow-cooked pinto beans with grilled zucchini, red bell peppers, and sweet onions seasoned with smoky spices, topped with chopped Niman Ranch pork bacon and three over-medium local eggs for a hearty, protein-packed breakfast P:34g F:34g C:38g = 590 cal<h4>**contains eggs, pork**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Savory_breakfast_bowl.jpg?v=1782849059",
				shopifyHandle: "brk143",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 }
				}
			},
			{
				id: "brk106",
				name: "Chocolate Chip Pancakes",
				cuisineType: "New American",
				description: "with pork bacon, 2 pastured San Diego eggs, and fresh maple syrup on the side P:30g F:15g C:55g = 573 cal<h4>**contains eggs, coconut, pork**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ChocolateChip-pancakes-800.jpg?v=1758348615",
				shopifyHandle: "brk106"
			},
			{
				id: "brk144",
				name: "Maple Tahini Overnight Protein Oats",
				cuisineType: "New American",
				description: "Creamy overnight oats made with gluten-free oats, coconut milk, vanilla plant protein, and chia seeds, lightly sweetened with pure maple syrup and finished with a rich maple tahini-inspired flavor from sunflower seed butter, warming Ceylon cinnamon, flax seeds, coconut flakes, and naturally sweet dried figs. A nourishing, protein-packed breakfast that tastes like a wholesome treat while providing lasting energy throughout the morning P:28g F:16g C:48g = 445 cal<h4>**contains coconut, sesame**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Maple_Tahini_oats.jpg?v=1782849086",
				shopifyHandle: "brk144"
			},
			{
				id: "prb104",
				name: "Cherry Garcia Protein Balls",
				description: "with Enjoy Life dark chocolate chips, fresh blended cherries, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! P:12g F:10g C:15g = 200 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/cherry_garcia_protein_balls.jpg?v=1782849038",
				shopifyHandle: "prb104",
				options: {
					fifteenBalls: { label: "15 Balls", price: 25.0 },
					thirtyBalls: { label: "30 Balls", price: 45.0 }
				}
			}
		],
		"wednesday-2026-07-15": [
			{
				id: "ckn162",
				name: "Herb Roasted Chicken",
				cuisineType: "New American",
				menuNote: "Paleo • Naturally Low Carb",
				description: "Rosie's herb chicken thighs grilled and served with sage-roasted butternut squash and caramelized rainbow carrots and Brussels sprouts. Finished with our vibrant house-made herb sauce P:42g F:20g C:34g = 488 cal",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Herb-crusted-chicken-800_54cb6e01-ca25-4bd6-af89-5c42394f1100.jpg?v=1759131545",
				shopifyHandle: "ckn162",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					vegan: { label: "Vegan — Sub Mediterranean Garbanzo Beans" }
				}
			},
			{
				id: "ckn115",
				name: "Chicken Tikka Masala",
				cuisineType: "Indian",
				menuNote: "Low Carb",
				description: "with white jasmine rice, sweet onions, carrots, peas, Rosie's organic chicken breast, and cooked in our house made tika masala sauce P:42g F:22g C:45g = 546 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Tika_masala.jpg?v=1780910233",
				shopifyHandle: "ckn115",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					lowCarb: { label: "Low carb (extra veggies)", price: 2.0 },
					extraSauce: { label: "Extra sauce (on side)", price: 2.0 },
					vegan: { label: "Vegan — Sub Garlic tofu" }
				}
			},
			{
				id: "bef132",
				name: "Grass-Fed Beef Short Rib & Butternut Squash Risotto",
				cuisineType: "Portuguese",
				description: "Slow-braised grass-fed beef short rib served over a creamy butternut squash risotto infused with rosemary, sage, and white wine, paired with roasted brussel sprouts for a rich, comforting, and elevated dish P:46g F:30g C:44g = 640 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/SteakAndButternut-800.jpg?v=1778408662",
				shopifyHandle: "bef132",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					lowCarb: { label: "Low carb (extra veggies)", price: 2.0 }
				}
			},
			{
				id: "prk106",
				name: "Al Pastor Pork Bowl",
				cuisineType: "Mexican",
				menuNote: "Low Carb",
				description: "A bold, flavor-packed bowl featuring fluffy white jasmine rice, red bell peppers, onions, chipotle black beans, and sweet corn, topped with tender Niman Ranch pork shoulder slow-cooked in our house-made al pastor sauce. Made with guajillo and chipotle peppers, fresh-squeezed pineapple juice, raw honey, cumin, and paprika, then finished with our bright, house-made mango salsa for the perfect sweet-heat balance P:45g F:34g C:50g = 686 cal<h4>**contains pork, eggs, coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/BBQ-ShortRib-800.jpg?v=1766583565",
				shopifyHandle: "prk106",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					lowCarb: { label: "Low carb (extra veggies)", price: 2.0 }
				}
			},
			{
				id: "fsh117",
				name: "Mint Chimichurri Salmon",
				cuisineType: "Argentinian",
				menuNote: "Paleo",
				description: "with baby gold potatoes, asparagus, Sky steelhead, and topped with our mint chimichurri sauce P:38g F:24g C:38g = 520 cal",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ChimichurriSalmonPlated.jpg?v=1777405144",
				shopifyHandle: "fsh117",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 },
					extraSauce: { label: "Extra sauce (on side)", price: 2.0 },
					vegan: { label: "Vegan" }
				}
			},
			{
				id: "brk120",
				name: "Pesto Breakfast Bowl",
				cuisineType: "Italian",
				menuNote: "Paleo",
				description: "with roasted gold baby potatoes, sauté kale, blistered cherry tomatoes, Italian chicken sausage, 2 poached San Diego pastured eggs, and topped with our house made pesto sauce P:38g F:25g C:42g = 545 cal<h4>**contains pumpkin seeds, eggs, coconut, pork**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Pesto-Breakfast-Bowl-800.jpg?v=1759135543",
				shopifyHandle: "brk120",
				options: {
					extraProtein: { label: "Extra protein", price: 3.0 }
				}
			},
			{
				id: "brk145",
				name: "Chunky Monkey Overnight Oats",
				cuisineType: "New American",
				description: "A wholesome twist on the classic Chunky Monkey flavor, featuring creamy overnight oats blended with ripe banana, rich cacao, coconut milk, and pure vanilla. Mixed with chia seeds, crunchy pumpkin seeds, naturally sweet Medjool dates, and Enjoy Life dark chocolate chips, this protein-packed breakfast delivers the perfect balance of chocolatey indulgence and nourishing ingredients to keep you fueled all morning long P:28g F:18g C:60g = 495 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Chunky_Monkey_oats.jpg?v=1782849019",
				shopifyHandle: "brk145"
			},
			{
				id: "prb104",
				name: "Cherry Garcia Protein Balls",
				description: "with Enjoy Life dark chocolate chips, fresh blended cherries, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! P:12g F:10g C:15g = 200 cal<h4>**contains coconut**</h4>",
				image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/cherry_garcia_protein_balls.jpg?v=1782849038",
				shopifyHandle: "prb104",
				options: {
					fifteenBalls: { label: "15 Balls", price: 25.0 },
					thirtyBalls: { label: "30 Balls", price: 45.0 }
				}
			}
		],
		"sunday-2026-06-14": [
                {
                        id: "ckn152",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Sticky Gochuang Chicken",
                        cuisineType: "Asian",
                        menuNote: "Paleo • Low Carb",
                        description: "Rosie’s chicken breast tossed in a sweet and spicy sticky gochujang glaze made with raw honey, coconut sugar, coconut aminos, garlic, sesame oil, and rice vinegar, served with low-carb konjac noodles, broccoli, carrots, and finished with fresh green onions. (P:46g F:14g C:15g) = 355 calories<h4>**contains coconut, sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Sticky_Gu_Chx.jpg?v=1780440183",
                        shopifyHandle: "ckn152",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub tofu" }
                        }
                },
                {
                        id: "ckn144",
                        name: "Mediterranean Chicken Bowl",
                        cuisineType: "Mediterranean",
                        menuNote: "Paleo",
                        description: "With brown rice, lentils, pickled onions, cucumber, tomato, red onion, garlic, Rosie’s chicken thighs, harissa spice blend, house-made yogurt dill sauce (P: 45g F: 28g C: 52g) = 660 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Med_chxbowl.jpg?v=1780440180",
                        shopifyHandle: "ckn144",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                vegan: { label: "Vegan — Sub Mediterranean Garbanzo Beans" }
                        }
                },
                {
                        id: "lmb104",
                        name: "Spicy Grass-Fed Lamb Meatballs",
                        cuisineType: "Mediterranean",
                        menuNote: "Paleo • Naturally Low Carb",
                        description: "Roasted butternut squash, grilled asparagus, blistered cherry tomatoes, sautéed red onions, and 3 harissa Atkins Ranch grass-fed/finished lamb meatballs topped with our vibrant house-made green goddess dressing (P:46g F:32g C:45g)= 652 calories<h4>**contains eggs, coconut, sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Spicy_lamb_meatballs.jpg?v=1780440161",
                        shopifyHandle: "lmb104",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub Mediterranean Tempeh" }
                        }
                },
                {
                        id: "ckn151",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Quinoa Beet Salad",
                        cuisineType: "New American",
                        description: "Rosie’s chicken breast served over a hearty kale and quinoa salad with roasted carrots, yams, green onions, shallots, dried cherries, and hemp hearts, tossed in a bright sherry vinaigrette made with Dijon mustard, maple syrup, lemon juice, garlic, and cold-pressed olive oil (P:48g F:22g C:39g) = 545 calories<h4>**contains Hemp hearts**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Quinoa_beet_salad.jpg?v=1780440171",
                        shopifyHandle: "ckn151",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tempeh" }
                        }
                },
                {
                        id: "bef122",
                        name: "Maple Dijon Grass-Fed Beef Bowl",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "with roasted sweet potatoes, garlic olive oil, broccoli, blistered cherry tomatoes, Cape Grim grass-fed/finished beef, and topped with our house made maple Dijon dressing (P:45g F:20g C:40g)= 520 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/maplebeefbowl-800.jpg?v=1759134531",
                        shopifyHandle: "bef122",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "ckn117",
                        name: "Chimichurri Chicken Thighs",
                        cuisineType: "Argentinian",
                        menuNote: "Paleo",
                        description: "with smashed gold potatoes, saute kale, saute mushrooms, blistered cherry tomatoers, Rosie's organic chicken thighs, and our house made chimichurri sauce on top (P:43g F:22g C:45g)= 550 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ChimichurriChicken-800.jpg?v=1757073488",
                        shopifyHandle: "ckn117",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "fsh114",
                        name: "Teriyaki Wild Isles Salmon",
                        cuisineType: "Asian",
                        menuNote: "Low Carb",
                        description: "with white jasmine rice, sugar snap peas, garlic broccoli, sauté red bell peppers, and wild isles salmon from Purefish glazed in our house made teriyaki sauce (P:38g F:18g C:50g)= 514 calories<h4>**contains sesame, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Teriyaki-Salmon-800.jpg?v=1756357360",
                        shopifyHandle: "fsh114",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "brk101",
                        name: "Banana Protein Pancakes",
                        cuisineType: "New American",
                        description: "with 3 banana protein pancakes, 2 pastured San Diego eggs, housemade pork maple sausage, and fresh maple syrup on the side (P:25g F:25g C:50g)= 525 calories<h4>**contains coconut, eggs, pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/PumpkinPancakes.jpg?v=1762787742",
                        shopifyHandle: "brk101"
                },
                {
                        id: "brk113",
                        name: "Mediterranean Breakfast Bowl",
                        cuisineType: "Mediterranean",
                        description: "Golden quinoa layered with sautéed baby green kale, blistered cherry tomatoes, and briny green olives, topped with three San Diego pastured poached eggs and herb sage chicken sausage. Finished with our house-made mint chimichurri for a fresh, vibrant, and savory kick in every bite (P:33g F:23g C:33g)= 471 calories<h4>**contains eggs**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Med_breakfast_bowl.jpg?v=1780440167",
                        shopifyHandle: "brk113",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk132",
                        name: "Vanilla Overnight Collagen Oats",
                        cuisineType: "New American",
                        description: "Creamy vanilla oats soaked in coconut milk with chia seeds, sunflower seed butter, and layered with fresh berries. (P:37g F:26g C:30g)=502 calories<h4>**contains coconut, sunflower seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/VanillaOats.jpg?v=1780448245",
                        shopifyHandle: "brk132"
                },
                {
                        id: "prb106",
                        name: "Maple Pumpkin Spice Dark Chocolate Protein Balls",
                        description: "with Enjoy Life dark chocolate chips, pumpkin spice, sugar free pumpkin pure, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:12g F:10g C:15g)= 200 calories per two balls<h4>**contains coconut, sunflower seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/maple-spice-protein-balls-800.jpg?v=1759135973",
                        shopifyHandle: "prb106",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],
        "wednesday-2026-06-17": [
                {
                        id: "ckn153",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Ismael's Filipino Chicken Adobo",
                        cuisineType: "Filipino",
                        menuNote: "Low Carb",
                        description: "Rosie’s chicken thighs slow-simmered in a savory adobo sauce served over Spanish rice with agave glazed roasted carrots and topped with a fresh corn salsa (P:42g F:25g C:46g) = 595 calories<h4>**contains coconut, corn**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Ismael_s_Filipino_Chicken_Adobo.jpg?v=1780440190",
                        shopifyHandle: "ckn153",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub chipotle black beans" }
                        }
                },
                {
                        id: "ckn130",
                        name: "Maple Mustard Glazed Chicken",
                        cuisineType: "New American",
                        description: "with roasted purple potatoes and asparagus paired with Rosie's Organic chicken breast, pan-seared and finished in our sweet and tangy house-made maple mustard glaze (P:43g F:17g C:42g)= 493 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/GarlicChicken-800.jpg?v=1775708974",
                        shopifyHandle: "ckn130",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "trk110",
                        name: "Thai Basil Green Curry Turkey Bowl",
                        cuisineType: "Indian",
                        description: "with white jasmine rice, sauté garlic olive oil garbanzo beans, carrots, sweet onions, peas, Diestel organic turkey, and topped with our house made Thai basil green curry sauce (P:42g F:20g C:45g)= 528 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Thai-basil-green-curry-800.jpg?v=1757077367",
                        shopifyHandle: "trk110",
                        options: {
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "bef129",
                        name: "Red Wine Braised Short Ribs",
                        cuisineType: "Italian-American",
                        menuNote: "Paleo",
                        description: "with smashed gold potatoes, roasted broccolini, and Cape Grim grass-fed/grass finished beef short rib slow cooked for 8 hours in our house made red wine herb sauce (P:45g F:20g C:45g)= 540 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Portuguese-shortRib-800.jpg?v=1756033398",
                        shopifyHandle: "bef129",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "ckn123",
                        name: "Hawaiian Shoyu Chicken Thighs",
                        cuisineType: "Asian",
                        description: "with brown rice, snap peas, red bell peppers, roasted aspragus, Rosie's organic chicken thighs, fresh scallions, and topped with our house made Hawaiian shoyu sauce (P:40g F:15g C:45g)= 475 calories<h4>**contains coconut, sesame**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/hawaiian-chx-thighs-800.jpg?v=1759131738",
                        shopifyHandle: "ckn123",
                        options: {
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "fsh119",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Herb Wild Caught Seabass",
                        cuisineType: "Argentinian",
                        menuNote: "Paleo",
                        description: "Wild-caught seabass from Pure Fish served with pickled dill potatoes, charred broccolini, and finished with a bright fresh herb sauce. (P:41g F:21g C:38g) = 510 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Seabass.jpg?v=1780445137",
                        shopifyHandle: "fsh119",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "brk120",
                        name: "Pesto Breakfast Bowl",
                        cuisineType: "Italian",
                        description: "with roasted gold baby potatoes, sauté kale, blistered cherry tomatoes, Italian chicken sausage, 2 poached San Diego pastured eggs, and topped with our house made pesto sauce (P:38g F:25g C:42g)= 545 calories<h4>**contains pumpkin seeds, eggs, coconut, pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Pesto-Breakfast-Bowl-800.jpg?v=1759135543",
                        shopifyHandle: "brk120",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "brk116",
                        name: "Breakfast Tacos",
                        cuisineType: "Mexican",
                        description: "with Siete tortillas, sweet onions, red bell peppers, pork bacon, pastured San Diego eggs, and topped with our chipotle mayo (P:33g F:24g C:40g)= 508 calories<h4>**contains eggs, pork, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Breakfast-tacos-800.jpg?v=1759135848",
                        shopifyHandle: "brk116",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "prb106",
                        name: "Maple Pumpkin Spice Dark Chocolate Protein Balls",
                        description: "with Enjoy Life dark chocolate chips, pumpkin spice, sugar free pumpkin pure, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:12g F:10g C:15g)= 200 calories per two balls<h4>**contains coconut, sunflower seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/maple-spice-protein-balls-800.jpg?v=1759135973",
                        shopifyHandle: "prb106",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],	
		"sunday-2026-06-07": [
                {
                        id: "ckn140",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Thai Red Chicken Curry",
                        cuisineType: "Thai",
                        description: "with jasmine brown rice, roasted garlic cauliflower, snap peas, roasted carrots, Rosie's organic chicken breast, and slow cooked in our house made red curry sauce (P:42g F:15g C:45g)= 483 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/KoreanBraisedChx-800.jpg?v=1759751430",
                        shopifyHandle: "ckn140",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub curry tofu" }
                        }
                },
                {
                        id: "ckn150",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Chicken Stroganoff",
                        cuisineType: "Russian",
                        description: "Tender lightly floured and pan-seared Rosie's chicken breast served with sautéed mushrooms, shallots, asparagus, and cherry tomatoes tossed with Jovial gluten-free spaghetti in a rich and creamy dairy-free stroganoff sauce made with coconut milk, white wine, dijon mustard, and garlic for a comforting, flavor-packed twist on a classic dish (P:42g F:18g C:48g)= 530 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ChickenStroganoff-800.jpg?v=1779841782",
                        shopifyHandle: "ckn150",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                vegan: { label: "Vegan — Sub pan-seared tempeh" }
                        }
                },
                {
                        id: "prk108",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Rosemary Grilled Pork Chops",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "A juicy pork chop dry-rubbed with smoky BBQ seasoning, fresh rosemary, and thyme, then flame-grilled for a bold, savory finish. Served with maple glazed butternut squash and garlic broccolini, and topped with our sweet and tangy house-made maple dijon dressing for the perfect balance of smoky, herbaceous, and sweet flavors (P:46g F:28g C:30g)= 560 calories<h4>**contains pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/RosemaryPorkChop-800.jpg?v=1779841497",
                        shopifyHandle: "prk108",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "bef110",
                        name: "Grass-Fed Beef Chili",
                        cuisineType: "Latin",
                        menuNote: "Spicy",
                        description: "with sweet onions, Cape Grim grass-fed/finished beef, pinto beans, fresh corn, red bell peppers, roasted tomatoes, and grass-fed beef. Slow cooked for 8 hours in our fresh herbs/spices of cumin, paprika, parsley, oregano, and more. Topped with our house made green #2 sauce (P:50g F:22g C:32g)= 526 calories<h4>**contains eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/ElkChili-800.jpg?v=1757076015",
                        shopifyHandle: "bef110",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "ckn122",
                        name: "Harissa Honey Chicken",
                        cuisineType: "Mediterranean",
                        menuNote: "Paleo",
                        description: "Fire-roasted yams, sweet cherry tomatoes, red onions, and garlic olive oil asparagus served with Rosie's organic chicken thighs, marinated in our house-made harissa blend. Finished with a refreshing tzatziki on top (P:43g F:30g C:46g)= 626 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Harissa-800.jpg?v=1779841359",
                        shopifyHandle: "ckn122",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                vegan: { label: "Vegan — Sub harissa garbanzo beans" }
                        }
                },
                {
                        id: "bef123",
                        name: "Moroccan Braised Grass-Fed Beef Short Rib",
                        cuisineType: "Moroccan",
                        menuNote: "Paleo",
                        description: "with roasted smashed gold potatoes, saute mushrooms, garlic olive oil brussel sprouts, and Cape Grim grass-fed/finished beef short rib slow cooked for 12-hours in our house made Moroccan sauce (P:40g F:30g C:42g)= 598 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/BBQ-ShortRib-800.jpg?v=1766583565",
                        shopifyHandle: "bef123",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "fsh114",
                        name: "Teriyaki Wild Isles Salmon",
                        cuisineType: "Asian",
                        menuNote: "High in Omega-3s",
                        description: "with white jasmine rice, sugar snap peas, garlic broccoli, sauté red bell peppers, and wild isles salmon from Purefish glazed in our house made teriyaki sauce (P:38g F:18g C:50g)= 514 calories<h4>**contains sesame, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Teriyaki-Salmon-800.jpg?v=1756357360",
                        shopifyHandle: "fsh114",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "brk121",
                        name: "Pesto Breakfast Tacos",
                        cuisineType: "New American",
                        description: "Two warm corn tortillas filled with our savory breakfast scramble of sweet onions, cherry tomatoes, green kale, Niman Ranch pork bacon, and San Diego pastured eggs, all topped with our vibrant house-made pesto sauce for a fresh, flavorful start to the day (P:27g F:27g C:37g)= 495 calories<h4>**contains eggs, pork, pumpkin seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/pestoBreakfastTacos.jpg?v=1780327998",
                        shopifyHandle: "brk121",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "brk142",
                        name: "<span style=\"display:inline-block;background:#3D081B;color:#ffffff;font-size:10px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;padding:2px 9px;border-radius:20px;margin-right:6px;vertical-align:middle;\">New</span>Strawberries and Cream Protein Pancakes",
                        cuisineType: "New American",
                        description: "Fluffy strawberry-infused protein pancakes made with gluten-free flour, vanilla plant protein, creamy coconut milk, and white chocolate chips, served with pure maple syrup. Comes with two slices of Niman Ranch pork bacon and your choice of two over-medium or poached eggs for the perfect sweet-and-savory breakfast (P:30g F:15g C:55g)= 475 calories<h4>**contains eggs, pork, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/StrawberriesAndCreamPancakes-800.jpg?v=1779841289",
                        shopifyHandle: "brk142"
                },
                {
                        id: "prb104",
                        name: "Cherry Garcia Protein Balls",
                        description: "with Enjoy Life dark chocolate chips, fresh blended cherries, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:12g F:10g C:15g)= 200 calories per two balls<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/CherryGarcia-ProteinBalls-800.jpg?v=1757657599",
                        shopifyHandle: "prb104",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        ],
        "wednesday-2026-06-10": [
                {
                        id: "ckn114",
                        name: "Chicken Piccata Meatballs",
                        cuisineType: "Italian",
                        description: "with creamy risotto, roasted broccoli, blistered cherry tomatoes, Rosie's organic chicken meatballs, and topped with our house made piccata sauce (P:43g F:19g C:43g)= 515 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Chicken-PiccataMeatballs-800.jpg?v=1769595869",
                        shopifyHandle: "ckn114",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
				{
                        id: "ckn143",
                        name: "Pad Thai Chicken",
                        cuisineType: "Thai",
                        menuNote: "Paleo",
                        description: "with roasted Japanese yams, sauté red bell peppers, garlic olive oil purple cabbage, and topped with our house made pad Thai sauce (P: 40g F:20g C:30g)= 460 calories<h4>**contains sesame, coconut, sunflower seeds**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Pad-thai-chicken-800.jpg?v=1758348401",
                        shopifyHandle: "ckn143",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub garlic tofu" }
                        }
                },
                {
                        id: "bef102",
                        name: "Barbecue Braised Grass-Fed Beef Short Rib",
                        description: "with roasted smashed gold potatoes, saute asparagus, and Cape Grim grass-fed/finished beef short rib slow cooked for 12-hours in our house made BBQ sauce (P:40g F:33g C:43g)= 629 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Portuguese-shortRib-800.jpg?v=1756033398",
                        shopifyHandle: "bef102",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "ckn120",
                        name: "Garlic Herb Chicken Thighs",
                        cuisineType: "New American",
                        menuNote: "Paleo",
                        description: "Juicy Rosie’s Organic chicken thighs marinated and grilled with fresh thyme, sage, rosemary, and garlic-infused olive oil. Served alongside fire-roasted sweet potatoes and topped with our refreshing house-made cucumber salad with cherry tomatoes, red onion, cucumbers, and dill, then finished with a light balsamic glaze for the perfect savory-sweet balance (P:42g F:20g C:42g)= 516 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/garlicHerbChicken-800.jpg?v=1779842522",
                        shopifyHandle: "ckn120",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub tempeh" }
                        }
                },
                {
                        id: "lmb102",
                        name: "Lamb Curry Grass-Fed Meatballs",
                        cuisineType: "Indian",
                        description: "with jasmine brown rice, sauté garbanzo beans, garlic cauliflower, roasted carrots, snow peas, Niman Ranch lamb meatballs, and topped with our house made curry sauce (P:41g F:27g C:43g)= 663 calories<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/Lamb-curry-meatballs-800.jpg?v=1756355922",
                        shopifyHandle: "lmb102",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 },
                                vegan: { label: "Vegan — Sub tofu" }
                        }
                },
                {
                        id: "fsh108",
                        name: "Lemon Rosemary Wild Isles Salmon",
                        cuisineType: "Mediterranean",
                        menuNote: "Paleo",
                        description: "with roasted gold potatoes, garlic olive oil broccoli, and topped with our house made lemon rosemary sauce (P:37g F:15g C:35g)= 423 calories",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/RosemarySalmon-800.jpg?v=1757651152",
                        shopifyHandle: "fsh108",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                noSauce: { label: "No sauce", price: 0 },
                                extraSauce: { label: "Extra sauce (on side)", price: 2 }
                        }
                },
                {
                        id: "brk134",
                        name: "Spanish Poached Eggs",
                        cuisineType: "Spanish",
                        description: "A flavorful dish featuring fresh Spanish-style rice with sautéed sweet onions, mushrooms, and bell peppers, topped with three pastured poached eggs and finished with our house-made Green Goddess sauce (P:26g F:26g C:40g) = 498 calories<h4>**contains eggs, coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/SpanishPoachedEggs.jpg?v=1779842695",
                        shopifyHandle: "brk134",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 },
                                lowCarb: { label: "Low carb (extra veggies)", price: 2 },
                                vegan: { label: "Vegan — Sub tofu scramble" }
                        }
                },
                {
                        id: "brk103",
                        name: "Blueberry Maple Sausage and Eggs",
                        cuisineType: "New American",
                        description: "with 2 house made blueberry maple sausage patties, 3 poached eggs, roasted gold potatoes, sauté sweet onions, sauté kale, and blistered cherry tomatoes (P:34g F:30g C:42g)= 574 calories<h4>**contains eggs, pork**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/blueberryMapleSausage.jpg?v=1779842616",
                        shopifyHandle: "brk103",
                        options: {
                                extraProtein: { label: "Extra protein", price: 3 }
                        }
                },
                {
                        id: "prb104",
                        name: "Cherry Garcia Protein Balls",
                        description: "with Enjoy Life dark chocolate chips, fresh blended cherries, Organifi complete vanilla vegan protein powder, maple syrup, sunflower butter, coconut flakes, gluten free oats, coconut oil, and love! (P:12g F:10g C:15g)= 200 calories per two balls<h4>**contains coconut**</h4>",
                        image: "https://cdn.shopify.com/s/files/1/0845/3564/0380/files/CherryGarcia-ProteinBalls-800.jpg?v=1757657599",
                        shopifyHandle: "prb104",
                        options: {
                                fifteenBalls: { label: "15 Balls", price: 25 },
                                thirtyBalls: { label: "30 Balls", price: 45 }
                        }
                }
        	]
		};
	}
	scrollToMealSection() {
		const mealSection = document.querySelector("[data-meal-section]");
		if (mealSection) {
			// Small delay to ensure content is rendered
			setTimeout(() => {
				mealSection.scrollIntoView({
					behavior: "smooth",
					block: "start"
				});
			}, 100);
		}
	}
	getMealPlanFromCustomer() {
		const customerTags = this.customer.tags || [];
		console.log("Customer tags found:", customerTags);

		// Check for special tag first
		const hasSpecialTag = customerTags.some(
			(tag) => tag.toLowerCase() === "special"
		);

		// Check for VIP
		if (customerTags.some((tag) => tag.toLowerCase() === "vip")) {
			console.log("✅ VIP customer detected");
			return {
				mealsPerWeek: 999,
				planName: "VIP",
				allowsOverage: true,
				requiresMinimum: false,
				isActive: true,
				isVIP: true,
				hasSpecialTag: hasSpecialTag
			};
		}

		// PRIORITY 1: Check for custom "meal plan" tags first
		const mealPlanTag = customerTags.find(
			(tag) => tag.toLowerCase().includes("meal plan") && /\d+/.test(tag) // Contains a number
		);

		if (mealPlanTag) {
			console.log("✅ Custom meal plan tag found:", mealPlanTag);

			// Extract the number from the meal plan tag
			const match = mealPlanTag.match(/(\d+)/);
			if (match) {
				const mealCount = parseInt(match[1]);
				console.log("✅ Using custom meal plan:", mealCount, "meals");

				return {
					mealsPerWeek: mealCount,
					planName: `${mealCount} Meal Plan`,
					allowsOverage: true,
					requiresMinimum: !hasSpecialTag,
					isActive: true,
					isCustomPlan: true, // Flag to indicate this came from custom tag
					hasSpecialTag: hasSpecialTag,
					sourceTag: mealPlanTag // Store the original tag for debugging
				};
			}
		}

		// FALLBACK: Use original "meals per week" logic if no custom meal plan tag
		console.log(
			"No custom meal plan tag found, checking legacy 'meals per week' tags"
		);

		let totalMeals = 0;
		let planComponents = [];

		customerTags.forEach((tag) => {
			const lowerTag = tag.toLowerCase();
			if (lowerTag.includes("meals per week")) {
				const match = lowerTag.match(/(\d+)\s+meals?\s+per\s+week/);
				if (match) {
					const mealCount = parseInt(match[1]);
					totalMeals += mealCount;
					planComponents.push(`${mealCount}`);
				}
			}
		});

		if (totalMeals > 0) {
			console.log("✅ Using legacy meals per week tags, total:", totalMeals);

			const plan = {
				mealsPerWeek: totalMeals,
				planName: `${totalMeals} Meals Per Week`,
				planComponents: planComponents,
				allowsOverage: true,
				requiresMinimum: !hasSpecialTag,
				isActive: true,
				isCombined: planComponents.length > 1,
				isLegacyPlan: true, // Flag to indicate this came from old tags
				hasSpecialTag: hasSpecialTag
			};

			return plan;
		}

		console.log("❌ No meal plan found");
		return null;
	}
	hasWednesdayOnlyTag() {
		const customerTags = this.customer.tags || [];
		return customerTags.some((tag) => tag.toLowerCase() === "wednesday");
	}

	hasVIPTag() {
		return this.mealPlan && this.mealPlan.isVIP;
	}
	isRestrictedPlan() {
		return this.mealPlan && this.mealPlan.mealsPerWeek <= 8;
	}

	// Add method to check if Thursday can be saved
	canSaveWednesday(baseDate) {
		// If no meal plan, VIP, large plan (10+), or special tag - no restrictions
		if (
			!this.mealPlan ||
			this.mealPlan.mealsPerWeek >= 10 ||
			this.mealPlan.isVIP ||
			this.mealPlan.hasSpecialTag
		) {
			return { allowed: true, reason: "" };
		}

		// Rest of the existing logic for regular customers with 8 or fewer meals...
		if (this.mealPlan.mealsPerWeek <= 8) {
			// Use calculateWeeklyTotal to get the correct Monday count
			const weeklyTotal = this.calculateWeeklyTotal(baseDate);
			const sundayMealCount = weeklyTotal.sunday;

			if (sundayMealCount < this.mealPlan.mealsPerWeek) {
				const needed = this.mealPlan.mealsPerWeek - sundayMealCount;
				return {
					allowed: false,
					reason: `You must select ${this.mealPlan.mealsPerWeek} meals from Sunday before adding Wednesday meals.`,
					detailedReason: `Your ${
						this.mealPlan.planName
					} requires selecting your core meals from Sunday first.\n\nCurrent Sunday selection: ${sundayMealCount} meals\nNeeded: ${needed} more meal${
						needed > 1 ? "s" : ""
					} from Sunday\n\nWednesday meals are available as add-ons after meeting your Sunday requirement.`,
					sundayMealsNeeded: needed,
					sundayMealsSaved: sundayMealCount
				};
			}
		}

		return { allowed: true, reason: "" };
	}

	async init() {
		/*
		if (window.maintenanceMode) {
			this.enableMaintenanceMode();
			return;
		}*/
		this.performStartupCleanup();

		// Correct the constructor's tag-based guess against Railway, which
		// is authoritative everywhere else in this system (see
		// ExtractMealPlanFromTags in program.cs — tags are only ever a
		// fallback on the backend, and shouldn't be primary here either).
		// Divergence should be rare — mainly right after an admin edits a
		// customer's plan directly in Railway before their Shopify tags
		// catch up — but when it happens, ordering limits need to reflect
		// the truth, not a stale tag.
		//
		// VIP is exempt entirely: it's an intentional, permanent tag-only
		// designation with no expectation of a real Railway subscription
		// record, so comparing it against Railway would be a false mismatch
		// every time, not a real one to correct.
		if (!this.customer.isGuest && this.railwayAPI && !(this.mealPlan && this.mealPlan.isVIP)) {
			try {
				const result = await this.railwayAPI.getCustomer(this.customer.id);
				if (result.success && result.customer) {
					// Grandfathered protein ball subscription (balls/week, 0 = none) —
					// used for the "added on top of your subscription" Yes/No prompt.
					this.subscriptionProteinBallQty = Number(result.customer.proteinBallQuantity) || 0;
					const railwayIsSubscriber = result.customer.subscriptionStatus === "active"
						&& !!result.customer.mealPlanSize;

					if (railwayIsSubscriber !== this.isActualSubscriber) {
						// Subscriber status itself was wrong — enableNonSubscriberMode()
						// already made real DOM changes at construction time (body
						// class, disabled elements, banners) that aren't safe to
						// hand-unwind here. A reload gets a clean, correct render
						// instead of risking a half-corrected page.
						//
						// Guarded to happen at most once per session: a genuinely
						// cancelled customer whose Shopify tags never got cleaned
						// up would otherwise hit this same mismatch after every
						// reload — tags say subscriber, Railway says cancelled —
						// looping forever. After one attempt, proceed with
						// whatever's currently rendered rather than reload again.
						const reloadKey = "railwayMealPlanReloadAttempted";
						if (!sessionStorage.getItem(reloadKey)) {
							sessionStorage.setItem(reloadKey, "true");
							console.warn("Subscriber status corrected from Railway — reloading for a clean render");
							window.location.reload();
							return;
						} else {
							console.warn("Subscriber status still mismatched after a reload — proceeding without looping again. Tags vs. Railway may be genuinely out of sync for this customer.");
						}
					}

					// Same subscriber status either way — safe to just correct the
					// meal count in place, since it's read on demand during
					// validation, not already rendered into the DOM.
					// planName is a separately-stored formatted string (e.g.
					// "4 Meal Plan") used in the stat card display AND several
					// validation/confirmation messages — correcting mealsPerWeek
					// alone left this stale everywhere it's used.
					if (result.customer.mealPlanSize && this.mealPlan) {
						this.mealPlan.mealsPerWeek = result.customer.mealPlanSize;
						this.mealPlan.planName = `${result.customer.mealPlanSize} Meal Plan`;
						console.log("✅ Meal plan corrected from Railway:", this.mealPlan.planName);
					}
				}
			} catch (error) {
				console.warn("Could not verify meal plan against Railway — using tag-based value:", error);
			}
		}

		// Load weekConfig + weeklyMenus from Railway, and selections in parallel
		const menuPromise = this.loadMenuFromRailway();
		const selectionsPromise = !this.customer.isGuest
			? this.loadFromRailway()
			: Promise.resolve({ success: false });

		const [menuResult, railwayResult] = await Promise.all([
			menuPromise,
			selectionsPromise,
			this.loadPendingRedemptions()
		]);

		// Apply weekConfig and weeklyMenus from Railway
		if (menuResult.success) {
			this.weekConfig = menuResult.weekConfig;
			this.weeklyMenus = menuResult.weeklyMenus;
			console.log('✅ Menu + weekConfig loaded from Railway:', this.weekConfig.availableWeeks.length, 'weeks');
		} else {
			console.warn('⚠️ Could not load menu from Railway — using hardcoded fallback');
			this.weeklyMenus = this.initializeMenus();
		}

		// Apply selections
		if (!this.customer.isGuest) {
			if (railwayResult.success && Object.keys(railwayResult.selections || {}).length > 0) {
				console.log('✅ Selections loaded from Railway');
			} else {
				console.log('⚠️ Railway selections empty or failed, falling back to localStorage');
				this.loadExistingSelections();
			}
		} else {
			this.loadExistingSelections();
		}

		// Defer mobile cleanup so first render isn't blocked
		setTimeout(() => this.forceMobileCleanup(), 0);

		this.renderWeeks();
		this.bindEvents();

		document.addEventListener("visibilitychange", () => {
			this.handlePageVisibilityChange();
		});

		console.log("Weekly Meal Selector initialized for customer:", this.customer.name);

		if (this.isNonSubscriber) {
			setTimeout(() => {
				this.autoSelectSundayMenu();
			}, 100);
		}
		this.customerPortal = new CustomerPortal(this);
	}

	// Load weekConfig + weeklyMenus from /menu/current — replaces hardcoded data
	async loadMenuFromRailway() {
		try {
			const response = await fetch(`${this.railwayAPI.baseUrl}/menu/current`);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const data = await response.json();
			if (!data.success) throw new Error('menu/current returned success=false');
			return {
				success: true,
				weekConfig: {
					version: this.dataVersion,
					availableWeeks: data.weekConfig.availableWeeks
				},
				weeklyMenus: data.weeklyMenus
			};
		} catch (err) {
			console.error('❌ loadMenuFromRailway failed:', err);
			return { success: false };
		}
	}
	enableMaintenanceMode() {
		// Hide the portal stats and history sections
		const portalStats = document.getElementById('portal-stats');
		if (portalStats) portalStats.style.display = 'none';
		
		const portalHistory = document.getElementById('customer-meal-history-portal');
		if (portalHistory) portalHistory.style.display = 'none';

		const portalActions = document.querySelector('.portal-actions');
		if (portalActions) portalActions.style.display = 'none';

		// Hide the week selection and meal section
		const weekSelection = document.querySelector('.week-selection');
		if (weekSelection) weekSelection.style.display = 'none';

		const mealSection = document.querySelector('[data-meal-section]');
		if (mealSection) mealSection.style.display = 'none';

		// Show maintenance banner in place of everything
		const welcomeCard = document.querySelector('.customer-welcome');
		if (welcomeCard) {
			const banner = document.createElement('div');
			banner.style.cssText = `
				background: #fff3cd;
				border: 2px solid #ffc107;
				border-radius: 12px;
				padding: 40px;
				text-align: center;
				margin: 40px auto;
				max-width: 700px;
			`;
			banner.innerHTML = `
				<div style="font-size: 48px; margin-bottom: 20px;">🔧</div>
				<h2 style="color: #856404; margin-bottom: 15px;">
					Meal Selection Temporarily Unavailable
				</h2>
				<p style="color: #856404; font-size: 16px; line-height: 1.6; margin-bottom: 15px;">
					We're making some exciting updates! 
					You'll be able to select your meals starting 
					<strong>Friday April 17th at 8:00 AM</strong>.
				</p>
				<p style="color: #856404; font-size: 14px; line-height: 1.6; margin-bottom: 20px;">
					In the meantime, feel free to browse our menu below to plan your selections ahead of time.
				</p>
				<p style="color: #856404; font-size: 14px;">
					Questions? <a href="/pages/contact" style="color: #856404; font-weight: bold;">
					Contact us
					</a> and we'll be happy to help.
				</p>
			`;
			welcomeCard.insertAdjacentElement('afterend', banner);
		}

		// Still render the menu so they can browse
		// Use non-subscriber mode which shows meals without any selection controls
		this.isNonSubscriber = true;
		this.weeklyMenus = this.initializeMenus();
		this.renderWeeks();
		
		// Auto-select the first available week so menu is immediately visible
		const weeks = this.getAvailableWeeks().filter(w => !w.isDeadlinePassed);
		if (weeks.length > 0) {
			this.selectWeek(weeks[0]);
		}
	}
	forceMobileCleanup() {
		const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			navigator.userAgent
		);
		const isSmallScreen = window.innerWidth <= 768;

		if (isMobile || isSmallScreen) {
			console.log("Mobile device detected - forcing data cleanup");

			// Clear ALL working selections on mobile
			this.mealQuantities = {};
			this.mealOptions = {};
			this.proteinBallSelections = {};

			// Add mobile-specific cleanup
			this.clearMobileCache();

			// Show mobile warning if data seems stale
			this.checkMobileDataFreshness();
		}
	}

	clearStaleData() {
		try {
			const storageKey = `meal_selections_${this.customer.id}`;
			const data = JSON.parse(localStorage.getItem(storageKey) || "{}");

			// Check if data has version info
			const storedVersion = data.dataVersion || data.version;
			const currentVersion = this.dataVersion;

			console.log(
				`Data version check: stored="${storedVersion}", current="${currentVersion}"`
			);

			if (!storedVersion || storedVersion !== currentVersion) {
				console.log(
					"🧹 CLEARING STALE DATA - Version mismatch or no version found"
				);

				// Keep only the essentials, clear everything else
				const cleanData = {
					dataVersion: currentVersion,
					lastUpdated: new Date().toISOString(),
					customerInfo: data.customerInfo || {
						id: this.customer.id,
						name: this.customer.name,
						email: this.customer.email
					},
					cleanupReason: storedVersion
						? `Version upgrade: ${storedVersion} -> ${currentVersion}`
						: "No version found"
				};

				localStorage.setItem(storageKey, JSON.stringify(cleanData));
				this.weeklySelections = {};
			}
		} catch (error) {
			console.error("Error checking data version:", error);
			// If localStorage is corrupted, clear it
			this.clearCorruptedStorage();
		}
	}

	detectStaleWeeks() {
		const now = new Date();
		const availableWeekIds = this.getAvailableWeeks().map((w) => w.id);
		const storedWeekIds = Object.keys(this.weeklySelections || {});

		let hasStaleWeeks = false;
		const staleWeeks = [];

		storedWeekIds.forEach((weekId) => {
			if (
				weekId === "lastUpdated" ||
				weekId === "customerInfo" ||
				weekId === "dataVersion"
			)
				return;

			// If stored week is not in current available weeks, it's stale
			if (!availableWeekIds.includes(weekId)) {
				console.log(`Stale week detected: ${weekId}`);
				staleWeeks.push(weekId);
				hasStaleWeeks = true;
			}
		});

		if (hasStaleWeeks) {
			console.log(`Found ${staleWeeks.length} stale weeks:`, staleWeeks);
			this.handleStaleWeeks(staleWeeks);
		}

		return { hasStaleWeeks, staleWeeks };
	}

	clearMobileCache() {
		try {
			// Clear session storage too (mobile browsers sometimes sync these)
			sessionStorage.clear();

			// Clear any temporary data
			const tempKeys = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (
					key &&
					(key.includes("temp_") ||
						key.includes("cache_") ||
						key.includes("old_meal_"))
				) {
					tempKeys.push(key);
				}
			}

			tempKeys.forEach((key) => localStorage.removeItem(key));
			console.log(`Cleared ${tempKeys.length} temporary cache items`);
		} catch (error) {
			console.error("Error clearing mobile cache:", error);
		}
	}

	checkMobileDataFreshness() {
		try {
			const storageKey = `meal_selections_${this.customer.id}`;
			const data = JSON.parse(localStorage.getItem(storageKey) || "{}");
			const lastUpdated = data.lastUpdated;

			if (lastUpdated) {
				const daysSinceUpdate =
					(new Date() - new Date(lastUpdated)) / (1000 * 60 * 60 * 24);

				if (daysSinceUpdate > 7) {
					console.log(`Mobile data is ${daysSinceUpdate.toFixed(1)} days old`);
				}
			}
		} catch (error) {
			console.error("Error checking data freshness:", error);
		}
	}

	handleStaleWeeks(staleWeeks) {
		const storageKey = `meal_selections_${this.customer.id}`;
		const data = JSON.parse(localStorage.getItem(storageKey) || "{}");

		// Archive stale weeks instead of deleting (for reference)
		const archivedWeeks = data.archivedWeeks || {};

		staleWeeks.forEach((weekId) => {
			if (data[weekId]) {
				archivedWeeks[weekId] = {
					...data[weekId],
					archivedAt: new Date().toISOString(),
					reason: "Week no longer available"
				};
				delete data[weekId];

				// Also remove from memory
				if (this.weeklySelections[weekId]) {
					delete this.weeklySelections[weekId];
				}
			}
		});

		data.archivedWeeks = archivedWeeks;
		data.lastCleanup = new Date().toISOString();
		data.dataVersion = this.dataVersion;

		localStorage.setItem(storageKey, JSON.stringify(data));

		console.log(`Archived ${staleWeeks.length} stale weeks`);
	}

	clearCorruptedStorage() {
		try {
			const storageKey = `meal_selections_${this.customer.id}`;
			localStorage.removeItem(storageKey);

			// Create fresh storage
			const freshData = {
				dataVersion: this.dataVersion,
				lastUpdated: new Date().toISOString(),
				customerInfo: {
					id: this.customer.id,
					name: this.customer.name,
					email: this.customer.email
				},
				cleanupReason: "Corrupted data cleared"
			};

			localStorage.setItem(storageKey, JSON.stringify(freshData));

			console.log("Cleared corrupted storage and created fresh data");
		} catch (error) {
			console.error("Could not clear corrupted storage:", error);
			alert(
				"Storage error detected. Please clear your browser data and try again."
			);
		}
	}

	// 9. AUTOMATIC CLEANUP ON LOAD
	performStartupCleanup() {
		console.log("Performing startup cleanup...");

		// Clear any working selections that might be stale
		this.mealQuantities = {};
		this.mealOptions = {};
		this.proteinBallSelections = {};

		// Detect stale weeks
		this.detectStaleWeeks();

		// Ensure UI starts clean
		setTimeout(() => {
			this.enforceCleanUIState();
		}, 100);

		console.log("Startup cleanup completed");
	}

	// 10. VERSION UPDATER (call this when you update menus)
	updateDataVersion(newVersion, reason = "Menu updated") {
		this.dataVersion = newVersion;

		const storageKey = `meal_selections_${this.customer.id}`;
		try {
			const data = JSON.parse(localStorage.getItem(storageKey) || "{}");

			// Mark old version for cleanup
			if (data.dataVersion && data.dataVersion !== newVersion) {
				data.previousVersion = data.dataVersion;
				data.upgradeReason = reason;
				data.upgradedAt = new Date().toISOString();
			}

			data.dataVersion = newVersion;
			data.lastUpdated = new Date().toISOString();

			localStorage.setItem(storageKey, JSON.stringify(data));
			console.log(`Data version updated to ${newVersion}: ${reason}`);
		} catch (error) {
			console.error("Error updating data version:", error);
		}
	}

	checkIfNonSubscriber() {
		// Check multiple conditions for non-subscriber status:

		// 1. Guest users
		if (this.customer.isGuest) {
			console.log("Non-subscriber detected: Guest user");
			return true;
		}

		// 2. No customer object (not logged in)
		if (!this.customer || !this.customer.id) {
			console.log("Non-subscriber detected: No customer data");
			return true;
		}

		// 3. Check for subscription tags
		const subscriptionTags = ["meals per week", "vip", "VIP"];

		const hasSubscriptionTag =
			this.customer.tags &&
			this.customer.tags.some((tag) =>
				subscriptionTags.some((subTag) =>
					tag.toLowerCase().includes(subTag.toLowerCase())
				)
			);

		if (!hasSubscriptionTag) {
			console.log("Non-subscriber detected: No subscription tags found");
			return true;
		}

		// 4. Check if meal plan exists
		if (!this.mealPlan || !this.mealPlan.planName) {
			console.log("Non-subscriber detected: No meal plan found");
			return true;
		}

		return false;
	}

	bindEvents() {
		// Save button (replacement mode)
		const saveBtn = document.querySelector("[data-save-order]");
		if (saveBtn) {
			saveBtn.addEventListener("click", () => this.saveWeeklyOrder(false));
		}
		
		// Add to existing button (addition mode) 
		const addBtn = document.querySelector("[data-add-to-meals]");
		if (addBtn) {
			addBtn.addEventListener("click", () => this.saveWeeklyOrder(true));
		}
		
		// Clear selections button
		const clearBtn = document.querySelector('[data-clear-selections]');
		if (clearBtn) {
			clearBtn.addEventListener('click', () => this.clearAllSelections());
		}

		// Sides save button
		const saveSidesBtn = document.querySelector("[data-save-sides]");
		if (saveSidesBtn) {
			saveSidesBtn.addEventListener("click", () => this.saveSidesSelection());
		}
	}

	enableNonSubscriberMode() {
		console.log("Enabling non-subscriber mode...");

		// Add class to body for CSS targeting
		document.body.classList.add("non-subscriber-mode");

		// Update the welcome message
		this.updateWelcomeForNonSubscriber();

		// Disable interactive elements
		this.disableInteractiveElements();

		// Automatically select and show Monday menu
		this.autoSelectSundayMenu();

		// Add subscribe CTA after meals are loaded
		setTimeout(() => {
			this.addSubscribeCTA();
			this.addViewOnlyBanner();
			this.hideDeadlineNotice();
		}, 500);
	}

	autoSelectSundayMenu() {
		console.log("Auto-selecting Sunday menu for non-subscriber...");

		const deliveries = this.getAvailableWeeks();

		// Find the earliest available Sunday and Wednesday
		const sundayDeliveries = deliveries.filter(
			(d) => d.id.includes("sunday") && !d.isDeadlinePassed
		);
		const wednesdayDeliveries = deliveries.filter(
			(d) => d.id.includes("wednesday") && !d.isDeadlinePassed
		);

		const earliestSunday = sundayDeliveries.sort(
			(a, b) => a.deliveryDate - b.deliveryDate
		)[0];
		const earliestWednesday = wednesdayDeliveries.sort(
			(a, b) => a.deliveryDate - b.deliveryDate
		)[0];

		console.log("Earliest Sunday delivery:", earliestSunday?.id);
		console.log("Earliest Wednesday delivery:", earliestWednesday?.id);

		if (earliestSunday) {
			this.selectWeek(earliestSunday);

			// For non-subscribers, also append Wednesday menu, then Kids last
			if (earliestWednesday && this.isNonSubscriber) {
				setTimeout(() => {
					this.appendWednesdayMenu(earliestWednesday);
					this.appendKidsMenu(earliestSunday, earliestWednesday);
				}, 100);
			} else if (this.isNonSubscriber) {
				setTimeout(() => {
					this.appendKidsMenu(earliestSunday, null);
				}, 100);
			}
		}

		const mealSection = document.querySelector("[data-meal-section]");
		if (mealSection) {
			mealSection.style.display = "block";
		}
	}
	appendWednesdayMenu(wednesdayDelivery) {
		const mealsGrid = document.querySelector("[data-meals-grid]");
		if (!mealsGrid) return;

		// Excludes kids meals — those get shown once, combined with any on
		// Sunday, in the single Kids section appended last (appendKidsMenu).
		const wednesdayMeals = (this.weeklyMenus[wednesdayDelivery.id] || [])
			.filter((m) => !this.isKidsMeal(m.id));

		// Add Wednesday title
		const wednesdayTitle = document.createElement("h2");
		wednesdayTitle.className = "wednesday-menu-title";
		wednesdayTitle.style.cssText = `
			grid-column: 1 / -1;
			text-align: center;
			margin: 3rem 0 2rem 0;
			font-size: 2rem;
			color: #3D081B;
			padding-top: 2rem;
			border-top: 2px solid #e1e3e5;
		`;
		wednesdayTitle.textContent = `${wednesdayDelivery.display} Menu`;

		mealsGrid.appendChild(wednesdayTitle);

		// Add Wednesday meals
		wednesdayMeals.forEach((meal) => {
			const mealElement = document.createElement("div");
			mealElement.innerHTML = this.renderMealCard(meal, wednesdayDelivery.id);
			mealsGrid.appendChild(mealElement.firstElementChild);
		});
	}
	appendKidsMenu(sundayDelivery, wednesdayDelivery) {
		const mealsGrid = document.querySelector("[data-meals-grid]");
		if (!mealsGrid) return;

		// Combine kids meals from whichever day(s) they're assigned to,
		// de-duplicated by meal ID in case the same item is on both —
		// guests only ever see each one once, in this one final section.
		const sundayKids = (this.weeklyMenus[sundayDelivery.id] || [])
			.filter((m) => this.isKidsMeal(m.id));
		const wednesdayKids = wednesdayDelivery
			? (this.weeklyMenus[wednesdayDelivery.id] || []).filter((m) => this.isKidsMeal(m.id))
			: [];

		const seen = new Set();
		const kidsMeals = [...sundayKids, ...wednesdayKids].filter((m) => {
			if (seen.has(m.id)) return false;
			seen.add(m.id);
			return true;
		});

		if (kidsMeals.length === 0) return;

		const divider = document.createElement("div");
		divider.className = "kids-menu-divider";
		divider.style.cssText = `
			grid-column: 1 / -1;
			text-align: center;
			margin: 3rem 0 2rem 0;
			padding-top: 2rem;
			border-top: 2px solid #e1e3e5;
		`;
		divider.innerHTML = `
			<h1 style="margin: 0 0 0.5rem 0; color: #3D081B;">Simple Favorites</h1>
			<p style="margin: 0;"><i>Kid-friendly classics everyone can enjoy</i></p>
		`;
		mealsGrid.appendChild(divider);

		kidsMeals.forEach((meal) => {
			const mealElement = document.createElement("div");
			mealElement.innerHTML = this.renderMealCard(meal, sundayDelivery.id);
			mealsGrid.appendChild(mealElement.firstElementChild);
		});
	}
	hideDeadlineNotice() {
		// Hide the deadline notice for non-subscribers
		const deadlineNotice = document.querySelector(
			".deadline-notice, [data-deadline-notice]"
		);
		if (deadlineNotice) {
			deadlineNotice.style.display = "none";
		}
	}

	updateWelcomeForNonSubscriber() {
		const welcomeEl = document.querySelector(".customer-welcome h2");
		const statusEl = document.querySelector(".subscription-status");

		if (this.maintenanceMode && this.isActualSubscriber) {
			// A real subscriber, just paused by maintenance mode — don't tell
			// them to subscribe or that they have no active subscription,
			// both of which would be simply wrong for them.
			if (welcomeEl) {
				welcomeEl.textContent = `Welcome back, ${this.customer.firstName}!`;
			}
			if (statusEl) {
				statusEl.innerHTML = `
					<span class="badge" style="background: #ffc107; color: #333;">🔧 Ordering Paused</span>
					<span class="caption-with-letter-spacing">Maintenance in progress — check back soon</span>
				`;
			}
			return;
		}

		if (welcomeEl) {
			if (this.customer.isGuest) {
				welcomeEl.textContent = "Welcome! Browse Our Weekly Menu";
			} else {
				welcomeEl.textContent = `Welcome, ${this.customer.firstName}! Subscribe to Start Ordering`;
			}
		}

		if (statusEl) {
			statusEl.innerHTML = `
				<span class="badge" style="background: #ffc107; color: #333;">● Browsing Mode</span>
				<span class="caption-with-letter-spacing">No Active Subscription</span>
			`;
		}
	}

	disableInteractiveElements() {
		// Disable meal quantity controls
		const quantityControls = document.querySelectorAll(".quantity-controls");
		quantityControls.forEach((control) => {
			control.style.display = "none";
		});

		// Hide meal options
		const mealOptions = document.querySelectorAll(
			".meal-options, .addon-options-wrapper"
		);
		mealOptions.forEach((option) => {
			option.style.display = "none";
		});

		// Hide order summary
		const orderSummary = document.querySelector(".order-summary");
		if (orderSummary) {
			orderSummary.style.display = "none";
		}

		// Hide save button
		const saveBtn = document.querySelector("[data-save-order]");
		if (saveBtn) {
			saveBtn.style.display = "none";
		}

		// Make week cards less interactive
		const weekCards = document.querySelectorAll(".week-card");
		weekCards.forEach((card) => {
			card.style.cursor = "default";
		});
	}

	addViewOnlyBanner() {
		const mealSelection = document.querySelector(".meal-selection__header");
		if (!mealSelection || document.querySelector(".view-only-banner")) return;

		const banner = document.createElement("div");
		banner.className = "view-only-banner";

		if (this.maintenanceMode) {
			banner.innerHTML = `
				<p class="view-only-banner-text">
					🔧 Ordering is temporarily paused for maintenance. You can still browse this week's menu.
				</p>
			`;
		} else {
			banner.innerHTML = `
				<p class="view-only-banner-text">
					👀 You're viewing our menu in browse mode. 
					<a href="https://www.primalbalancenutrition.com/pages/subscribe" class="view-only-banner-link">
						Subscribe now
					</a> 
					to customize your meals and place orders.
				</p>
			`;
		}
		mealSelection.appendChild(banner);
	}

	addSubscribeCTA() {
		const mealsContainer = document.querySelector(".meals-container");
		if (!mealsContainer || document.querySelector(".subscribe-cta-section")) return;

		const ctaSection = document.createElement("div");
		ctaSection.className = "subscribe-cta-section";

		if (this.maintenanceMode) {
			// Same message for everyone during maintenance — subscribers and
			// guests alike, since ordering is paused for the whole site, not
			// just this one visitor. Inviting new signups while ordering is
			// broken doesn't make sense either, so this replaces the normal
			// subscribe pitch entirely rather than showing alongside it.
			ctaSection.innerHTML = `
				<h2 class="subscribe-cta-title">🔧 Ordering is temporarily paused</h2>
				<p class="subscribe-cta-subtitle">
					We're making some updates behind the scenes. You can still browse this week's menu below —
					selecting and saving orders will be back shortly. Thanks for your patience!
				</p>
			`;
			mealsContainer.parentNode.insertBefore(ctaSection, mealsContainer.nextSibling);
			return;
		}

		ctaSection.innerHTML = `
			<h2 class="subscribe-cta-title">Ready to eat healthy and feel good?</h2>

			<p class="subscribe-cta-subtitle">
				Fresh (never frozen) free-range, grass-fed, organic meals
			</p>

			<ul class="subscribe-cta-benefits" role="list">
				<li class="subscribe-benefit">Seed Oil Free</li>
				<li class="subscribe-benefit">Gluten Free</li>
				<li class="subscribe-benefit">Dairy Free</li>
				<li class="subscribe-benefit">Soy Free</li>
				<li class="subscribe-benefit">Nut Free</li>
				<li class="subscribe-benefit">Shellfish Free</li>
				<li class="subscribe-benefit">Plastic-Free</li>
			</ul>

			<p class="subscribe-cta-note">
				No compromises, just real food
			</p>

			<a href="https://www.primalbalancenutrition.com/pages/subscribe" class="subscribe-cta-button">
				View Meal Plans
			</a>
		`;

		mealsContainer.parentNode.insertBefore(ctaSection, mealsContainer.nextSibling);
	}

	getAvailableWeeks() {
		const deliveries = [];
		const now = new Date();

		// If weekConfig not yet loaded, build a minimal set from known selection data
		// This prevents "no upcoming deliveries" while Railway data is loading
		if (!this.weekConfig.availableWeeks || this.weekConfig.availableWeeks.length === 0) {
			const knownWeekIds = Object.keys(this.weeklySelections || {});
			knownWeekIds.forEach(weekId => {
				const dateMatch = weekId.match(/(\d{4}-\d{2}-\d{2})$/);
				if (!dateMatch) return;
				const date = new Date(dateMatch[1]);
				const isSunday = weekId.startsWith('sunday');
				// Tuesday 4PM Pacific before the delivery Sunday
				const tuesdayBefore = new Date(date);
				tuesdayBefore.setDate(date.getDate() - (isSunday ? 5 : 8));
				tuesdayBefore.setHours(23, 0, 0, 0); // 4PM Pacific = 11PM UTC
				const isDeadlinePassed = now > tuesdayBefore;
				deliveries.push({
					id: weekId,
					sundayDate: isSunday ? dateMatch[1] : new Date(date.getTime() - 3*24*60*60*1000).toISOString().split('T')[0],
					wednesdayDate: isSunday ? new Date(date.getTime() + 3*24*60*60*1000).toISOString().split('T')[0] : dateMatch[1],
					display: weekId,
					deliveryDate: date,
					deadline: tuesdayBefore,
					isDeadlinePassed,
					millisecondsLeft: Math.max(0, tuesdayBefore - now),
					hoursLeft: Math.max(0, Math.ceil((tuesdayBefore - now) / (1000 * 60 * 60))),
					isUrgent: false,
					deadlineText: isDeadlinePassed ? 'Deadline passed' : 'Upcoming'
				});
			});
			return deliveries;
		}

		this.weekConfig.availableWeeks.forEach((week) => {
		
			const deadlineString = week.deadline;
			let deadline;

			if (deadlineString.includes("T") && 
				!deadlineString.includes("Z") &&
				!deadlineString.includes("+") && 
				!deadlineString.includes("-")) {

				const tempDate = new Date(deadlineString);
				const pacificOffset = this.getPacificTimeOffset(tempDate);
				deadline = new Date(tempDate.getTime() - pacificOffset);
			} else {
				deadline = new Date(deadlineString);
			}

			const isDeadlinePassed = now > deadline;
			const millisecondsLeft = Math.max(0, deadline - now);
			const hoursLeft = Math.max(0, Math.ceil(millisecondsLeft / (1000 * 60 * 60)));
			const isUrgent = hoursLeft <= 48 && !isDeadlinePassed;

			let deadlineText;
			if (isDeadlinePassed) {
				deadlineText = `Deadline was ${week.deadlineDisplay}`;
			} else if (hoursLeft <= 24) {
				deadlineText = `Only ${hoursLeft} hours left to customize!`;
			} else {
				const daysLeft = Math.floor(hoursLeft / 24);
				const remainingHours = hoursLeft % 24;
				if (daysLeft > 0) {
					deadlineText = `${daysLeft}d ${remainingHours}h left - Deadline: ${week.deadlineDisplay}`;
				} else {
					deadlineText = `${hoursLeft}h left - Deadline: ${week.deadlineDisplay}`;
				}
			}

			// Sunday delivery
			deliveries.push({
				id: `sunday-${week.sundayDate}`,
				deliveryDate: new Date(week.sundayDate),
				display: week.sundayDisplay,
				dayName: "Sunday",
				deadline: deadline,
				deadlineText: deadlineText,
				deadlineDisplay: week.deadlineDisplay, 
				isDeadlinePassed: isDeadlinePassed,
				hoursLeft: hoursLeft,
				isUrgent: isUrgent,
				hasSelection: this.hasExistingSelection(`sunday-${week.sundayDate}`),
				weekGroup: week.id
			});

			// Wednesday delivery
			deliveries.push({
				id: `wednesday-${week.wednesdayDate}`,
				deliveryDate: new Date(week.wednesdayDate),
				display: week.wednesdayDisplay,
				dayName: "Wednesday",
				deadline: deadline,
				deadlineText: deadlineText,
				deadlineDisplay: week.deadlineDisplay, 
				isDeadlinePassed: isDeadlinePassed,
				hoursLeft: hoursLeft,
				isUrgent: isUrgent,
				hasSelection: this.hasExistingSelection(`wednesday-${week.wednesdayDate}`),
				weekGroup: week.id
			});
		});

		return deliveries.sort((a, b) => {
			// Primary: Active (not past deadline) before past deadline
			if (a.isDeadlinePassed !== b.isDeadlinePassed) {
				return a.isDeadlinePassed ? 1 : -1;
			}

			// Active weeks: chronological — soonest delivery first (3/23 before 3/30)
			if (!a.isDeadlinePassed) {
				if (a.weekGroup !== b.weekGroup) {
					return a.weekGroup.localeCompare(b.weekGroup); // ascending = soonest first
				}
				// Within same week: Sunday before Wednesday
				return a.dayName === "Sunday" ? -1 : 1;
			}

			// Past deadline weeks: most recent first
			if (a.weekGroup !== b.weekGroup) {
				return b.weekGroup.localeCompare(a.weekGroup);
			}
			return a.dayName === "Sunday" ? -1 : 1;
		});
	}

	isWednesdayUnlocked(weekGroup) {
		// 10+ meal plans, VIP, special, or Wednesday-only: always unlocked
		if (
			!this.mealPlan ||
			this.mealPlan.mealsPerWeek >= 10 ||
			this.mealPlan.isVIP ||
			this.mealPlan.hasSpecialTag ||
			this.hasWednesdayOnlyTag()
		) {
			return true;
		}
		// Restricted plan (<10): Wednesday unlocked only after Sunday is saved
		const sundayWeekId = `sunday-${weekGroup}`;
		return this.hasExistingSelection(sundayWeekId);
	}

	getExtraMealPrice() {
		if (!this.mealPlan) return this.extraMealPricing.default;
		const planSize = this.mealPlan.mealsPerWeek;
		return this.extraMealPricing[planSize] ?? this.extraMealPricing.default;
	}

	// Helper method to get Pacific Time offset
	getPacificTimeOffset(date) {
		// Create a date in Pacific Time
		const utc = date.getTime() + date.getTimezoneOffset() * 60000;

		// Pacific Time is UTC-8 (PST) or UTC-7 (PDT)
		// Check if it's during Daylight Saving Time
		const year = date.getFullYear();

		// DST in 2025: March 9 - November 2
		const dstStart = new Date(year, 2, 9); // March 9
		const dstEnd = new Date(year, 10, 2); // November 2

		// Find the second Sunday in March
		dstStart.setDate(9 + ((7 - dstStart.getDay()) % 7));
		// Find the first Sunday in November
		dstEnd.setDate(1 + ((7 - dstEnd.getDay()) % 7));

		const isDST = date >= dstStart && date < dstEnd;
		const pacificOffset = isDST ? 7 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000; // 7 or 8 hours in milliseconds

		return pacificOffset;
	}

	renderWeeks() {
		const weekGrid = document.querySelector("[data-week-grid]");
		if (!weekGrid) {
			console.error("Week grid element not found!");
			return;
		}

		let deliveries = this.getAvailableWeeks();

		// Filter for Wednesday-only customers
		if (this.hasWednesdayOnlyTag() && !this.hasVIPTag()) {
			// VIP overrides Wednesday restriction
			console.log(
				"Wednesday-only customer detected - filtering to Wednesday deliveries"
			);
			deliveries = deliveries.filter((d) => d.id.includes("wednesday"));
		}
		// Filter out past deadline weeks for guests and non-subscribers
		if (this.customer.isGuest || !this.mealPlan) {
			console.log("Guest or non-subscriber - hiding past deadline weeks");
			deliveries = deliveries.filter((d) => !d.isDeadlinePassed);
		}
		console.log("Rendering weeks:", deliveries);

		const weekCards = deliveries.map((delivery) => {

			// ── Wednesday gating for restricted plans (<10 meals) ──────────────────
			const isRestrictedWednesday =
				delivery.id.includes("wednesday") &&
				this.mealPlan &&
				this.mealPlan.mealsPerWeek < 10 &&
				!this.mealPlan.isVIP &&
				!this.mealPlan.hasSpecialTag &&
				!this.hasWednesdayOnlyTag();

			if (isRestrictedWednesday && delivery.isDeadlinePassed === false) {
				const wednesdayUnlocked = this.isWednesdayUnlocked(delivery.weekGroup);

				if (!wednesdayUnlocked) {
					// Show a locked "teaser" card — Sunday must be saved first
					return `<div class="week-card week-card--wednesday-locked" data-week-id="${delivery.id}" title="Save your Sunday meals first to unlock Wednesday extras">
						<div class="week-date" style="font-size:1.3rem; color:#856404; font-weight:700;">➕ Optional Extra Meals</div>
						<div class="week-date">${delivery.display}</div>
						<div class="week-status" style="color:#856404;">🔒 Save Sunday order first</div>
						<div class="week-deadline">Wednesday add-ons unlock after your Sunday selections are saved</div>
					</div>`;
				}

				// Unlocked — show with clear "extra / add-on" language
				const extraStatusText = delivery.hasSelection
					? "✓ Extra Meals Selected"
					: "Want more meals this week? Extra meals will be added to your bill.";
				const extraStatusClass = delivery.hasSelection
					? "week-status--selected"
					: "week-status--extra";

				return `<div class="week-card week-card--extra ${
					this.selectedWeek && this.selectedWeek.id === delivery.id ? "week-card--selected" : ""
				}" data-week-id="${delivery.id}">
					<div class="week-date" style="font-size:1.2rem; color:#856404; font-weight:700; margin-bottom:0.3rem;">➕ EXTRA MEALS — NOT IN PLAN</div>
					<div class="week-date">${delivery.display}</div>
					<div class="week-status ${extraStatusClass}">${extraStatusText}</div>
					<div class="week-deadline">Deadline: ${delivery.deadlineDisplay}</div>
				</div>`;
			}
			// ── End Thursday gating ───────────────────────────────────────────────

			const selectedClass =
				this.selectedWeek && this.selectedWeek.id === delivery.id
					? "week-card--selected"
					: "";
			const pastClass = delivery.isDeadlinePassed ? "week-card--past" : "";

			let statusText;
			let statusClass;
			let deadlineDisplayText;

			if (delivery.id === "thursday-2025-12-25") {
				statusText = `<span style="color: #2d7a2d; font-weight: 600;">Merry Christmas 🎄</span><br><span style="font-weight: 600;">&</span><br><span style="color: #1e5a9e; font-weight: 600;">Happy Hanukkah 🕎</span>`;
				statusClass = "week-status--holiday";
				deadlineDisplayText = "";
			} else {
				statusText = delivery.hasSelection
					? "✓ Meals Selected"
					: delivery.isDeadlinePassed
					? "👨‍🍳 No meals selected"
					: "Click to Customize";
				statusClass = delivery.hasSelection
					? "week-status--selected"
					: "week-status--chef-choice";

				deadlineDisplayText = delivery.isDeadlinePassed
					? `Deadline was ${delivery.deadlineDisplay}`
					: `Deadline: ${delivery.deadlineDisplay}`;
			}

			return `<div class="week-card ${selectedClass} ${pastClass}" data-week-id="${delivery.id}">
				<div class="week-date">${delivery.display}</div>
				<div class="week-status ${statusClass}">${statusText}</div>
				<div class="week-deadline">${deadlineDisplayText}</div>
			</div>`;
		});

		// Format "Week of Month Day" from YYYY-MM-DD
		const weekLabel = (dateStr) => {
			const [y, m, day] = dateStr.split('-').map(Number);
			const date = new Date(y, m - 1, day);
			return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
		};

		// Inject "Week of..." label before each new week group.
		// grid-column: 1 / -1 spans both columns on desktop, full width on mobile.
		let lastGroup = null;
		let html = '';
		weekCards.forEach((cardHtml, i) => {
			const delivery = deliveries[i];
			if (delivery.weekGroup !== lastGroup) {
				const isPast = deliveries
					.filter(d => d.weekGroup === delivery.weekGroup)
					.every(d => d.isDeadlinePassed);
				const pastTag = isPast ? ' <span class="week-group-past-tag">Past</span>' : '';
				html += '<div class="week-group-label' + (isPast ? ' week-group-label--past' : '') + '" style="grid-column: 1 / -1;">Week of ' + weekLabel(delivery.weekGroup) + pastTag + '</div>';
				lastGroup = delivery.weekGroup;
			}
			html += cardHtml;
		});

		weekGrid.innerHTML = html;

		// Add event listeners for week selection
		weekGrid.querySelectorAll(".week-card").forEach((card) => {
			card.addEventListener("click", () => {
				const deliveryId = card.dataset.weekId;

				// Disable clicking on Christmas day
				if (deliveryId === "thursday-2025-12-25") {
					return; // Do nothing
				}

				const delivery = deliveries.find((d) => d.id === deliveryId);
				this.selectWeek(delivery);
			});
		});
	}

	selectWeek(delivery) {
		this.selectedWeek = delivery;
		this.renderWeeks();
		this.showMealSelection();
		this.scrollToMealSection();
	}

	showMealSelection() {
		const mealSection = document.querySelector("[data-meal-section]");
		if (!mealSection) return;

		mealSection.style.display = "block";

		this.updateWeekTitle();
		this.updateDeadlineNotice();

		// Sides is now an always-visible section (no more tab to switch to),
		// so it needs to load directly here instead of waiting for a tab
		// click. Still Sunday-only — hide the whole section on Wednesday
		// rather than showing an always-empty one.
		const isSunday = this.selectedWeek.id && this.selectedWeek.id.startsWith("sunday");
		const sidesDivider = document.querySelector(".sides-section-divider");
		const sidesContainer = document.querySelector(".sides-container");
		const sidesSummaryEl = document.querySelector("[data-sides-summary]");
		if (sidesDivider) sidesDivider.style.display = isSunday ? "" : "none";
		if (sidesContainer) sidesContainer.style.display = isSunday ? "" : "none";
		if (isSunday) {
			this.loadAndRenderSides();
		} else if (sidesSummaryEl) {
			sidesSummaryEl.style.display = "none";
		}

		if (this.selectedWeek.isDeadlinePassed) {
			console.log("📦 Showing past delivery - NOT clearing working state");
			this.renderPastDelivery();
		} else {
			console.log("📝 Showing active week - ensuring clean working state");
			this.ensureCleanWorkingState();
			this.renderMeals();
			this.renderRedemptionBanner();
		}

		this.updateOrderSummary();
	}

	ensureCleanWorkingState() {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId) return;

		// Reset working data to empty/default state
		this.mealQuantities[weekId] = {};
		this.mealOptions[weekId] = {};

		if (!this.proteinBallSelections[weekId]) {
			this.proteinBallSelections[weekId] = {};
		}
		// Protein ball selections keyed by meal ID, no pre-seeding needed

		console.log(`🧹 Ensured clean working state for ${weekId}`);
	}

	// ── SIDES (à la carte) ──────────────────────────────────────────────
	// Kept entirely separate from meal quantities/totals: sides never count
	// toward the meal-plan minimum, and are saved through their own endpoint.

	// switchMenuTab — commented out (August 2026), no longer called from
	// anywhere since the Menu/Sides tab UI itself is commented out in
	// weekly-meals.liquid (Sides is now an always-visible section instead).
	// Kept together with that HTML as one restorable unit in case the tab
	// approach comes back later.
	// switchMenuTab(tab) {
	// 	this.activeMenuTab = tab;
	//
	// 	document.querySelectorAll("[data-menu-tab]").forEach((btn) => {
	// 		btn.classList.toggle("menu-tab--active", btn.dataset.menuTab === tab);
	// 	});
	// 	document.querySelectorAll("[data-tab-panel]").forEach((panel) => {
	// 		panel.style.display = panel.dataset.tabPanel === tab ? "block" : "none";
	// 	});
	//
	// 	const root = document.querySelector(".weekly-meal-selector") || document.body;
	// 	root.setAttribute("data-active-menu-tab", tab);
	//
	// 	if (tab === "sides") {
	// 		this.loadAndRenderSides();
	// 	}
	//
	// 	this.updateOrderSummary();
	// }

	// Cleanup pass (August 2026): shared by every place that previously
	// hand-wrote `weekId.split("-")` to build a YYYY-MM-DD string — was
	// duplicated 8+ times, including 2 I added myself in the same session
	// this was built. weekId format is always "<sunday|wednesday>-YYYY-MM-DD".
	getDeliveryDateFromWeekId(weekId) {
		if (!weekId) return null;
		const parts = weekId.split("-");
		if (parts.length < 4) return null;
		return `${parts[1]}-${parts[2]}-${parts[3]}`;
	}

	getSidesDeliveryDate() {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		return this.getDeliveryDateFromWeekId(weekId);
	}

	// Turns any macros text into one consistent format: "P:38g • F:19g • C:50g • 520 Cal".
	// Returns "" if it can't find all four numbers.
	formatMacros(raw) {
		const t = String(raw || "");
		const g = (re) => { const m = t.match(re); return m ? m[1] : null; };
		const p = g(/P\s*:\s*(\d+(?:\.\d+)?)\s*g/i), f = g(/F\s*:\s*(\d+(?:\.\d+)?)\s*g/i),
			c = g(/C\s*:\s*(\d+(?:\.\d+)?)\s*g/i), cal = g(/(\d+(?:\.\d+)?)\s*cal/i);
		return (p !== null && f !== null && c !== null && cal !== null)
			? `P:${p}g • F:${f}g • C:${c}g • ${cal} Cal` : "";
	}

	// Description paragraph, then macros on their own line, then the allergen line.
	// Uses meal.macros when the server sends it; for older data it pulls the
	// "(P:38g • F:19g • C:50g • 520 cal)" part out of the description text.
	mealDescriptionHtml(meal) {
		let text = String(meal.description || "");
		let macros = meal.macros ? this.formatMacros(meal.macros) : "";
		const legacy = text.match(/\s*\(?\s*P\s*:\s*\d+(?:\.\d+)?\s*g[^<]*?\d+(?:\.\d+)?\s*cal\s*\)?/i);
		if (legacy) {
			if (!macros) macros = this.formatMacros(legacy[0]);
			text = text.replace(legacy[0], "");
		}
		let allergenText = "";
		const h = text.indexOf("<h4");
		if (h >= 0) {
			allergenText = text.slice(h).replace(/<[^>]*>/g, "").replace(/\*\*/g, "").replace(/^\s*contains\s*/i, "").trim();
			text = text.slice(0, h);
		}
		return `<p class="meal-description">${text.trim()}</p>` +
			(macros ? `<div class="meal-macros">${macros}</div>` : "") +
			(allergenText ? `<div class="meal-allergens"><span class="meal-allergens-label">Allergens:</span> ${allergenText}</div>` : "");
	}

	async loadAndRenderSides() {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		const grid = document.querySelector("[data-sides-grid]");
		if (!weekId || !grid) return;

		grid.innerHTML = `<div class="sides-empty-note">Loading sides...</div>`;

		// Catalog is cached per weekId for the session — only fetch once
		if (!this.sidesCatalog[weekId]) {
			const catalogResult = await this.railwayAPI.getAlaCarteCatalog(weekId);
			this.sidesCatalog[weekId] = catalogResult.items || [];
		}

		// Extras counters always start at 0, exactly like meal counters (October 2026):
		// a counter means "add this many to my order", never "this is what's saved".
		// Saved Extras show in the order summary / Upcoming Orders like saved meals,
		// and saving adds to them (see saveSidesQuietly). Previously the counters were
		// pre-filled with the saved quantity after a refresh, so customers couldn't
		// tell whether saving again would add a second one, and lowering it to 0 did
		// nothing. Removing Extras works like removing meals: cancel the delivery.
		if (!this.isNonSubscriber && !this.sidesQuantities[weekId]) {
			this.sidesQuantities[weekId] = {};
		}

		this.renderSidesGrid();
	}

	renderSidesGrid() {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		const grid = document.querySelector("[data-sides-grid]");
		if (!weekId || !grid) return;

		const items = this.sidesCatalog[weekId] || [];

		if (items.length === 0) {
			grid.innerHTML = weekId.startsWith("sunday")
				? `<div class="sides-empty-note">No sides available for this delivery yet — check back soon.</div>`
				: `<div class="sides-empty-note">Sides are available for Sunday deliveries only.</div>`;
			this.updateSidesSummary();
			this.updateSidesSaveButton();
			return;
		}

		const readOnly = this.isNonSubscriber || (this.selectedWeek && this.selectedWeek.isDeadlinePassed);
		const quantities = this.sidesQuantities[weekId] || {};

		grid.innerHTML = items.map((item) => {
			const qty = quantities[item.item_id] || 0;
			const unitLabel = item.unit_label || (item.unit_type === "flat" ? "" : "per lb");
			const allergenText = item.allergens && item.allergens.trim() ? item.allergens : "None";
			const hasDescription = item.description && item.description.trim();
			const hasMacros = item.macros && item.macros.trim();
			const categoryLabel = item.category ? item.category.charAt(0).toUpperCase() + item.category.slice(1) : "";

			let portionText = "";
			let macrosText = item.macros || "";
			if (hasMacros) {
				// Portion size ("2 slices cooked", "3/4 cup cooked") and the
				// P/F/C/Cal macros are typed into the same free-text field
				// (no separate admin field for it), so split on the first
				// "P:" marker to put them on their own lines.
				const macroMatch = item.macros.match(/P\s*:/i);
				if (macroMatch && macroMatch.index > 0) {
					portionText = item.macros.slice(0, macroMatch.index).replace(/[\s•\-,]+$/, "").trim();
					macrosText = item.macros.slice(macroMatch.index).trim();
				}
			}
			macrosText = this.formatMacros(macrosText) || macrosText;

			return `<div class="side-card" data-side-item-id="${item.item_id}">
				${item.image ? `<div class="side-card__image-wrap"><img class="side-card__image" src="${item.image}" alt="${item.item_name}" onerror="this.closest('.side-card__image-wrap').innerHTML='<div class=&quot;side-card__image-broken&quot;>Photo unavailable</div>'"></div>` : ""}
				<div class="side-card__body">
					<div class="side-card__top">
						<h4 class="side-card__name">${item.item_name}</h4>
						${(categoryLabel || unitLabel) ? `<div class="side-card__meta">
							${categoryLabel ? `<span class="side-card__type">${categoryLabel}</span>` : ""}
							${(categoryLabel && unitLabel) ? `<span class="side-card__meta-divider">·</span>` : ""}
							${unitLabel ? `<span class="side-card__unit">${unitLabel}</span>` : ""}
						</div>` : ""}
						<div class="side-card__price">$${Number(item.price).toFixed(2)}</div>
						${hasDescription ? `<div class="side-card__description">${item.description}</div>` : ""}
						${hasMacros ? `<div class="side-card__macros">${portionText ? `<span class="side-card__portion" style="font-weight:600;color:#3D081B;">${portionText.charAt(0).toUpperCase() + portionText.slice(1)}:</span> ` : ""}${macrosText}</div>` : ""}
						${allergenText !== "None" ? `<div class="side-card__allergens"><span class="side-card__allergens-label">Allergens:</span> ${allergenText}</div>` : ""}
						${item.is_protein_ball ? this.proteinBallCardNotes(weekId) : ""}
					</div>
					${readOnly ? "" : `
					<div class="side-card__controls">
						<button type="button" class="side-card__qty-btn" data-side-action="decrease" data-side-id="${item.item_id}"${qty <= 0 ? " disabled" : ""}>−</button>
						<span class="side-card__qty-display" data-side-qty="${item.item_id}">${qty}</span>
						<button type="button" class="side-card__qty-btn" data-side-action="increase" data-side-id="${item.item_id}">+</button>
					</div>`}
				</div>
			</div>`;
		}).join("");

		if (!readOnly) {
			grid.querySelectorAll("[data-side-action]").forEach((btn) => {
				btn.addEventListener("click", () => {
					const itemId = btn.dataset.sideId;
					this.flashSideQtyButton(btn);
					if (btn.dataset.sideAction === "increase") {
						this.increaseSideQuantity(itemId);
					} else {
						this.decreaseSideQuantity(itemId);
					}
				});
			});
		}

		this.updateSidesSummary();
		this.updateSidesSaveButton();
		this.updateOrderSummary();
	}

	// Notes on protein ball cards in Extras (October 2026 — protein balls moved
	// from the meal-card radio buttons to Extras, 8-count only).
	proteinBallCardNotes(weekId) {
		const notes = [];
		if (this.pendingProteinBallRedemption && weekId && weekId.startsWith("sunday")) {
			notes.push(`<div class="side-card__pb-note side-card__pb-note--reward" style="margin-top:0.5rem;padding:0.45rem 0.6rem;border-radius:6px;background:#fff6e0;color:#7a4b00;font-size:0.82rem;line-height:1.35;">🎁 You have a free order of protein balls for your next Sunday delivery. Add one here to choose the flavor (it won't be charged), or we'll include one for you.</div>`);
		}
		if (this.subscriptionProteinBallQty > 0) {
			notes.push(`<div class="side-card__pb-note" style="margin-top:0.5rem;padding:0.45rem 0.6rem;border-radius:6px;background:#f6eef1;color:#5a1a30;font-size:0.82rem;line-height:1.35;">Your protein ball subscription arrives automatically. Anything added here is extra.</div>`);
		}
		return notes.join("");
	}

	// Required Yes/No prompt (no close button, no click-outside). Resolves true
	// for Yes, false for No.
	askYesNo({ title, message, yesText = "Yes", noText = "No" }) {
		return new Promise((resolve) => {
			const overlay = document.createElement("div");
			overlay.setAttribute("role", "dialog");
			overlay.setAttribute("aria-modal", "true");
			overlay.style.cssText = "position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,0.55);display:flex;align-items:center;justify-content:center;padding:16px;";
			overlay.innerHTML = `
				<div style="background:#fff;border-radius:12px;max-width:440px;width:100%;padding:1.5rem;box-shadow:0 10px 40px rgba(0,0,0,0.25);font-family:inherit;">
					<h3 style="margin:0 0 0.75rem;font-size:1.15rem;color:#3D081B;">${title}</h3>
					<p style="margin:0 0 1.25rem;font-size:0.95rem;line-height:1.5;color:#333;">${message}</p>
					<div style="display:flex;gap:0.75rem;flex-wrap:wrap;">
						<button type="button" data-yesno="yes" style="flex:1;min-width:140px;padding:0.75rem 1rem;border:none;border-radius:8px;background:#910F3F;color:#fff;font-weight:600;font-size:0.95rem;cursor:pointer;">${yesText}</button>
						<button type="button" data-yesno="no" style="flex:1;min-width:140px;padding:0.75rem 1rem;border:2px solid #910F3F;border-radius:8px;background:#fff;color:#910F3F;font-weight:600;font-size:0.95rem;cursor:pointer;">${noText}</button>
					</div>
				</div>`;
			const finish = (answer) => {
				overlay.remove();
				resolve(answer);
			};
			overlay.querySelector('[data-yesno="yes"]').addEventListener("click", () => finish(true));
			overlay.querySelector('[data-yesno="no"]').addEventListener("click", () => finish(false));
			document.body.appendChild(overlay);
			overlay.querySelector('[data-yesno="yes"]').focus();
		});
	}

	// Subscribers with a (grandfathered) protein ball subscription who picked
	// protein balls in Extras must confirm those are extra and billed
	// separately. "No" removes the protein ball Extras; everything else
	// still saves.
	async confirmProteinBallExtras(weekId) {
		if (!(this.subscriptionProteinBallQty > 0)) return;
		const quantities = this.sidesQuantities[weekId] || {};
		const pbItems = (this.sidesCatalog[weekId] || []).filter(i => i.is_protein_ball && (quantities[i.item_id] || 0) > 0);
		if (pbItems.length === 0) return;

		const orders = pbItems.reduce((sum, i) => sum + quantities[i.item_id], 0);
		const addYes = await this.askYesNo({
			title: "Add extra protein balls?",
			message: `You already have a protein ball subscription (${this.subscriptionProteinBallQty} balls a week), which arrives automatically. The ${orders} order${orders === 1 ? "" : "s"} (${orders * 8} balls) you selected in Extras will be <strong>added on top of your subscription</strong> and charged separately. Add the extra protein balls?`,
			yesText: "Yes, add them",
			noText: "No, remove them"
		});
		if (addYes) return;

		pbItems.forEach(i => { quantities[i.item_id] = 0; });
		this.sidesQuantities[weekId] = quantities;
		if (this.selectedWeek && this.selectedWeek.id === weekId) this.renderSidesGrid();
	}

	// Quick cranberry "blink" on the +/- button itself when clicked, so
	// there's visible feedback right at the tap point (September 2026).
	// Re-triggerable on rapid clicks by removing/re-adding the class on
	// the next animation frame rather than relying on animationend, which
	// wouldn't fire again if the class was already present.
	flashSideQtyButton(btn) {
		if (!btn) return;
		btn.classList.remove("side-card__qty-btn--flash");
		void btn.offsetWidth; // force reflow so the animation restarts
		btn.classList.add("side-card__qty-btn--flash");
	}

	increaseSideQuantity(itemId) {
		const weekId = this.selectedWeek.id;
		if (!this.sidesQuantities[weekId]) this.sidesQuantities[weekId] = {};
		this.sidesQuantities[weekId][itemId] = (this.sidesQuantities[weekId][itemId] || 0) + 1;
		this.updateSideCardDisplay(itemId);
	}

	decreaseSideQuantity(itemId) {
		const weekId = this.selectedWeek.id;
		if (!this.sidesQuantities[weekId]) return;
		const current = this.sidesQuantities[weekId][itemId] || 0;
		if (current <= 0) return;
		this.sidesQuantities[weekId][itemId] = current - 1;
		this.updateSideCardDisplay(itemId);
	}

	updateSideCardDisplay(itemId) {
		const weekId = this.selectedWeek.id;
		const qty = (this.sidesQuantities[weekId] || {})[itemId] || 0;
		const display = document.querySelector(`[data-side-qty="${itemId}"]`);
		if (display) display.textContent = qty;
		const card = document.querySelector(`[data-side-item-id="${itemId}"]`);
		if (card) {
			const decreaseBtn = card.querySelector('[data-side-action="decrease"]');
			if (decreaseBtn) decreaseBtn.disabled = qty <= 0;
		}
		this.updateSidesSummary();
		// This was missing entirely — nothing else in the sides quantity
		// change chain ever called this. Under the old tab model, switching
		// TO the Sides tab happened to trigger a refresh once; now that
		// Sides is always visible (no more tab switch to lean on), a side's
		// quantity changing needs to trigger this directly, exactly like
		// every meal option/quantity change already does.
		this.updateOrderSummary();
		this.updateSidesSaveButton();
		this.updateOrderSummary();
	}

	// Intentionally a no-op: this box duplicated the "Total Sides" line already
	// shown in the order summary below, so it's kept hidden. Left in place
	// (rather than removed) since updateSidesSummary() is still called from a
	// few spots and this avoids touching those call sites.
	updateSidesSummary() {
		const summaryEl = document.querySelector("[data-sides-summary]");
		if (!summaryEl) return;
		summaryEl.style.display = "none";
	}

	updateSidesSaveButton() {
		const saveSidesBtn = document.querySelector("[data-save-sides]");
		if (!saveSidesBtn) return;

		const weekId = this.selectedWeek && this.selectedWeek.id;
		const items = (weekId && this.sidesCatalog[weekId]) || [];
		const readOnly = this.isNonSubscriber || (this.selectedWeek && this.selectedWeek.isDeadlinePassed);

		saveSidesBtn.style.display = (!readOnly && items.length > 0) ? "block" : "none";
	}

	// Forces the customer's saved sides/extras selection for this delivery
	// date to actually clear on the backend, regardless of what's currently
	// in this.sidesQuantities. Needed because saveSidesQuietly() no-ops on
	// an empty selection (it's built to skip the API call when there's
	// simply nothing new to save) — that's fine for the normal save flow,
	// but it means it can never be used to WIPE a previously saved
	// selection. "Delete all selections" needs an explicit empty-array
	// save to actually tell the backend to soft-delete the existing rows
	// (September 2026 — found when a customer deleted their order but the
	// sides they'd saved earlier kept reappearing on refresh).
	async clearSidesSelection(weekId) {
		if (!weekId || !this.customer || !this.customer.id || !weekId.startsWith("sunday")) {
			return { success: true };
		}

		const deliveryDate = this.getDeliveryDateFromWeekId(weekId);

		try {
			const result = await this.railwayAPI.saveAlaCarteSelection({
				customerId: this.customer.id,
				customerEmail: this.customer.email,
				customerName: this.customer.name,
				weekId,
				deliveryDate,
				selections: []
			});

			if (result.success) {
				this.sidesQuantities[weekId] = {};
				this.sidesExistingSave[weekId] = false;
				return { success: true };
			}
			return { success: false, error: result.error || "unknown error" };
		} catch (error) {
			return { success: false, error: error.message };
		}
	}

	// Used by the combined save flow (saveWeeklyOrder) now that Sides saves
	// alongside meals in one click, instead of through its own separate
	// button. Deliberately has no alerts/scrolling/portal-refresh of its
	// own — the caller does all of that once, for both meals and sides
	// together, to avoid showing two separate success popups for one click.
	async saveSidesQuietly(weekId) {
		if (!weekId || !this.customer || !this.customer.id) {
			return { success: true, hadSelections: false };
		}

		await this.confirmProteinBallExtras(weekId);

		const quantities = this.sidesQuantities[weekId] || {};
		const newSelections = Object.entries(quantities)
			.filter(([, qty]) => qty > 0)
			.map(([itemId, qty]) => ({ itemId: parseInt(itemId), quantity: qty }));

		if (newSelections.length === 0) {
			return { success: true, hadSelections: false };
		}

		const deliveryDate = this.getDeliveryDateFromWeekId(weekId);

		// Additive, like meals: the backend replaces the whole Extras list for the
		// date, so read what's already saved and add the new counts on top.
		// Re-read every time (not cached) so another tab/device can't be overwritten.
		// If the read fails, don't save — saving only the new items would wipe the old ones.
		const existing = await this.railwayAPI.getAlaCarteSelection(this.customer.id, deliveryDate);
		if (!existing.success) {
			return { success: false, hadSelections: true, error: "couldn't check your saved extras — nothing was changed" };
		}
		const onMenu = new Set((this.sidesCatalog[weekId] || []).map(i => String(i.item_id)));
		const merged = {};
		(existing.selections || []).forEach((sel) => {
			// Items no longer on this week's menu can't be re-saved (the backend rejects them)
			if (onMenu.size === 0 || onMenu.has(String(sel.item_id))) {
				merged[sel.item_id] = (merged[sel.item_id] || 0) + sel.quantity;
			}
		});
		newSelections.forEach((sel) => {
			merged[sel.itemId] = (merged[sel.itemId] || 0) + sel.quantity;
		});
		const selections = Object.entries(merged)
			.filter(([, qty]) => qty > 0)
			.map(([itemId, qty]) => ({ itemId: parseInt(itemId), quantity: qty }));

		try {
			const result = await this.railwayAPI.saveAlaCarteSelection({
				customerId: this.customer.id,
				customerEmail: this.customer.email,
				customerName: this.customer.name,
				weekId,
				deliveryDate,
				selections
			});

			if (result.success) {
				this.sidesExistingSave[weekId] = true;
				return { success: true, hadSelections: true };
			}
			return { success: false, hadSelections: true, error: result.error || "unknown error" };
		} catch (error) {
			return { success: false, hadSelections: true, error: error.message };
		}
	}

	// Standalone "Save My Sides" button (if it's still on the page). Same additive
	// save as the combined flow, so it can never wipe Extras already saved.
	async saveSidesSelection() {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId || !this.customer || !this.customer.id) return;

		if (!this.mealPlan) {
			alert("You need an active meal plan to order sides.\n\nPlease sign up for a subscription at primalbalancenutrition.com/pages/subscribe");
			return;
		}

		const saveSidesBtn = document.querySelector("[data-save-sides]");
		const originalText = saveSidesBtn ? saveSidesBtn.textContent : "";

		try {
			if (saveSidesBtn) {
				saveSidesBtn.textContent = "💾 Saving...";
				saveSidesBtn.disabled = true;
			}

			const result = await this.saveSidesQuietly(weekId);

			if (!result.hadSelections) {
				alert("Select at least one extra to add.");
			} else if (result.success) {
				alert("Your extras have been added to your order!");
				this.sidesQuantities[weekId] = {};
				if (document.querySelector("[data-sides-grid]")) {
					this.renderSidesGrid();
				}
				if (this.customerPortal) {
					this.customerPortal.loadPortalData();
				}
				setTimeout(() => {
					window.scrollTo({ top: 0, behavior: "smooth" });
				}, 500);
			} else {
				alert("Could not save your extras: " + (result.error || "unknown error"));
			}
		} finally {
			if (saveSidesBtn) {
				saveSidesBtn.textContent = originalText || "💾 Save My Sides";
				saveSidesBtn.disabled = false;
			}
		}
	}

	updateWeekTitle() {
		const weekTitle = document.querySelector("[data-week-title]");
		if (weekTitle) {
			if (this.selectedWeek.isDeadlinePassed) {
				weekTitle.textContent = this.selectedWeek.display + " - Final Selection";
			} else {
				weekTitle.textContent = this.selectedWeek.display;
			}
		}
	}

	updateDeadlineNotice() {
		const deadlineNotice = document.querySelector("[data-deadline-notice]");
		if (deadlineNotice) {
			if (this.selectedWeek.isDeadlinePassed) {
				deadlineNotice.className = "deadline-notice deadline-notice--past";
				if (this.selectedWeek.hasSelection) {
					const weekData = this.weeklySelections[this.selectedWeek.id];
					const hasChefChoice = weekData && weekData.hasChefChoiceMeals;
					const allChefChoice = hasChefChoice && weekData.meals &&
						weekData.meals.every((m) => m.isChefChoice);

					if (allChefChoice) {
						deadlineNotice.innerHTML =
							"👨‍🍳 Selection deadline has passed. Since you didn't choose your own meals in time, our chef selected these for you below.";
					} else if (hasChefChoice) {
						deadlineNotice.innerHTML =
							"✅ Selection deadline has passed. Below is your final meal selection — meals marked 👨‍🍳 <strong>Chef's Choice</strong> were selected by our chef to complete your plan.";
					} else {
						deadlineNotice.innerHTML =
							"✅ Selection deadline has passed. Below is your final meal selection.";
					}
				} else {
					deadlineNotice.innerHTML =
						"📅 Selection deadline has passed.";
				}
			} else {
				deadlineNotice.className =
					"deadline-notice" +
					(this.selectedWeek.isUrgent ? " deadline-notice--urgent" : "");
				deadlineNotice.innerHTML = this.selectedWeek.deadlineText;
			}
		}
	}

	renderPastDelivery() {
		const mealsGrid = document.querySelector("[data-meals-grid]");
		if (!mealsGrid) return;

		const savedSelection = this.weeklySelections[this.selectedWeek.id];

		// Check if they have a saved selection with actual meals
		if (
			savedSelection &&
			savedSelection.meals &&
			savedSelection.meals.length > 0
		) {
			console.log(
				`📦 Rendering past selection for ${this.selectedWeek.id}:`,
				savedSelection
			);

			// Get the menu for this week
			const meals = this.weeklyMenus[this.selectedWeek.id];

			if (!meals || meals.length === 0) {
				console.error(`❌ No menu found for ${this.selectedWeek.id}`);
				console.log("Available menu keys:", Object.keys(this.weeklyMenus));
				mealsGrid.innerHTML =
					'<p style="text-align: center; padding: 2rem; color: #e74c3c;">Error: Menu data not found. Please contact support.</p>';
				return;
			}

			console.log(
				`📋 Found menu with ${meals.length} meals for ${this.selectedWeek.id}`
			);

			// Create maps of saved data to pass to render methods
			const savedQuantities = {};
			const savedOptions = {};
			const savedIsChefChoice = {};

			savedSelection.meals.forEach((meal) => {
				const id = meal.id.toLowerCase();
				savedQuantities[id] = meal.quantity;
				savedIsChefChoice[id] = !!meal.isChefChoice;
				const rawOptions = meal.optionDetails || meal.OptionDetails || {};
				const normalizedOptions = {};
				Object.entries(rawOptions).forEach(([optKey, optVal]) => {
					normalizedOptions[optKey] = {
						...optVal,
						isSelected: optVal.isSelected || optVal.IsSelected || false
					};
				});
				savedOptions[id] = normalizedOptions;
			});

			console.log("📊 Saved quantities:", savedQuantities);
			console.log("📊 Saved options:", savedOptions);

			// Render meal cards, passing the saved data directly
			const mealCards = meals
				.map((meal) => {
					const rendered = this.renderPastMealCardWithData(
						meal,
						savedQuantities,
						savedOptions,
						savedIsChefChoice
					);
					if (rendered) {
						console.log(`✅ Rendered card for meal ${meal.id} (${meal.name})`);
					} else {
						console.log(
							`⏭️ Skipped meal ${meal.id} (${meal.name}) - quantity: ${
								savedQuantities[meal.id] || 0
							}`
						);
					}
					return rendered;
				})
				.filter((html) => html !== "");

			console.log(
				`📦 Generated ${mealCards.length} meal cards (before protein balls)`
			);

			// Handle protein balls
			if (
				savedSelection.proteinBalls &&
				savedSelection.proteinBalls.selectedOption !== "none"
			) {
				const proteinBallMeal = meals.find((m) => this.isProteinBallMeal(m.id));
				if (proteinBallMeal) {
					const pbCard = this.renderPastProteinBallCard(
						proteinBallMeal,
						savedSelection.proteinBalls
					);
					if (pbCard) {
						mealCards.push(pbCard);
						console.log("✅ Added protein balls card");
					}
				}
			}

			if (mealCards.length === 0) {
				console.error(`❌ No meal cards rendered for ${this.selectedWeek.id}`);
				console.log("Saved meals data:", savedSelection.meals);
				mealsGrid.innerHTML = this.renderChefsChoiceMessage();
			} else {
				mealsGrid.innerHTML = mealCards.join("");
				console.log(`✅ Rendered ${mealCards.length} meal cards for past delivery`);
			}
		} else {
			// No selection was made - show Chef's Choice
			console.log(
				`👨‍🍳 No selection for ${this.selectedWeek.id}, showing Chef's Choice`
			);
			mealsGrid.innerHTML = this.renderChefsChoiceMessage();
		}
	}

	renderPastMealCardWithData(meal, savedQuantities, savedOptions, savedIsChefChoice) {
		const quantity = savedQuantities[meal.id] || savedQuantities[meal.id.toUpperCase()] || 0;

		// Skip protein balls here (handled separately)
		if (this.isProteinBallMeal(meal.id)) {
			return "";
		}

		// For regular meals - must have quantity > 0
		if (!quantity || quantity === 0) {
			return ""; // Don't render meals that weren't ordered
		}

		const isChefChoice = savedIsChefChoice && (
			savedIsChefChoice[meal.id] || savedIsChefChoice[meal.id.toUpperCase()]
		);

		// Render the full meal card with image and all details
		return `<div class="meal-card meal-card--past">
        <img src="${meal.image}" alt="${meal.name.replace(/<[^>]*>/g, '')}" class="meal-image" loading="lazy">
        <div class="meal-info">
            <h3 class="meal-name">${meal.name}</h3>
			${(meal.cuisineType || meal.menuNote) ? `
			<div class="meal-meta">
				${meal.cuisineType ? `<span class="meal-cuisine">${meal.cuisineType}</span>` : ''}
				${meal.cuisineType && meal.menuNote ? `<span class="meal-meta-divider">·</span>` : ''}
				${meal.menuNote ? `<span class="meal-note">${meal.menuNote}</span>` : ''}
			</div>` : ''}
            <div class="meal-description-wrapper">
                ${this.mealDescriptionHtml(meal)}
            </div>
            <div class="meal-bottom-content">
                ${meal.options ? this.renderPastMealOptionsWithData(meal, savedOptions[meal.id] || {}) : ""}
                <div class="quantity-display-past" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                    <span class="quantity-selected">Selected: ${quantity} portion${
			quantity > 1 ? "s" : ""
		}</span>
                    ${isChefChoice
			? `<span class="badge-chefs-choice" style="background:#fff3e0;color:#e65100;border:1px solid #ffcc80;border-radius:12px;padding:2px 10px;font-size:11px;font-weight:600;letter-spacing:0.02em;">👨‍🍳 Chef's Choice</span>`
			: `<span class="badge-your-pick" style="background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;border-radius:12px;padding:2px 10px;font-size:11px;font-weight:600;letter-spacing:0.02em;">✓ Your Pick</span>`}
                </div>
            </div>
        </div>
    </div>`;
	}

	renderPastMealOptionsWithData(meal, selectedOptions) {
		if (!meal.options) return "";

		const selectedOptionsList = [];

		Object.entries(meal.options).forEach(([key, option]) => {
			if (selectedOptions[key] && (selectedOptions[key].isSelected || selectedOptions[key].IsSelected)) {
				selectedOptionsList.push(option.label);
			}
		});

		if (selectedOptionsList.length === 0) {
			return "";
		}

		return `<div class="meal-options meal-options--past">
			<div class="options-title">Your Customizations:</div>
			<div class="options-list">
				${selectedOptionsList.map((option) => `<div class="option-selected">✓ ${option}</div>`).join("")}
			</div>
		</div>`;
	}

	renderPastProteinBallCard(meal, proteinBallData) {
		const optionDetails = meal.options[proteinBallData.selectedOption];

		if (!optionDetails) {
			return "";
		}

		return `<div class="meal-card meal-card--past meal-card--addon">
        <img src="${meal.image}" alt="${meal.name.replace(/<[^>]*>/g, '')}" class="meal-image" loading="lazy">
        <div class="meal-info">
            <h3 class="meal-name">${meal.name}</h3>
			${(meal.cuisineType || meal.menuNote) ? `
			<div class="meal-meta">
				${meal.cuisineType ? `<span class="meal-cuisine">${meal.cuisineType}</span>` : ''}
				${meal.cuisineType && meal.menuNote ? `<span class="meal-meta-divider">·</span>` : ''}
				${meal.menuNote ? `<span class="meal-note">${meal.menuNote}</span>` : ''}
			</div>` : ''}
            <div class="meal-description-wrapper">
                ${this.mealDescriptionHtml(meal)}
            </div>
            <div class="meal-bottom-content">
                <div class="meal-options meal-options--past">
                    <div class="options-title">Your Selection:</div>
                    <div class="option-selected">✓ ${optionDetails.label}</div>
                </div>
            </div>
        </div>
    </div>`;
	}
	renderPastMealCard(meal) {
		const quantity = this.getMealQuantity(meal.id);
		const isProteinBalls = this.isProteinBallMeal(meal.id);

		// Handle protein balls
		if (isProteinBalls) {
			const selectedOption = this.getProteinBallSelection(meal.id);
			if (!selectedOption || selectedOption === "none") {
				return ""; // Don't render if not selected
			}

			const optionDetails = meal.options[selectedOption];
			if (!optionDetails) {
				return "";
			}

			return `<div class="meal-card meal-card--past meal-card--addon">
				<img src="${meal.image}" alt="${meal.name.replace(/<[^>]*>/g, '')}" class="meal-image" loading="lazy">
				<div class="meal-info">
					<h3 class="meal-name">${meal.name}</h3>
					${(meal.cuisineType || meal.menuNote) ? `
					<div class="meal-meta">
						${meal.cuisineType ? `<span class="meal-cuisine">${meal.cuisineType}</span>` : ''}
						${meal.cuisineType && meal.menuNote ? `<span class="meal-meta-divider">·</span>` : ''}
						${meal.menuNote ? `<span class="meal-note">${meal.menuNote}</span>` : ''}
					</div>` : ''}
					<div class="meal-description-wrapper">
						${this.mealDescriptionHtml(meal)}
					</div>
					<div class="meal-bottom-content">
						<div class="meal-options meal-options--past">
							<div class="options-title">Your Selection:</div>
							<div class="option-selected">✓ ${optionDetails.label}</div>
						</div>
					</div>
				</div>
			</div>`;
		}

		// For regular meals - must have quantity > 0
		if (!quantity || quantity === 0) {
			return ""; // Don't render meals that weren't ordered
		}

		// Render the full meal card with image and all details
		return `<div class="meal-card meal-card--past">
			<img src="${meal.image}" alt="${meal.name.replace(/<[^>]*>/g, '')}" class="meal-image" loading="lazy">
			<div class="meal-info">
				<h3 class="meal-name">${meal.name}</h3>
				${(meal.cuisineType || meal.menuNote) ? `
				<div class="meal-meta">
					${meal.cuisineType ? `<span class="meal-cuisine">${meal.cuisineType}</span>` : ''}
					${meal.cuisineType && meal.menuNote ? `<span class="meal-meta-divider">·</span>` : ''}
					${meal.menuNote ? `<span class="meal-note">${meal.menuNote}</span>` : ''}
				</div>` : ''}
				<div class="meal-description-wrapper">
					${this.mealDescriptionHtml(meal)}
				</div>
				<div class="meal-bottom-content">
					${meal.options ? this.renderPastMealOptions(meal) : ""}
					<div class="quantity-display-past">
						<span class="quantity-selected">Selected: ${quantity} portion${quantity > 1 ? "s" : ""}</span>
					</div>
				</div>
			</div>
		</div>`;
	}

	renderPastMealOptions(meal) {
		if (!meal.options) return "";

		const selectedOptions = [];

		Object.entries(meal.options).forEach(([key, option]) => {
			if (this.getMealOption(meal.id, key)) {
				selectedOptions.push(option.label);
			}
		});

		if (selectedOptions.length === 0) {
			return "";
		}

		return `<div class="meal-options meal-options--past">
			<div class="options-title">Your Customizations:</div>
			  <div class="options-list">
				${selectedOptions
					.map((option) => `<div class="option-selected">✓ ${option}</div>`)
					.join("")}
			  </div>
		</div>`;
	}

	renderChefsChoiceMessage() {
		const hasAnyHistoricalSelections =
			Object.keys(this.weeklySelections).length > 0;

		if (!hasAnyHistoricalSelections) {
			// New customer message
			return `<div class="chefs-choice-message">
			<div class="chefs-choice-card">
				<div class="chefs-choice-icon">👨‍🍳</div>
				<h3 class="chefs-choice-title">Selection Deadline Has Passed</h3>
				<p class="chefs-choice-description">
				The customization deadline for this ${this.selectedWeek.dayName} delivery has already passed. 
				However, you can still customize upcoming deliveries that haven't reached their deadlines yet!
				</p>
				<div class="chefs-choice-note">
				<strong>Next Steps:</strong> Check the other delivery dates to find weeks you can still customize. 
				Future deliveries can be customized until Wednesday at 4:00 PM.
				</div>
			</div>
			</div>`;
		}
		return `<div class="chefs-choice-message">
		  <div class="chefs-choice-card">
			<div class="chefs-choice-icon">👨‍🍳</div>
			<h3 class="chefs-choice-title">No meals selected</h3>
			<p class="chefs-choice-description">
			  The customization deadline for this ${this.selectedWeek.dayName} delivery has already passed.
			  Unless requested to skip you'll receive a surprise selection of our most popular dishes!
			</p>
			<div class="chefs-choice-note">
			  <strong>Note: </strong>Meal plans of less than 10 will not receive a Chef's choice delivery on Thursdays. 
			  <br/>Don't forget to customize your future deliveries before the deadline to choose your preferred meals.
			</div>
		  </div>
		</div>`;
	}

	renderMeals() {
		const mealsGrid = document.querySelector("[data-meals-grid]");
		if (!mealsGrid) return;

		// Use the selected week ID to get the right menu
		const meals = this.weeklyMenus[this.selectedWeek.id] || [];

		if (meals.length === 0) {
			console.warn(`No meals found for ${this.selectedWeek.id}`);
			// Fallback: try to find meals for this date in the old format
			const fallbackMeals = this.findFallbackMeals(this.selectedWeek.id);
			if (fallbackMeals.length > 0) {
				mealsGrid.innerHTML = fallbackMeals
					.map((meal) => this.renderMealCard(meal, this.selectedWeek.id))
					.join("");
			} else {
				mealsGrid.innerHTML = "<p>No meals available for this delivery date.</p>";
			}
		} else if (this.isNonSubscriber) {
			// Guests see Sunday, then Wednesday, then Kids LAST as one
			// combined section (via appendWednesdayMenu + appendKidsMenu) —
			// so kids meals get excluded here entirely rather than grouped
			// inline, to avoid them appearing twice or in the wrong order.
			const regularMeals = meals.filter((m) => !this.isKidsMeal(m.id));
			mealsGrid.innerHTML = regularMeals
				.map((meal) => this.renderMealCard(meal, this.selectedWeek.id))
				.join("");
		} else {
			// Kids meals (KDS-prefixed) render exactly like any other meal —
			// same renderMealCard, same events, same counting/validation —
			// just grouped after the regular menu with a divider between
			// them, matching how the logged-out guest view already
			// separates Sunday from Wednesday.
			const regularMeals = meals.filter((m) => !this.isKidsMeal(m.id));
			const kidsMeals = meals.filter((m) => this.isKidsMeal(m.id));

			let html = regularMeals
				.map((meal) => this.renderMealCard(meal, this.selectedWeek.id))
				.join("");

			if (kidsMeals.length > 0) {
				html += `
					<div class="kids-menu-divider" style="grid-column: 1 / -1; text-align: center; margin: 3rem 0 2rem 0; padding-top: 2rem; border-top: 2px solid #e1e3e5;">
						<h1 style="margin: 0 0 0.5rem 0; color: #3D081B;">Simple Favorites</h1>
						<p style="margin: 0;"><i>Kid-friendly classics everyone can enjoy</i></p>
					</div>
				`;
				html += kidsMeals
					.map((meal) => this.renderMealCard(meal, this.selectedWeek.id))
					.join("");
			}

			mealsGrid.innerHTML = html;
		}

		this.bindMealEvents();
		setTimeout(() => {
			this.enforceCleanUIState();
		}, 50);
	}
	findFallbackMeals(weekId) {
		// Try to find meals in old format as fallback
		const dayType = weekId.split("-")[0];
		const oldKeys = Object.keys(this.weeklyMenus).filter((key) =>
			key.includes(dayType)
		);

		if (oldKeys.length > 0) {
			return this.weeklyMenus[oldKeys[0]] || [];
		}
		return [];
	}

	enforceCleanUIState() {
		// Reset all quantity displays to 0
		document.querySelectorAll("[data-quantity]").forEach((quantityDisplay) => {
			quantityDisplay.textContent = "0";
		});

		// Uncheck all meal option checkboxes
		document.querySelectorAll("[data-meal-option]").forEach((checkbox) => {
			checkbox.checked = false;
		});

		// Set protein balls to "none" option
		document.querySelectorAll("[data-protein-ball]").forEach((radio) => {
			if (radio.value === "none") {
				radio.checked = true;
			} else {
				radio.checked = false;
			}
		});

		// Disable all decrease buttons (since quantities are 0)
		document.querySelectorAll('[data-action="decrease"]').forEach((btn) => {
			btn.disabled = true;
		});

		console.log("🧹 Enforced clean UI state - all controls reset");
	}
	renderMealCard(meal, weekId) {
		if (this.isNonSubscriber) {
			// Simple version for non-subscribers (doesn't need weekId)
			const mealCard = document.createElement("div");
			mealCard.className = "meal-card meal-card--view-only";
			mealCard.dataset.mealId = meal.id;

			mealCard.innerHTML = `
			<img src="${meal.image}" alt="${meal.name.replace(/<[^>]*>/g, '')}" class="meal-image" loading="lazy"/>
			<div class="meal-info">
				<h3 class="meal-name">${meal.name}</h3>
				${(meal.cuisineType || meal.menuNote) ? `
				<div class="meal-meta">
					${meal.cuisineType ? `<span class="meal-cuisine">${meal.cuisineType}</span>` : ''}
					${meal.cuisineType && meal.menuNote ? `<span class="meal-meta-divider">·</span>` : ''}
					${meal.menuNote ? `<span class="meal-note">${meal.menuNote}</span>` : ''}
				</div>` : ''}
				
				${this.mealDescriptionHtml(meal)}
			</div>
			`;

			return mealCard.outerHTML; 
		} else {
			//const quantity = this.getMealQuantity(meal.id);
			const isProteinBalls = this.isProteinBallMeal(meal.id);
			const quantity = this.mealQuantities[weekId]?.[meal.id] || 0;
			const selectedOptions = this.mealOptions[weekId]?.[meal.id] || {};

			return `<div class="meal-card ${
				isProteinBalls ? "meal-card--addon" : ""
			}" data-meal-id="${meal.id}">
				<img src="${meal.image}" 
				alt="${meal.name.replace(/<[^>]*>/g, '')}"
				class="meal-image" 
				loading="lazy"
				width="400" 
				height="400"
				onload="this.style.opacity='1'"
				onerror="this.src='https://via.placeholder.com/400x400/f5f5f5/666?text=Image+Not+Available'"
				data-loading="true">
				<div class="meal-info">
					<h3 class="meal-name">${meal.name}</h3>
					${(meal.cuisineType || meal.menuNote) ? `
					<div class="meal-meta">
						${meal.cuisineType ? `<span class="meal-cuisine">${meal.cuisineType}</span>` : ''}
						${meal.cuisineType && meal.menuNote ? `<span class="meal-meta-divider">·</span>` : ''}
						${meal.menuNote ? `<span class="meal-note">${meal.menuNote}</span>` : ''}
					</div>` : ''}
					${this.mealDescriptionHtml(meal)}
					${
						isProteinBalls
							? this.renderProteinBallOptions(meal)
							: meal.options
							? this.renderMealOptions(meal)
							: this.renderIngredients(meal) + '<div class="meal-spacer"></div>'
					}
					${isProteinBalls ? "" : this.renderQuantityControls(meal.id, quantity)}
				</div>
			</div>`;
			return mealCard.outerHTML; // Return as string
		}
	}

	renderProteinBallOptions(meal) {
		if (!meal.options) return "";

		const weekId = this.selectedWeek && this.selectedWeek.id;
		const selectedOption = this.getProteinBallSelection(meal.id);

		return `<div class="addon-options-wrapper">
          <div class="addon-options">
            <div class="options-title">Choose Your Protein Ball Option:</div>
            <div class="options-grid">
				${Object.entries(meal.options)
					.map(
						([key, option]) => `<label class="option-radio">
                  <input type="radio" 
                        name="protein-balls-${weekId}-${meal.id}"
                        value="${key}"
                        data-protein-ball="${meal.id}" 
                        data-option-key="${key}"${
							selectedOption === key ? " checked" : ""
						}>
                  <span class="option-label">${option.label}</span>
                  <span class="option-price">$${option.price.toFixed(2)}</span>
                </label>`
					)
					.join("")}
				<label class="option-radio">
                <input type="radio" 
                      name="protein-balls-${weekId}-${meal.id}"
                      value="none"
                      data-protein-ball="${meal.id}" 
                      data-option-key="none"${!selectedOption || selectedOption === "none" ? " checked": ""}>
                <span class="option-label">No Protein Balls</span>
                <span class="option-price">$0.00</span>
              </label>
            </div>
          </div>
        </div>`;
	}

	getProteinBallSelection(mealId) {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId) return "none";

		return (
			(this.proteinBallSelections &&
				this.proteinBallSelections[weekId] &&
				this.proteinBallSelections[weekId][mealId]) ||
			"none"
		);
	}

	updateProteinBallSelection(mealId, optionKey) {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId) return;

		if (!this.proteinBallSelections) {
			this.proteinBallSelections = {};
		}

		if (!this.proteinBallSelections[weekId]) {
			this.proteinBallSelections[weekId] = {};
		}

		this.proteinBallSelections[weekId][mealId] = optionKey;
	}

	renderIngredients(meal) {
		if (!meal.ingredients) return "";
		const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
		return `<details class="meal-ingredients"><summary>Full ingredients</summary><p class="meal-ingredients-text">${esc(meal.ingredients)}</p><p class="meal-ingredients-note">Ingredients may change. All meals are prepared in a shared kitchen, so cross-contact with other allergens is possible. If you have a severe allergy, please contact us before ordering.</p></details>`;
	}

	renderMealOptions(meal) {
		const ingredientsHtml = this.renderIngredients(meal);

		if (!meal.options) return ingredientsHtml;

		return `${ingredientsHtml}<div class="meal-options-wrapper">
        <div class="meal-options">
          <div class="options-title">Customize Your Meal:</div>
          <div class="options-grid">
            ${Object.entries(meal.options).map(([key, option]) => `<label class="option-checkbox">
                <input type="checkbox" data-meal-option="${meal.id}" data-option-key="${key}"${this.getMealOption(meal.id, key) ? " checked" : ""}>
                <span class="option-label">${option.label}</span>
                ${option.price > 0? `<span class="option-price">+${option.price.toFixed(2)}</span>`: ""}
              </label>`).join("")}
          </div>
        </div>
      </div>`;
	}

	renderQuantityControls(mealId, quantity) {
		return `<div class="quantity-controls">
        <div class="quantity-selector">
          <button type="button" class="quantity-btn" data-action="decrease" data-meal="${mealId}"${
			quantity <= 0 ? " disabled" : ""
		}>−</button>
          <span class="quantity-display" data-quantity="${mealId}">${quantity}</span>
          <button type="button" class="quantity-btn" data-action="increase" data-meal="${mealId}">+</button>
        </div>
      </div>`;
	}

	bindMealEvents() {
		// Quantity controls (only for regular meals)
		document.querySelectorAll("[data-action]").forEach((btn) => {
			btn.addEventListener("click", () => {
				const mealId = btn.dataset.meal;
				const action = btn.dataset.action;

				if (action === "increase") {
					this.increaseMealQuantity(mealId);
				} else if (action === "decrease") {
					this.decreaseMealQuantity(mealId);
				}
			});
		});

		// Meal options (for regular meals)
		document.querySelectorAll("[data-meal-option]").forEach((checkbox) => {
			checkbox.addEventListener("change", () => {
				const mealId = checkbox.dataset.mealOption;
				const optionKey = checkbox.dataset.optionKey;
				this.updateMealOption(mealId, optionKey, checkbox.checked);
				this.updateOrderSummary();
			});
		});

		// Protein ball radio buttons
		document.querySelectorAll("[data-protein-ball]").forEach((radio) => {
			radio.addEventListener("change", () => {
				if (radio.checked) {
					const mealId = radio.dataset.proteinBall;
					const optionKey = radio.dataset.optionKey;
					this.updateProteinBallSelection(mealId, optionKey);
					this.updateOrderSummary();
				}
			});
		});
	}

	// QUANTITY MANAGEMENT
	increaseMealQuantity(mealId) {
		const weekId = this.selectedWeek.id;
		if (!this.mealQuantities[weekId]) {
			this.mealQuantities[weekId] = {};
		}

		this.mealQuantities[weekId][mealId] =
			(this.mealQuantities[weekId][mealId] || 0) + 1;
		this.updateMealDisplay(mealId);
		this.updateOrderSummary();
	}

	decreaseMealQuantity(mealId) {
		const weekId = this.selectedWeek.id;
		if (!this.mealQuantities[weekId]) return;

		const currentQty = this.mealQuantities[weekId][mealId] || 0;
		if (currentQty > 0) {
			this.mealQuantities[weekId][mealId] = currentQty - 1;
			this.updateMealDisplay(mealId);
			this.updateOrderSummary();
		}
	}

	updateMealDisplay(mealId) {
		const quantity = this.getMealQuantity(mealId);
		const quantityDisplay = document.querySelector(`[data-quantity="${mealId}"]`);
		const decreaseBtn = document.querySelector(
			`[data-action="decrease"][data-meal="${mealId}"]`
		);

		if (quantityDisplay) {
			quantityDisplay.textContent = quantity;
		}

		if (decreaseBtn) {
			decreaseBtn.disabled = quantity <= 0;
		}

		const optionsTitle = document.querySelector(
			`[data-meal-id="${mealId}"] .options-title`
		);
		if (optionsTitle) {
			optionsTitle.textContent = "Customize Your Meal:";
		}

		this.updateOrderSummary();
	}

	// DATA HELPER METHODS
	getMealQuantity(mealId) {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId) return 0;
		return (
			(this.mealQuantities[weekId] && this.mealQuantities[weekId][mealId]) || 0
		);
	}

	getMealOption(mealId, optionKey) {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId) return false;
		return (
			(this.mealOptions[weekId] &&
				this.mealOptions[weekId][mealId] &&
				this.mealOptions[weekId][mealId][optionKey]) ||
			false
		);
	}

	updateMealOption(mealId, optionKey, isSelected) {
		const weekId = this.selectedWeek && this.selectedWeek.id;
		if (!weekId) return;

		if (!this.mealOptions[weekId]) {
			this.mealOptions[weekId] = {};
		}

		if (!this.mealOptions[weekId][mealId]) {
			this.mealOptions[weekId][mealId] = {};
		}

		this.mealOptions[weekId][mealId][optionKey] = isSelected;
	}

	hasExistingSelection(weekId) {
		// Check if weeklySelections has this weekId
		if (!this.weeklySelections[weekId]) {
			console.log(
				`hasExistingSelection(${weekId}): false - no entry in weeklySelections`
			);
			return false;
		}

		const selection = this.weeklySelections[weekId];

		// Check if hasSelection flag is set
		if (selection.hasSelection === false) {
			console.log(
				`hasExistingSelection(${weekId}): false - hasSelection flag is false`
			);
			return false;
		}

		// Check if there are actual meals
		const hasMeals = selection.meals && selection.meals.length > 0;
		const hasProteinBalls =
			selection.proteinBalls &&
			selection.proteinBalls.selectedOption &&
			selection.proteinBalls.selectedOption !== "none";

		const result = hasMeals || hasProteinBalls;

		console.log(
			`hasExistingSelection(${weekId}): ${result} (meals: ${hasMeals}, proteinBalls: ${hasProteinBalls})`
		);

		return result;
	}

	updateOrderSummary() {
		const summaryContent = document.querySelector("[data-order-summary]");
		const totalSection = document.querySelector("[data-order-total]");
		const totalCount = document.querySelector("[data-total-count]");
		const saveBtn = document.querySelector("[data-save-order]");

		if (!summaryContent || !totalSection || !saveBtn) return;

		const weekId = this.selectedWeek && this.selectedWeek.id;

		// Clear existing add button when switching days
		this.clearExistingAddButton(saveBtn);

		if (this.selectedWeek && this.selectedWeek.isDeadlinePassed) {
			this.hideSummaryForPastDeadline(summaryContent, totalSection, saveBtn);
			return;
		}

		if (!weekId) {
			this.showEmptySummary(summaryContent, totalSection, saveBtn);
			return;
		}

		const summaryData = this.calculateSummaryData(weekId);
		const sidesData = this.getSidesSummaryData(weekId);

		// Check if we have any selection (meals OR protein balls OR sides)
		const proteinBallMeals = (this.weeklyMenus[weekId] || [])
			.filter(m => this.isProteinBallMeal(m.id));
		const hasProteinBalls = proteinBallMeals.some(m => {
			const sel = this.proteinBallSelections[weekId]?.[m.id];
			return sel && sel !== "none";
		});
		const hasMeals = summaryData.totalMeals > 0;
		const hasSides = sidesData.totalCount > 0;
		const hasAnySelection = hasMeals || hasProteinBalls || hasSides;

		if (hasAnySelection) {
			this.showActiveSummary(
				summaryContent,
				totalSection,
				totalCount,
				summaryData,
				hasProteinBalls,
				sidesData
			);
		} else {
			this.showEmptySummary(summaryContent, totalSection, saveBtn);
		}
	}
	addNewWeek(newWeekConfig) {
		// Add the new week to available weeks
		this.weekConfig.availableWeeks.push(newWeekConfig);

		// Remove weeks older than 8 weeks to prevent unlimited growth
		const eightWeeksAgo = new Date();
		eightWeeksAgo.setDate(eightWeeksAgo.getDate() - 56);

		this.weekConfig.availableWeeks = this.weekConfig.availableWeeks.filter(
			(week) => {
				return new Date(week.mondayDate) > eightWeeksAgo;
			}
		);

		// Refresh the UI
		this.renderWeeks();

		// Update portal if it exists
		if (this.customerPortal) {
			this.customerPortal.loadPortalData();
		}

		console.log(`New week added: ${newWeekConfig.id}`);
		return true;
	}

	clearExistingAddButton(saveBtn) {
		const buttonContainer = saveBtn.parentElement;
		const existingAddBtn = buttonContainer.querySelector("[data-add-to-meals]");
		if (existingAddBtn) {
			existingAddBtn.remove();
		}
	}

	hideSummaryForPastDeadline(summaryContent, totalSection, saveBtn) {
		summaryContent.innerHTML = "";
		totalSection.style.display = "none";
		saveBtn.style.display = "none";
	}

	showEmptySummary(summaryContent, totalSection, saveBtn) {
		summaryContent.innerHTML =
			'<p class="body">Select meals to see your order summary</p>';
		totalSection.style.display = "none";
		this.renderSaveButtons(0, false, null, false); // Pass all 4 parameters
	}

	calculateSummaryData(weekId) {
		const selectedMeals = this.mealQuantities[weekId];
		const meals = this.weeklyMenus[weekId];

		// SAFETY CHECK: If no meals found, return empty data
		if (!meals || meals.length === 0) {
			console.warn(
				`No meals found for ${weekId}. Available menu keys:`,
				Object.keys(this.weeklyMenus)
			);
			return {
				totalMeals: 0,
				totalAddOnCost: 0,
				summaryHtml: "",
				planAnalysis: this.analyzeMealPlan(0, weekId)
			};
		}

		let totalMeals = 0;
		let totalAddOnCost = 0;
		let summaryHtml = "";

		// Regular meals
		for (const [mealId, quantity] of Object.entries(selectedMeals || {})) {
			if (quantity > 0 && !this.isProteinBallMeal(mealId)) {
				const meal = meals.find((m) => m.id.toLowerCase() === mealId.toLowerCase());
				if (meal) {
					totalMeals += quantity;

					const mealSummary = this.renderMealSummaryItem(meal, quantity, weekId);
					summaryHtml += mealSummary.html;
					totalAddOnCost += mealSummary.addOnCost;
				} else {
					console.warn(`Meal ${mealId} not found in menu for ${weekId}`);
				}
			}
		}

		// Add protein balls to summary if selected
		const proteinBallSummary = this.renderProteinBallSummary(weekId);
		if (proteinBallSummary.html) {
			summaryHtml += proteinBallSummary.html;
			totalAddOnCost += proteinBallSummary.addOnCost;
		}

		return {
			totalMeals,
			totalAddOnCost,
			summaryHtml,
			planAnalysis: this.analyzeMealPlan(totalMeals, weekId)
		};
	}
	renderProteinBallSummary(weekId) {
		const meals = this.weeklyMenus[weekId] || [];
		const proteinBallMeals = meals.filter(m => this.isProteinBallMeal(m.id));

		let html = "";
		let totalAddOnCost = 0;

		proteinBallMeals.forEach(meal => {
			const selectedOption = this.proteinBallSelections[weekId]?.[meal.id];
			if (!selectedOption || selectedOption === "none") return;
			if (!meal.options?.[selectedOption]) return;

			const option = meal.options[selectedOption];
			const cost = option.price || 0;
			totalAddOnCost += cost;

			html += `<div class="summary-item summary-item--addon">
				<div class="summary-item-main">
					<span class="meal-name-summary">${meal.name}</span>
					<span class="meal-quantity">Add-on</span>
					${cost > 0 ? `<span class="meal-addon-cost"> &nbsp;+$${cost.toFixed(2)}</span>` : ""}
				</div>
				<div class="summary-item-options">
					<span class="option-desc">${option.label}</span>
				</div>
			</div>`;
		});

		return { html, addOnCost: totalAddOnCost };
	}
	renderMealSummaryItem(meal, quantity, weekId) {
		let addOnCost = 0;
		const selectedOptions =
			(this.mealOptions[weekId] && this.mealOptions[weekId][meal.id]) || {};
		const addOnDescriptions = [];

		if (meal.options) {
			Object.entries(selectedOptions).forEach(([optionKey, isSelected]) => {
				if (isSelected && meal.options[optionKey]) {
					const optionPrice = meal.options[optionKey].price || 0;
					if (optionPrice > 0) {
						addOnCost += optionPrice * quantity;
						addOnDescriptions.push(
							meal.options[optionKey].label +
								" (+$" +
								optionPrice.toFixed(2) +
								" each)"
						);
					} else {
						addOnDescriptions.push(meal.options[optionKey].label);
					}
				}
			});
		}

		const html = `<div class="summary-item">
		<div class="summary-item-main">
			<span class="meal-name-summary">${meal.name}</span>
			<span class="meal-quantity">×${quantity}</span>
			${
				addOnCost > 0
					? ` <span class="meal-addon-cost"> &nbsp;(+${addOnCost.toFixed(2)})</span>`
					: ""
			}
		</div>
			${
				addOnDescriptions.length > 0
					? `<div class="summary-item-options">
			${addOnDescriptions
				.map((desc) => `<span class="option-desc">${desc}</span>`)
				.join("")}</div>`
					: ""
			}
		</div>`;

		return { html, addOnCost };
	}

	getSidesSummaryData(weekId) {
		const items = this.sidesCatalog[weekId] || [];
		const quantities = this.sidesQuantities[weekId] || {};
		let html = "";
		let totalCount = 0;
		let totalCost = 0;

		items.forEach((item) => {
			const qty = quantities[item.item_id] || 0;
			if (qty <= 0) return;
			const lineCost = qty * Number(item.price);
			totalCount += qty;
			totalCost += lineCost;

			// Portion size ("8 oz", "3 count", "1 lb") reinforces what one
			// unit actually is, alongside the (×qty) count — only shown
			// when the item has one set.
			const unitSuffix = item.unit_label && item.unit_label.trim()
				? ` - ${item.unit_label.trim()}`
				: "";

			// Matches the "name (×qty) ... $cost" layout used in the Upcoming
			// Deliveries billing breakdown, via the same .billing-row class.
			html += `<div class="billing-row">
				<span>${item.item_name}${unitSuffix} <span class="meal-quantity">(×${qty})</span></span>
				<span class="meal-addon-cost">$${lineCost.toFixed(2)}</span>
			</div>`;
		});

		return { html, totalCount, totalCost };
	}

	showActiveSummary(
		summaryContent,
		totalSection,
		totalCount,
		summaryData,
		hasProteinBalls = false,
		sidesData = null
	) {
		// Rebuilt (August 2026): this used to show EITHER meals OR sides
		// content depending on this.activeMenuTab — a leftover from the tab
		// model. Since Sides became an always-visible section instead of a
		// tab, activeMenuTab is never set anymore (switchMenuTab is
		// commented out and never called), which silently made the "sides"
		// branch here permanently unreachable — sides never showed in the
		// summary and the total-sides line was always force-hidden. Now
		// both are always combined together, matching how they're both
		// always visible on the page.
		const hasMealsContent = summaryData.summaryHtml && summaryData.summaryHtml.trim();
		const hasSidesContent = sidesData && sidesData.html;

		let content = (hasMealsContent ? summaryData.summaryHtml : "")
			+ (hasSidesContent ? sidesData.html : "");
		if (!content) {
			content = '<p class="body">Select meals or sides to see your order summary</p>';
		}
		summaryContent.innerHTML = content;

		totalSection.style.display = "block";

		const totalMealsEl = totalCount ? totalCount.closest(".total-meals") : null;
		const totalSidesEl = document.querySelector("[data-total-sides]");
		const totalSidesCountEl = document.querySelector("[data-total-sides-count]");
		const totalSidesCostEl = document.querySelector("[data-total-sides-cost]");

		if (totalMealsEl) totalMealsEl.style.display = "";
		totalCount.textContent = summaryData.totalMeals;

		if (totalSidesEl && totalSidesCountEl && totalSidesCostEl) {
			if (sidesData && sidesData.totalCount > 0) {
				totalSidesEl.style.display = "block";
				totalSidesCountEl.textContent = sidesData.totalCount;
				totalSidesCostEl.textContent = sidesData.totalCost.toFixed(2);
			} else {
				totalSidesEl.style.display = "none";
			}
		}

		const hasExisting = this.hasExistingSelection(this.selectedWeek.id);

		// Pass hasProteinBalls + hasSides info to save button rendering
		this.renderSaveButtons(
			summaryData.totalMeals,
			hasExisting,
			summaryData.planAnalysis,
			hasProteinBalls,
			sidesData && sidesData.totalCount > 0
		);
		this.displayMealBreakdown(summaryData.planAnalysis);
	}

	analyzeMealPlan(totalMeals, weekId) {
		// If no meal plan, return special analysis
		if (!this.mealPlan) {
			return {
				totalMeals: totalMeals,
				weeklyTotal: 0,
				planMeals: 0,
				isUnder: false,
				isOver: false,
				extraMeals: 0,
				missingMeals: 0,
				extraCost: 0,
				warnings: ["No active subscription"],
				blockSave: true,
				blockReason: "Subscription required to save meals",
				weekBreakdown: { sunday: 0, wednesday: 0, total: 0 },
				noSubscription: true
			};
		}
		const weekIdentifier = this.getDeliveryDateFromWeekId(weekId) || weekId;

		const weeklyTotal = this.calculateWeeklyTotal(weekIdentifier);
		console.log(
			"analyzeMealPlan - weekId:",
			weekId,
			"weekIdentifier:",
			weekIdentifier,
			"totals:",
			weeklyTotal
		);

		const analysis = {
			totalMeals: totalMeals,
			weeklyTotal: weeklyTotal.total,
			planMeals: this.mealPlan.mealsPerWeek,
			isUnder: false,
			isOver: false,
			extraMeals: 0,
			missingMeals: 0,
			extraCost: 0,
			warnings: [],
			blockSave: false,
			blockReason: "",
			weekBreakdown: weeklyTotal,
			hasSpecialTag: this.mealPlan.hasSpecialTag
		};

		// Special tag and VIP users get the old flexible behavior
		if (this.mealPlan.hasSpecialTag || this.mealPlan.isVIP) {
			if (weeklyTotal.total < this.mealPlan.mealsPerWeek) {
				analysis.isUnder = true;
				analysis.missingMeals = this.mealPlan.mealsPerWeek - weeklyTotal.total;
				analysis.warnings.push(
					`⭐ Special Member: Weekly total ${weeklyTotal.total} meals (${analysis.missingMeals} under your ${this.mealPlan.planName})`
				);
			} else if (weeklyTotal.total > this.mealPlan.mealsPerWeek) {
				analysis.isOver = true;
				analysis.extraMeals = weeklyTotal.total - this.mealPlan.mealsPerWeek;
				analysis.warnings.push(
					`⭐ Special Member: Weekly total ${weeklyTotal.total} meals (${analysis.extraMeals} extra meals)`
				);
			}
			return analysis;
		}

		// Regular customers get strict enforcement
		let projectedTotal = weeklyTotal.total;
		if (weekId.includes("sunday")) {
			projectedTotal = weeklyTotal.wednesday + totalMeals;
		} else if (weekId.includes("wednesday")) {
			projectedTotal = weeklyTotal.sunday + totalMeals;
		}

		// Minimum order warning
		if (totalMeals < this.planPricing.minimumOrderWarning && totalMeals > 0) {
			analysis.warnings.push(
				`ℹ️ Note: Minimum for delivery is ${this.planPricing.minimumOrderWarning} meals, but you can save partial selections to build your order with different customizations.`
			);
		}

		if (projectedTotal < this.mealPlan.mealsPerWeek) {
			analysis.isUnder = true;
			analysis.missingMeals = this.mealPlan.mealsPerWeek - projectedTotal;
			analysis.warnings.push(
				`Subscription requirement: ${this.mealPlan.mealsPerWeek} meals (${analysis.missingMeals} still needed)`
			);
		} else if (projectedTotal >= this.mealPlan.mealsPerWeek) {
			analysis.warnings.push(
				`Meeting subscription: ${projectedTotal} meals selected`
			);
		}
		return analysis;

		if (weeklyTotal.total < this.mealPlan.mealsPerWeek) {
			analysis.isUnder = true;
			analysis.missingMeals = this.mealPlan.mealsPerWeek - weeklyTotal.total;
			analysis.warnings.push(
				`Weekly total: ${weeklyTotal.total} meals (${analysis.missingMeals} under your ${this.mealPlan.planName})`
			);
		} else if (weeklyTotal.total > this.mealPlan.mealsPerWeek) {
			analysis.isOver = true;
			analysis.extraMeals = weeklyTotal.total - this.mealPlan.mealsPerWeek;
			// analysis.extraCost = analysis.extraMeals * this.planPricing.extraMealPrice;

			if (this.mealPlan.allowsOverage) {
				analysis.warnings.push(
					`Weekly total: ${weeklyTotal.total} meals (${analysis.extraMeals} extra meals above your subscription)`
				);
			} else {
				analysis.warnings.push(
					`Weekly total: ${weeklyTotal.total} meals exceeds your ${this.mealPlan.planName} by ${analysis.extraMeals}`
				);
				analysis.blockSave = true;
				analysis.blockReason = `Remove ${analysis.extraMeals} meals from this week`;
			}
		} else {
			analysis.warnings.push(
				`Weekly total: ${weeklyTotal.total} meals (perfect match for your ${this.mealPlan.planName}!)`
			);
		}
		return analysis;
	}

	calculateWeeklyTotal(weekIdentifier) {
		console.log("calculateWeeklyTotal called with:", weekIdentifier);

		let sundayId, wednesdayId;

		// Handle the new date-based identifier format
		if (
			typeof weekIdentifier === "string" &&
			weekIdentifier.match(/\d{4}-\d{2}-\d{2}/)
		) {
			console.log("Processing date-based identifier:", weekIdentifier);

			// Parse the date to determine if it's Sunday or Wednesday
			const parts = weekIdentifier.split("-");
			const year = parseInt(parts[0]);
			const month = parseInt(parts[1]) - 1; // Month is 0-indexed in Date
			const day = parseInt(parts[2]);

			const inputDate = new Date(year, month, day);
			const dayOfWeek = inputDate.getDay(); // 0 = Sunday, 3 = Wednesday

			console.log("Input date:", inputDate, "Day of week:", dayOfWeek);

			if (dayOfWeek === 0) {
				// Sunday
				sundayId = `sunday-${weekIdentifier}`;
				// Find Wednesday: 3 days later
				const wednesdayDate = new Date(inputDate);
				wednesdayDate.setDate(inputDate.getDate() + 3);
				const wednesdayYear = wednesdayDate.getFullYear();
				const wednesdayMonth = String(wednesdayDate.getMonth() + 1).padStart(2, "0");
				const wednesdayDay = String(wednesdayDate.getDate()).padStart(2, "0");
				wednesdayId = `wednesday-${wednesdayYear}-${wednesdayMonth}-${wednesdayDay}`;
			} else if (dayOfWeek === 3) {
				// Wednesday
				wednesdayId = `wednesday-${weekIdentifier}`;
				// Find Sunday: 3 days earlier
				const sundayDate = new Date(inputDate);
				sundayDate.setDate(inputDate.getDate() - 3);
				const sundayYear = sundayDate.getFullYear();
				const sundayMonth = String(sundayDate.getMonth() + 1).padStart(2, "0");
				const sundayDay = String(sundayDate.getDate()).padStart(2, "0");
				sundayId = `sunday-${sundayYear}-${sundayMonth}-${sundayDay}`;
			} else {
				// For other days, find the Sunday of that week
				const daysFromSunday = inputDate.getDay();
				const sundayDate = new Date(inputDate);
				sundayDate.setDate(inputDate.getDate() - daysFromSunday);

				const wednesdayDate = new Date(sundayDate);
				wednesdayDate.setDate(sundayDate.getDate() + 3);

				const sundayYear = sundayDate.getFullYear();
				const sundayMonth = String(sundayDate.getMonth() + 1).padStart(2, "0");
				const sundayDay = String(sundayDate.getDate()).padStart(2, "0");
				sundayId = `sunday-${sundayYear}-${sundayMonth}-${sundayDay}`;

				const wednesdayYear = wednesdayDate.getFullYear();
				const wednesdayMonth = String(wednesdayDate.getMonth() + 1).padStart(2, "0");
				const wednesdayDay = String(wednesdayDate.getDate()).padStart(2, "0");
				wednesdayId = `wednesday-${wednesdayYear}-${wednesdayMonth}-${wednesdayDay}`;
			}
		}

		console.log(`Calculated IDs - Sunday: ${sundayId}, Wednesday: ${wednesdayId}`);

		let sundayTotal = 0;
		let wednesdayTotal = 0;

		console.log(
			`Looking for saved data - Sunday: ${sundayId}, Wednesday: ${wednesdayId}`
		);
		console.log(
			"Available weeklySelections keys:",
			Object.keys(this.weeklySelections)
		);

		if (
			this.weeklySelections[sundayId] &&
			this.weeklySelections[sundayId].meals
		) {
			sundayTotal = this.weeklySelections[sundayId].meals.reduce(
				(sum, meal) => sum + meal.quantity,
				0
			);
			console.log(`Found Sunday data: ${sundayTotal} meals`);
		} else {
			console.log(`No Sunday data found for ${sundayId}`);
		}

		if (
			this.weeklySelections[wednesdayId] &&
			this.weeklySelections[wednesdayId].meals
		) {
			wednesdayTotal = this.weeklySelections[wednesdayId].meals.reduce(
				(sum, meal) => sum + meal.quantity,
				0
			);
			console.log(`Found Wednesday data: ${wednesdayTotal} meals`);
		} else {
			console.log(`No Wednesday data found for ${wednesdayId}`);
		}

		console.log(
			`Final totals - Sunday: ${sundayTotal}, Wednesday: ${wednesdayTotal}, Total: ${
				sundayTotal + wednesdayTotal
			}`
		);

		return {
			sunday: sundayTotal,
			wednesday: wednesdayTotal,
			total: sundayTotal + wednesdayTotal
		};
	}

	updateSavedMealBreakdown() {
		const breakdownEl = document.getElementById("saved-meal-breakdown");
		const sundayCountEl = document.getElementById("sunday-count");
		const wednesdayCountEl = document.getElementById("wednesday-count");
		const totalSavedEl = document.getElementById("total-saved-count");

		if (!breakdownEl || !sundayCountEl || !wednesdayCountEl || !totalSavedEl)
			return;

		// Count saved meals by day
		let sundayTotal = 0;
		let wednesdayTotal = 0;

		// Check all weeks for saved selections
		Object.keys(this.weeklySelections).forEach((weekId) => {
			const weekData = this.weeklySelections[weekId];
			if (weekData && weekData.meals) {
				const mealCount = weekData.meals.reduce(
					(sum, meal) => sum + meal.quantity,
					0
				);

				if (weekId.includes("sunday")) {
					sundayTotal += mealCount;
				} else if (weekId.includes("wednesday")) {
					wednesdayTotal += mealCount;
				}
			}
		});

		const totalSaved = sundayTotal + wednesdayTotal;

		// Update display
		sundayCountEl.textContent = sundayTotal;
		wednesdayCountEl.textContent = wednesdayTotal;
		totalSavedEl.textContent = totalSaved;

		// Show/hide breakdown based on whether there are saved meals
		if (totalSaved > 0) {
			breakdownEl.style.display = "block";
		} else {
			breakdownEl.style.display = "none";
		}
	}

	displayMealBreakdown(planAnalysis) {
		const totalSection = document.querySelector("[data-order-total]");
		if (!totalSection) return;

		// Remove existing breakdown if present
		const existingBreakdown = document.getElementById("meal-breakdown-display");
		if (existingBreakdown) {
			existingBreakdown.remove();
		}

		// Create breakdown display
		const breakdownHtml = `
			<div id="meal-breakdown-display" style="
				display: flex;
				justify-content: space-between;
				margin-top: 1rem;
				padding: 1rem;
				background: #f8f9fa;
				border-radius: 5px;
				font-size: 1.3rem;
			">
				<div style="text-align: left;">
					<strong>Sunday:</strong> ${planAnalysis.weekBreakdown.sunday} meals
				</div>
				<div style="text-align: right;">
					<strong>Wednesday:</strong> ${planAnalysis.weekBreakdown.wednesday} meals
				</div>
			</div>
		`;

		totalSection.insertAdjacentHTML('afterend', breakdownHtml);
	}

	renderPlanAnalysis(analysis, addOnCost) {
		let html = '<div class="plan-analysis">';

		// Check if no subscription
		if (analysis.noSubscription) {
			html += '<div class="plan-status">';
			html += '<h4 style="color: #856404;">No Active Subscription</h4>';
			html +=
				'<p style="color: #856404; font-size: 1.3rem;">Sign up for a meal plan or log in to start ordering</p>';
			html += "</div>";
			html += "</div>";
			return html;
		}

		html += '<div class="plan-status">';
		html += `<h4>Your ${this.mealPlan.planName}</h4>`;
		html += '<div class="weekly-breakdown">';
		html += `<div class="week-summary">`;
		html += `<span class="day-total">Sunday: ${analysis.weekBreakdown.sunday} meals</span>`;
		html += `<span class="day-total">Wednesday: ${analysis.weekBreakdown.wednesday} meals</span>`;
		html += `<span class="week-total"><strong>Week Total: ${analysis.weeklyTotal} meals</strong></span>`;
		html += `</div>`;
		html += "</div>";
		html += "</div>";

		html += "</div>";
		return html;
	}

	// SAVE BUTTON RENDERING
	renderSaveButtons(
		totalMeals,
		hasExisting,
		planAnalysis,
		hasProteinBalls = false,
		hasSides = false
	) {
		const saveBtn = document.querySelector("[data-save-order]");
		if (!saveBtn) {
			console.error("Save button not found!");
			return;
		}

		const buttonContainer = saveBtn.parentElement;

		// Remove any existing add button
		const existingAddBtn = buttonContainer.querySelector("[data-add-to-meals]");
		if (existingAddBtn) {
			existingAddBtn.remove();
		}

		// Check if user has no subscription
		if (!this.mealPlan) {
			this.renderNoSubscriptionButtons(saveBtn);
			return;
		}

		// Don't show any buttons for past deadline days
		if (this.selectedWeek && this.selectedWeek.isDeadlinePassed) {
			saveBtn.style.display = "none";
			return;
		}

		// Check if we have any selection (meals OR protein balls OR sides) —
		// sides added (August 2026) so someone who wants Chef's Choice for
		// their meals but still wants to add a side isn't blocked from
		// saving just that.
		const hasAnySelection = totalMeals > 0 || hasProteinBalls || hasSides;

		if (!hasAnySelection) {
			this.renderEmptySaveButton(saveBtn);
			return;
		}

		// Check if button should be enabled
		const belowMinimum =
			totalMeals < this.planPricing.minimumOrderWarning && totalMeals > 0;
		// If nothing meal-related is selected at all (no meals, no protein
		// balls) but sides are, there's no meal order to validate — this is
		// a sides-only save, so the meal-minimum check doesn't apply.
		const onlySidesSelected = totalMeals === 0 && !hasProteinBalls && hasSides;
		const canSave = onlySidesSelected || (!planAnalysis.blockSave && (!belowMinimum || hasExisting));

		// Update button state
		saveBtn.style.display = "block";
		saveBtn.disabled = !canSave;

		if (!canSave) {
			if (planAnalysis.blockSave) {
				saveBtn.textContent = "⚠️ " + planAnalysis.blockReason;
			} else if (belowMinimum && !hasExisting) {
				const needed = this.planPricing.minimumOrderWarning - totalMeals;
				saveBtn.textContent = `⚠️ Add ${needed} more meal${
					needed === 1 ? "" : "s"
				} (minimum 4 required)`;
			}
		} else {
			if (totalMeals === 0 && hasProteinBalls) {
				saveBtn.textContent = "💾 Save Protein Ball Selection";
			} else {
				saveBtn.textContent = "💾 Save My Weekly Selection";
			}
		}

		// Show "Add to Meals" button for existing selections — hides the
		// main save button entirely rather than showing both side by side
		// (August 2026: with a real Cancel action now available from
		// Upcoming Orders, the old two-button safety net stopped earning
		// its confusion — a customer can just cancel and redo instead).
		if (
			hasExisting &&
			!planAnalysis.blockSave &&
			hasAnySelection &&
			!this.selectedWeek.isDeadlinePassed
		) {
			saveBtn.style.display = "none";
			this.renderAddToMealsButton(saveBtn, totalMeals, hasProteinBalls, hasSides);
		}
	}

	renderPlanAnalysisAfterButton(planAnalysis) {
		const saveBtn = document.querySelector("[data-save-order]");
		const buttonContainer = saveBtn.parentElement;

		const existingAnalysis = buttonContainer.querySelector(
			".plan-analysis-after-button"
		);
		if (existingAnalysis) {
			existingAnalysis.remove();
		}

		if (!planAnalysis || planAnalysis.noSubscription) {
			return;
		}

		const selectedWeek = this.selectedWeek;
		if (!selectedWeek) return;

		// Get the week identifier from the selected week
		const weekId = selectedWeek.id; // e.g., "monday-2025-09-15"
		const parts = weekId.split("-");
		if (parts.length !== 4) return;

		const baseDate = `${parts[1]}-${parts[2]}-${parts[3]}`;

		// Build the Sunday and Wednesday IDs for this week
		let sundayId, wednesdayId;

		if (parts[0] === "sunday") {
			sundayId = weekId; // Already the Sunday ID
			// Calculate Wednesday (3 days later)
			const date = new Date(baseDate);
			date.setDate(date.getDate() + 3);
			const wednesdayDate = date.toISOString().split("T")[0];
			wednesdayId = `wednesday-${wednesdayDate}`;
		} else if (parts[0] === "wednesday") {
			wednesdayId = weekId; // Already the Wednesday ID
			// Calculate Sunday (3 days earlier)
			const date = new Date(baseDate);
			date.setDate(date.getDate() - 3);
			const sundayDate = date.toISOString().split("T")[0];
			sundayId = `sunday-${sundayDate}`;
		}

		// Count actual saved meals
		let sundayTotal = 0;
		let wednesdayTotal = 0;

		if (
			this.weeklySelections[sundayId] &&
			this.weeklySelections[sundayId].meals
		) {
			sundayTotal = this.weeklySelections[sundayId].meals.reduce(
				(sum, meal) => sum + meal.quantity,
				0
			);
		}

		if (
			this.weeklySelections[wednesdayId] &&
			this.weeklySelections[wednesdayId].meals
		) {
			wednesdayTotal = this.weeklySelections[wednesdayId].meals.reduce(
				(sum, meal) => sum + meal.quantity,
				0
			);
		}

		console.log(
			`Calculated totals - Sunday (${sundayId}): ${sundayTotal}, Wednesday (${wednesdayId}): ${wednesdayTotal}`
		);

		// Create the analysis HTML
		let html = '<div class="plan-analysis-after-button saved-meal-breakdown">';
		html += "<h4>Saved Meal Summary</h4>";
		html += '<div class="week-summary">';
		html += `<span class="day-total">Sunday: ${sundayTotal} meals</span>`;
		html += `<span class="day-total">Wednesday: ${wednesdayTotal} meals</span>`;
		html += `<span class="week-total"><strong>Total Saved: ${
			sundayTotal + wednesdayTotal
		} meals</strong></span>`;
		html += "</div>";
		html += "</div>";

		// Insert after save button
		const analysisDiv = document.createElement("div");
		analysisDiv.innerHTML = html;
		buttonContainer.appendChild(analysisDiv.firstElementChild);
	}

	renderNoSubscriptionButtons(saveBtn) {
		// Disable and gray out save button
		saveBtn.style.display = "block";
		saveBtn.disabled = true;
		saveBtn.textContent = "💾 Save My Weekly Selection";
		saveBtn.style.opacity = "0.5";

		// Create container for message and signup button
		const container = document.createElement("div");
		container.className = "no-subscription-container";
		container.style.cssText = `
			text-align: center;
			margin-top: 1.5rem;
			padding: 1.5rem;
			background: #fff3cd;
			border: 2px solid #ffeaa7;
			border-radius: 8px;`;

		// Add message
		const message = document.createElement("p");
		message.style.cssText = `
			color: #856404;
			font-size: 1.4rem;
			margin-bottom: 1.5rem;
			font-weight: 500;`;
		message.textContent = "You need an active meal plan to order";

		// Add signup button
		const signupBtn = document.createElement("a");
		signupBtn.href = "https://www.primalbalancenutrition.com/pages/subscribe";
		signupBtn.className = "button button--primary";

		signupBtn.textContent = "View Meal Plans";

		// Add hover effect
		signupBtn.onmouseover = function () {
			this.style.transform = "translateY(-2px)";
			this.style.boxShadow = "0 0.5rem 1.5rem rgba(61, 8, 27, 0.3)";
		};
		signupBtn.onmouseout = function () {
			this.style.transform = "translateY(0)";
			this.style.boxShadow = "none";
		};

		container.appendChild(message);
		container.appendChild(signupBtn);

		// Insert after save button
		saveBtn.parentElement.appendChild(container);
	}

	renderEmptySaveButton(saveBtn) {
		saveBtn.style.display = "block";
		saveBtn.disabled = true;
		saveBtn.textContent = "💾 Save My Weekly Selection";
	}

	renderActiveSaveButton(
		saveBtn,
		totalMeals,
		planAnalysis,
		hasProteinBalls = false
	) {
		saveBtn.style.display = "block";

		// Check minimum order requirement for initial save
		const hasExistingSave = this.hasExistingSelection(this.selectedWeek.id);
		const belowMinimum =
			totalMeals < this.planPricing.minimumOrderWarning && totalMeals > 0;

		const canSave = !planAnalysis.blockSave && (!belowMinimum || hasExistingSave);
		saveBtn.disabled = !canSave;

		if (!canSave) {
			if (planAnalysis.blockSave) {
				saveBtn.textContent = "⚠️ " + planAnalysis.blockReason;
			} else if (belowMinimum && !hasExistingSave) {
				const needed = this.planPricing.minimumOrderWarning - totalMeals;
				saveBtn.textContent = `⚠️ Add ${needed} more meal${
					needed === 1 ? "" : "s"
				} (minimum 4 required)`;
			}
		} else {
			if (totalMeals === 0 && hasProteinBalls) {
				// Only protein balls selected
				saveBtn.textContent = "💾 Save Protein Ball Selection";
			} else if (
				totalMeals < this.planPricing.minimumOrderWarning &&
				hasExistingSave
			) {
				saveBtn.textContent = `💾 Save Selection (${totalMeals} meal${
					totalMeals > 1 ? "s" : ""
				})`;
			} else {
				saveBtn.textContent = "💾 Save My Weekly Selection";
			}
		}
	}

	renderAddToMealsButton(saveBtn, totalMeals, hasProteinBalls = false, hasSides = false) {
		const addBtn = document.createElement("button");
		addBtn.type = "button";
		addBtn.className = "button button--secondary";
		addBtn.setAttribute("data-add-to-meals", "");
		addBtn.style.marginTop = "1rem";
		addBtn.style.display = "block";
		addBtn.style.margin = "1rem auto 0 auto";

		// Sides-only case (no new meals, no protein balls) — "Add 0 Meal to
		// Existing Selection" was technically correct but confusing to a
		// customer who's just adding an extra, not a meal (September 2026).
		if (totalMeals === 0 && !hasProteinBalls && hasSides) {
			addBtn.textContent = "➕ Add Extras to Your Order";
		} else if (totalMeals === 0 && hasProteinBalls) {
			addBtn.textContent = "➕ Add Protein Balls to Existing Selection";
		} else if (totalMeals > 0 && hasProteinBalls) {
			addBtn.textContent = `➕ Add ${totalMeals} Meal${
				totalMeals > 1 ? "s" : ""
			} + Protein Balls to Existing Selection`;
		} else {
			addBtn.textContent = `➕ Add ${totalMeals} Meal${
				totalMeals > 1 ? "s" : ""
			} to Existing Selection`;
		}

		addBtn.addEventListener("click", async () => {
			await this.addToExistingMeals();
		});

		saveBtn.insertAdjacentElement("afterend", addBtn);
	}

	async saveWeeklyOrder(isAddition = false) {
		console.log(`=== SAVE WEEKLY ORDER START (${isAddition ? 'ADDITION' : 'REPLACEMENT'}) ===`);

		// Check for subscription first
		if (!this.mealPlan) {
			alert(
				"You need an active meal plan to save and order meals.\n\nPlease sign up for a subscription at primalbalancenutrition.com/pages/subscribe"
			);
			return;
		}

		const weekId = this.selectedWeek.id;
		const selectedMeals = this.mealQuantities[weekId];

		// UPDATED - supports multiple protein ball types
		const proteinBallMeals = (this.weeklyMenus[weekId] || [])
			.filter(m => this.isProteinBallMeal(m.id));
		const hasProteinBalls = proteinBallMeals.some(m => {
			const sel = this.proteinBallSelections[weekId]?.[m.id];
			return sel && sel !== "none";
		});

		const hasMeals =
			selectedMeals && Object.values(selectedMeals).some((qty) => qty > 0);

		// Sides added (August 2026) — this gate previously only knew about
		// meals/protein balls, so it blocked a sides-only save (Chef's
		// Choice + sides) even after the button itself was correctly
		// enabled for that case.
		const sideQuantitiesForCheck = this.sidesQuantities[weekId] || {};
		const hasSidesSelected = Object.values(sideQuantitiesForCheck).some((qty) => qty > 0);

		if (!hasMeals && !hasProteinBalls && !hasSidesSelected) {
			alert(
				"Please select at least one meal, protein ball option, or side before saving."
			);
			return;
		}

		// THURSDAY RESTRICTION CHECK
		if (
			weekId.includes("wednesday") &&
			this.isRestrictedPlan() &&
			!this.hasVIPTag() &&
			!this.hasWednesdayOnlyTag()
		) {
			const baseDate = this.getDeliveryDateFromWeekId(weekId);
			const thursdayCheck = this.canSaveWednesday(baseDate);

			if (!thursdayCheck.allowed) {
				alert(thursdayCheck.detailedReason);
				return;
			}
		}

		const totalMeals = hasMeals
			? Object.values(selectedMeals).reduce((sum, qty) => sum + qty, 0)
			: 0;

		// Validation
		const validationResult = this.validateSubscriptionRequirements(
			weekId,
			totalMeals
		);
		if (!validationResult.canSave) {
			alert(validationResult.message);
			return;
		}

		const planAnalysis = this.analyzeMealPlan(totalMeals, weekId);

		// Check minimum order requirement
		const hasExistingSave = this.hasExistingSelection(weekId);
		if (
			!hasExistingSave &&
			totalMeals < this.planPricing.minimumOrderWarning &&
			totalMeals > 0 &&
			!this.hasVIPTag()
		) {
			alert(
				`Cannot save: Minimum 4 meals required for initial order.\n\nYou currently have ${totalMeals} meal${
					totalMeals === 1 ? "" : "s"
				} selected.\n\nPlease add ${3 - totalMeals} more meal${
					3 - totalMeals === 1 ? "" : "s"
				} to continue.`
			);
			return;
		}

		if (planAnalysis.blockSave) {
			alert(`Cannot save: ${planAnalysis.blockReason}`);
			return;
		}

		// Build confirmation message
		const baseDate = this.getDeliveryDateFromWeekId(weekId);
		const weeklyTotal = this.calculateWeeklyTotal(baseDate);

		let projectedSunday = weeklyTotal.sunday;
		let projectedWednesday = weeklyTotal.wednesday;

		if (weekId.includes("sunday")) {
			projectedSunday = totalMeals;
		} else if (weekId.includes("wednesday")) {
			projectedWednesday = totalMeals;
		}

		const projectedWeeklyTotal = projectedSunday + projectedWednesday;

		// Friendly redemption reminder
		if (this.pendingMealRedemptions > 0) {
			const upcomingWeek = this.getUpcomingWeekForRedemption();
			const isUpcomingWeek = upcomingWeek && upcomingWeek.weekGroup === this.selectedWeek.weekGroup;
			
			if (isUpcomingWeek) {
				// Calculate total meals selected across both days this week
				const baseDate = this.getDeliveryDateFromWeekId(weekId);
				const weeklyTotal = this.calculateWeeklyTotal(baseDate);
				
				// Project what total will be after this save
				let projectedSunday = weeklyTotal.sunday;
				let projectedWednesday = weeklyTotal.wednesday;
				if (weekId.includes('sunday')) projectedSunday = totalMeals;
				else projectedWednesday = totalMeals;
				const projectedTotal = projectedSunday + projectedWednesday;
				
				const planSize = this.mealPlan?.mealsPerWeek || 0;
				const expectedTotal = planSize + this.pendingMealRedemptions;
				const remaining = expectedTotal - projectedTotal;
				
				if (remaining > 0) {
					const mealWord = remaining === 1 ? 'meal' : 'meals';
					const confirmed = confirm(
						`Just a reminder 🎁 — you have ${this.pendingMealRedemptions} redeemed ` +
						`${this.pendingMealRedemptions === 1 ? 'meal' : 'meals'} for this week.\n\n` +
						`You currently have ${projectedTotal} meals selected across both days. ` +
						`You could select ${remaining} more ${mealWord} to use your reward — ` +
						`or the chef will choose for you.\n\n` +
						`Would you like to go back and select more meals, ` +
						`or continue saving your current selection?`
					);
					// confirmed = true means "continue saving", false means "go back"
					if (!confirmed) return; // Go back to meal selection
				}
			}
		}

		// Build confirmation message
		let confirmMessage = "";
		if (this.hasVIPTag()) {
			confirmMessage =
				`✨ VIP Order Summary ✨\n\n` +
				`Total meals selected: ${totalMeals}\n` +
				`Week Total: ${projectedWeeklyTotal} meals\n` +
				`• Sunday: ${projectedSunday} meals\n` +
				`• Wednesday: ${projectedWednesday} meals\n`;
		} else if (totalMeals === 0 && hasProteinBalls) {
			confirmMessage = `You've selected only protein balls for ${this.selectedWeek.display}.\n\nContinue saving this selection?`;
		} else {
			confirmMessage =
				`📋 MEAL SELECTION SUMMARY\n\n` +
				`Your Subscription: ${this.mealPlan.planName}\n` +
				`Your Week Total: ${projectedWeeklyTotal} meals\n` +
				`• Sunday: ${projectedSunday} meals\n` +
				`• Wednesday: ${projectedWednesday} meals\n\n` +
				`Would you like to continue saving this selection?`;
		}

		// Add protein ball info if applicable
		if (hasProteinBalls) {
			proteinBallMeals.forEach(meal => {
				const sel = this.proteinBallSelections[weekId]?.[meal.id];
				if (!sel || sel === "none") return;
				const option = meal.options?.[sel];
				if (!option) return;
				confirmMessage += `\n+ ${meal.name.replace(/<[^>]*>/g, '')}: ${option.label} ($${option.price.toFixed(2)})`;
			});
		}

		// Add sides info if applicable — sides now saves together with
		// meals in this same confirmation/click, Sunday-only
		if (weekId.startsWith("sunday")) {
			const sideQuantities = this.sidesQuantities[weekId] || {};
			const sideCatalog = this.sidesCatalog[weekId] || [];
			const selectedSides = Object.entries(sideQuantities)
				.filter(([, qty]) => qty > 0)
				.map(([itemId, qty]) => {
					const item = sideCatalog.find(i => String(i.item_id) === String(itemId));
					return item ? { name: item.item_name, qty, price: Number(item.price) } : null;
				})
				.filter(Boolean);

			if (selectedSides.length > 0) {
				confirmMessage += `\n\nSides:`;
				selectedSides.forEach(s => {
					confirmMessage += `\n+ ${s.name} (×${s.qty}) — $${(s.price * s.qty).toFixed(2)}`;
				});
			}
		}

		
		// Add addition/replacement context
		if (isAddition) {
			confirmMessage = `➕ ADD TO EXISTING SELECTION\n\n${confirmMessage}\n\nThese meals will be ADDED to your existing order.`;
		}

		const confirmed = confirm(confirmMessage);
		if (!confirmed) return;

		// GET BUTTON REFERENCES BEFORE DISABLING
		const saveBtn = document.querySelector("[data-save-order]");
		const addBtn = document.querySelector("[data-add-to-meals]");

		if (!saveBtn) {
			console.error("Save button not found!");
			alert("Error: Save button not found. Please refresh the page.");
			return;
		}

		// Store original button states
		const originalSaveText = saveBtn.textContent;
		const originalSaveDisabled = saveBtn.disabled;
		const originalAddText = addBtn ? addBtn.textContent : "";
		const originalAddDisabled = addBtn ? addBtn.disabled : false;

		try {
			// Disable buttons
			saveBtn.textContent = "💾 Saving...";
			saveBtn.disabled = true;

			if (addBtn) {
				addBtn.textContent = "➕ Please Wait...";
				addBtn.disabled = true;
			}

			this.syncStatus.showSyncing();

			const saveId = `save_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
			const orderData = this.buildOrderData(weekId, selectedMeals, saveId);
			orderData.isReplacement = !isAddition;  
			orderData.isAddition = isAddition;       
			orderData.deadlineTime = this.calculateDeadline(weekId);

			// 1. Save to Railway FIRST. Railway is what the kitchen, chef's choice
			// and billing all read from, so nothing below runs — no localStorage,
			// no Sheets backup (which triggers the confirmation email), no
			// "saved" message — unless Railway actually saved it (October 2026,
			// after an order saved only to Sheets showed as saved and emailed a
			// confirmation, but never reached Railway).
			const railwayResult = await this.railwayAPI.saveSelection(orderData);
			if (!railwayResult || !railwayResult.success) {
				console.error("❌ Railway save failed:", railwayResult && railwayResult.error);
				this.syncStatus.showOffline();
				const railwayFailure = new Error((railwayResult && railwayResult.error) || "Railway save failed");
				railwayFailure.isRailwayFailure = true;
				throw railwayFailure;
			}
			console.log("✅ Railway save: SUCCESS");
			this.syncStatus.showSuccess(
				isAddition ? "Meals added to order!" : "Meals saved to cloud!"
			);

			// 2. Local cache — only after Railway confirmed the save
			try {
				await this.saveToLocalStorage(orderData);
			} catch (localError) {
				console.error("❌ localStorage error:", localError);
			}

			// 3. Google Sheets backup — a failure here doesn't affect the order
			let googleSheetsSuccess = false;
			try {
				const sheetsResult = await this.saveToGoogleSheetsClean(orderData);
				googleSheetsSuccess = !!(sheetsResult && sheetsResult.success);
				console.log(googleSheetsSuccess ? "✅ Google Sheets backup: SUCCESS" : "⚠️ Google Sheets backup failed (order is saved in Railway)");
			} catch (sheetsError) {
				console.error("❌ Google Sheets error (order is saved in Railway):", sheetsError);
			}

			{
				this.updateLocalState(weekId, orderData);

				// Sides saves together with meals now, in this same click,
				// rather than through its own separate button — mirrors how
				// protein balls already ride along with the meal save.
				// Sunday-only, matching Sides' own restriction. Meals are
				// already saved at this point regardless of what happens
				// here, so a sides failure gets its own clear message
				// rather than silently vanishing into the meals success
				// alert.
				let sidesResult = { success: true, hadSelections: false };
				if (weekId.startsWith("sunday")) {
					sidesResult = await this.saveSidesQuietly(weekId);
				}

				if (this.customerPortal) {
					this.customerPortal.loadPortalData();
				}

				this.handleSuccessfulSave(totalMeals, true);

				if (sidesResult.hadSelections && !sidesResult.success) {
					alert(`Your meals saved, but your sides did not (${sidesResult.error || "unknown error"}). Please try saving again to include your sides.`);
				}

				console.log(`Saved to: Railway ✓ ${googleSheetsSuccess ? "Sheets ✓" : "Sheets ✗"}`);
			}
		} catch (error) {
			console.error("❌ Save error:", error);
			this.syncStatus.showError("Save failed");
			alert("Your meal selection did NOT save. Please try again.\n\nIf this keeps happening, please contact us before the Tuesday 4 PM deadline so we can make sure your order is in.");
		} finally {
			// Always re-enable buttons
			if (saveBtn) {
				saveBtn.textContent = originalSaveText;
				saveBtn.disabled = originalSaveDisabled;
			}
			if (addBtn) {
				addBtn.textContent = originalAddText;
				addBtn.disabled = originalAddDisabled;
			}
			
			setTimeout(() => {
				this.updateOrderSummary();
			}, 100);
		}
	}

	async clearAllSelections() {
		const weekId = this.selectedWeek?.id;
		if (!weekId) {
			alert('Please select a week first');
			return;
		}
		
		const weekInfo = this.getWeekInfo(weekId);
		const displayDate = weekInfo.display;
		
		const confirmed = confirm(
			`⚠️ This will permanently delete your saved meal selections for ${displayDate}.\n\n` +
			`This cannot be undone. Continue?`
		);
		
		if (!confirmed) {
			return;
		}
		
		const clearBtn = document.querySelector('[data-clear-selections]');
		
		try {
			if (clearBtn) {
				clearBtn.disabled = true;
				clearBtn.textContent = 'Clearing...';
			}
			
			this.syncStatus.showSyncing();
			
			console.log(`🗑️ Clearing selections for ${weekId}`);
			
			// Delete from Railway
			const railwayResult = await this.railwayAPI.deleteSelection(this.customer.id, weekId);
			
			if (railwayResult.success) {
				console.log('✅ Railway delete successful');

				// Also try to delete from Google Sheets backup
				try {
					await this.googleSheets.deleteSelections(this.customer.id, weekId);
					console.log('✅ Google Sheets backup deleted');
				} catch (sheetsError) {
					console.warn('⚠️ Google Sheets delete failed (Railway deleted successfully):', sheetsError);
				}

				// Clear from frontend
				delete this.weeklySelections[weekId];
				delete this.mealQuantities[weekId];
				delete this.mealOptions[weekId];
				delete this.proteinBallSelections[weekId];

				// "Delete all selections" needs to actually wipe any saved
				// sides/extras for this date too, not just the meals — this
				// deletes the meal_selections row above but that's a
				// separate table from ala_carte_selections, so without this
				// a customer's previously saved sides would silently
				// reappear (still on file) the next time they load the page.
				let sidesClearResult = { success: true };
				if (weekId.startsWith("sunday")) {
					sidesClearResult = await this.clearSidesSelection(weekId);
					if (sidesClearResult.success && document.querySelector("[data-sides-grid]")) {
						this.renderSidesGrid();
					}
				}

				// Clear from localStorage
				await this.saveToLocalStorage();

				// Refresh display
				this.showMealSelection();

				this.syncStatus.showSuccess("Selections cleared!");
				alert(`✅ Your meal selections for ${displayDate} have been deleted.`);

				if (!sidesClearResult.success) {
					alert(`Your meals were deleted, but your saved sides could not be cleared (${sidesClearResult.error || "unknown error"}). Please try deleting again.`);
				}

			} else {
				throw new Error(railwayResult.error || 'Failed to delete selections');
			}
			
		} catch (error) {
			console.error('❌ Clear error:', error);
			this.syncStatus.showError("Clear failed");
			alert('❌ Failed to delete selections. Please try again.');
		} finally {
			if (clearBtn) {
				clearBtn.disabled = false;
				clearBtn.textContent = 'Clear Selections';
			}
		}
	}

	validateSubscriptionRequirements(weekId, totalMeals) {
		if (!this.mealPlan) {
			return {
				canSave: false,
				message: "You need an active meal subscription to save meals."
			};
		}

		// Sides-only save (Chef's Choice for meals + a side added on top) —
		// none of the meal-count rules below know anything about sides, and
		// every one of them would otherwise block this since they all
		// enforce some meal-count minimum unconditionally. If there's no
		// meal order being submitted at all, there's nothing for these
		// rules to validate.
		const sideQuantitiesForValidation = this.sidesQuantities[weekId] || {};
		const hasSidesForValidation = Object.values(sideQuantitiesForValidation).some((qty) => qty > 0);
		const proteinBallMealsForValidation = (this.weeklyMenus[weekId] || [])
			.filter(m => this.isProteinBallMeal(m.id));
		const hasProteinBallsForValidation = proteinBallMealsForValidation.some(m => {
			const sel = this.proteinBallSelections[weekId]?.[m.id];
			return sel && sel !== "none";
		});
		if (totalMeals === 0 && !hasProteinBallsForValidation && hasSidesForValidation) {
			return { canSave: true };
		}

		// VIP and Special tag users have full freedom
		if (this.mealPlan.isVIP || this.mealPlan.hasSpecialTag) {
			if (totalMeals < 3) {
				return {
					canSave: false,
					message: `Minimum ${this.planPricing.minimumOrderWarning} meals required for any delivery day.\n\nYou currently have ${totalMeals} meal${totalMeals === 1 ? "" : "s"} selected.`
				};
			}
			return { canSave: true };
		}

		// **NEW: Thursday-only customers get special handling**
		if (this.hasWednesdayOnlyTag()) {
			// Wednesday-only customers must meet their subscription minimum on Wednesday
			if (weekId.includes("wednesday")) {
				if (totalMeals < this.mealPlan.mealsPerWeek) {
					const needed = this.mealPlan.mealsPerWeek - totalMeals;
					return {
						canSave: false,
						message: `Your ${this.mealPlan.planName} requires ${
							this.mealPlan.mealsPerWeek
						} meals.\n\nYou currently have ${totalMeals} meal${
							totalMeals === 1 ? "" : "s"
						} selected.\n\nPlease add ${needed} more meal${
							needed === 1 ? "" : "s"
						} to continue.`
					};
				}
			}
			// Thursday-only customers shouldn't see Monday, but just in case
			if (weekId.includes("sunday")) {
				return {
					canSave: false,
					message:
						"Your account is set up for Wednesday deliveries only. Please select Wednesday meals."
				};
			}
			return { canSave: true };
		}

		// Get current meal counts
		const baseDate = this.getDeliveryDateFromWeekId(weekId);
		const savedTotals = this.calculateWeeklyTotal(baseDate);

		// For this validation, we only care about saved data (not working selections)
		const currentSunday = savedTotals.sunday;
		const currentWednesday = savedTotals.wednesday;

		// Calculate projected totals after this save
		let projectedSunday = currentSunday;
		let projectedWednesday = currentWednesday;

		if (weekId.includes("sunday")) {
			projectedSunday = totalMeals;
		} else if (weekId.includes("wednesday")) {
			projectedWednesday = totalMeals;
		}

		const projectedTotal = projectedSunday + projectedWednesday;

		console.log(`Validation check:
        Plan: ${this.mealPlan.planName} (${this.mealPlan.mealsPerWeek} meals)
        Current: Sunday=${currentSunday}, Wednesday=${currentWednesday}
        Saving ${totalMeals} to ${weekId}
        Projected: Sunday=${projectedSunday}, Wednesday=${projectedWednesday}, Total=${projectedTotal}`);

		// RULE 1: Plans of 8 meals or less - Sunday must match subscription exactly
		if (this.mealPlan.mealsPerWeek <= 8) {
			console.log("=== 8 MEAL OR LESS PLAN VALIDATION ===");
			console.log("Plan meals per week:", this.mealPlan.mealsPerWeek);
			console.log("Current Sunday total:", currentSunday);

			if (weekId.includes("sunday")) {
				if (totalMeals < this.mealPlan.mealsPerWeek) {
					const needed = this.mealPlan.mealsPerWeek - totalMeals;
					return {
						canSave: false,
						message: `Your ${this.mealPlan.planName} requires at least ${
							this.mealPlan.mealsPerWeek
						} meals from Sunday.\n\nYou currently have ${totalMeals} meal${
							totalMeals === 1 ? "" : "s"
						} selected.\n\nPlease add ${needed} more meal${
							needed === 1 ? "" : "s"
						} to continue.`
					};
				}
			} else if (weekId.includes("wednesday")) {
				if (currentSunday < this.mealPlan.mealsPerWeek) {
					return {
						canSave: false,
						message: `Your ${this.mealPlan.planName} requires ${
							this.mealPlan.mealsPerWeek
						} meals from Sunday before adding Wednesday meals.\n\nSunday currently has: ${currentSunday} meals\nYou need: ${
							this.mealPlan.mealsPerWeek - currentSunday
						} more meals from Sunday first.\n\nWednesday meals are available as add-ons after meeting your Sunday requirement.`
					};
				}
			}
			return { canSave: true };
		}

		// RULE 2: Plans of 10+ meals
		if (this.mealPlan.mealsPerWeek >= 10) {
			if (totalMeals < 3) {
				return {
					canSave: false,
					message: `Minimum ${this.planPricing.minimumOrderWarning} meals required for any delivery day.\n\nYou currently have ${totalMeals} meal${
    					totalMeals === 1 ? "" : "s"
					} selected.`
				};
			}

			const otherDayMeals = weekId.includes("sunday")
				? currentWednesday
				: currentSunday;

			if (otherDayMeals > 0 && projectedTotal < this.mealPlan.mealsPerWeek) {
				const needed = this.mealPlan.mealsPerWeek - projectedTotal;
				return {
					canSave: false,
					message: `Your ${this.mealPlan.planName} requires ${
						this.mealPlan.mealsPerWeek
					} meals total when ordering from both days.\n\nCurrent week total would be: ${projectedTotal} meals\n• Sunday: ${projectedSunday} meals\n• Wednesday: ${projectedWednesday} meals\n\nYou need ${needed} more meal${
						needed > 1 ? "s" : ""
					} to meet your subscription requirement.`
				};
			}
		}

		return { canSave: true };
	}

	calculateDeadline(weekId) {
		// Parse weekId: "sunday-2026-01-05" or "wednesday-2026-01-08"
		const parts = weekId.split('-');
		if (parts.length === 4) {
			const year = parseInt(parts[1]);
			const month = parseInt(parts[2]) - 1; // JS months are 0-indexed
			const day = parseInt(parts[3]);
			
			// Delivery date
			const deliveryDate = new Date(year, month, day);
			
			// Deadline is always Tuesday before at 4 PM
			const deadline = new Date(deliveryDate);
			deadline.setDate(deliveryDate.getDate() - (deliveryDate.getDay() === 0 ? 5 : 8)); // Sunday: -5 days, Wednesday: -8 days
			deadline.setHours(16, 0, 0, 0); // 4 PM
			
			return deadline.toISOString();
		}
		
		// Fallback
		return new Date().toISOString();
	}

	buildOrderData(weekId, selectedMeals, saveId) {
		console.log("🏗️ === BUILDING ORDER DATA ===");
		console.log("🏗️ Week ID:", weekId);
		console.log("🏗️ Selected meals from UI:", selectedMeals);

		// Derive delivery day from weekId
		const deliveryDay = weekId.startsWith('sunday') ? 'Sunday' : 'Wednesday';

		const orderData = {
			customerId: this.customer.id,
			customerEmail: this.customer.email,
			customerName: this.customer.name.trim(),
			weekId: weekId,
			deliveryDay: deliveryDay, 
			deliveryDate: this.selectedWeek.deliveryDate.toISOString(),
			deliveryDisplay: this.selectedWeek.display,
			submissionTime: new Date().toISOString(),
			meals: [],
			proteinBalls: null,
			saveAttemptId: saveId
		};

		const meals = this.weeklyMenus[weekId] || [];
		console.log(
			"🏗️ Menu for this week has",
			meals.length,
			"meals with IDs:",
			meals.map((m) => m.id)
		);

		// Build regular meals array (excluding protein balls)
		for (const [mealId, quantity] of Object.entries(selectedMeals)) {
			if (quantity > 0 && !this.isProteinBallMeal(mealId)) {
				const meal = meals.find((m) => m.id.toLowerCase() === mealId.toLowerCase());
				if (meal) {
					console.log("🏗️ Adding meal:", meal.id, meal.name, "qty:", quantity);
					const selectedOptions =
						(this.mealOptions[weekId] && this.mealOptions[weekId][mealId]) || {};

					let totalAddOnCost = 0;
					const optionDetails = {};

					if (meal.options) {
						Object.entries(selectedOptions).forEach(([optionKey, isSelected]) => {
							if (isSelected && meal.options[optionKey]) {
								const option = meal.options[optionKey];
								optionDetails[optionKey] = {
									label: option.label,
									price: option.price || 0,
									isSelected: true,
									type: option.type || 'addon',  
									value: option.label  
								};
								totalAddOnCost += (option.price || 0) * quantity;
							}
						});
					}

					orderData.meals.push({
						id: meal.id,
						name: meal.name,
						description: meal.description,
						quantity: quantity,
						imageUrl: meal.image || "",  
						shopifyHandle: meal.shopifyHandle,
						selectedOptions: selectedOptions,
						optionDetails: optionDetails,
						totalAddOnCost: totalAddOnCost
					});
				}
			}
		}

		// Add protein ball selections - supports multiple protein ball types
		const proteinBallMeals = meals.filter(m => this.isProteinBallMeal(m.id));
		const proteinBallsData = [];

		proteinBallMeals.forEach(meal => {
			const sel = this.proteinBallSelections?.[weekId]?.[meal.id];
			if (!sel || sel === "none") return;
			if (!meal.options?.[sel]) return;
			const selectedOption = meal.options[sel];
			proteinBallsData.push({
				id: meal.id,
				name: meal.name,
				selectedOption: sel,
				optionLabel: selectedOption.label,
				price: selectedOption.price || 0,
				shopifyHandle: meal.shopifyHandle
			});
		});

		// Keep backwards compatible single proteinBalls field, store all in proteinBallsArray
		if (proteinBallsData.length > 0) {
			orderData.proteinBalls = proteinBallsData[0];
			orderData.proteinBallsArray = proteinBallsData;
		}
		
		console.log(
			"🏗️ Final orderData.meals:",
			orderData.meals.map((m) => ({ id: m.id, name: m.name }))
		);
		return orderData;
	}

	async saveToLocalStorage(orderData) {
		try {
			console.log("💾 === SAVING TO LOCALSTORAGE ===");
			console.log("💾 Week ID:", orderData.weekId);
			
			const storageKey = `meal_selections_${this.customer.id}`;
			let existingData = {};

			const existing = localStorage.getItem(storageKey);
			if (existing) {
				try {
					existingData = JSON.parse(existing);
				} catch (e) {
					console.warn("Could not parse existing data");
				}
			}

			// Build the selection data matching Railway's format
			const selectionData = {
				weekId: orderData.weekId,
				deliveryDate: orderData.deliveryDate,
				deliveryDay: orderData.deliveryDay,  
				deliveryDisplay: orderData.deliveryDisplay,
				submissionTime: orderData.submissionTime,
				deadlineTime: orderData.deadlineTime || this.calculateDeadline(orderData.weekId),
				isAddition: orderData.isAddition || false,
				isReplacement: orderData.isReplacement || false,  
				mealCount: orderData.meals.reduce((sum, m) => sum + m.quantity, 0),
				meals: orderData.meals.map(meal => ({ 
					id: meal.id,
					name: meal.name,
					description: meal.description,
					imageUrl: meal.imageUrl || meal.image,  
					quantity: meal.quantity,
					shopifyHandle: meal.shopifyHandle,
					selectedOptions: meal.selectedOptions || {},
					optionDetails: meal.optionDetails || {},
					totalAddOnCost: meal.totalAddOnCost || 0
				})),
				proteinBalls: orderData.proteinBalls || null,
				savedAt: new Date().toISOString()
			};

			existingData[orderData.weekId] = selectionData;
			existingData.dataVersion = this.dataVersion;
			existingData.lastUpdated = new Date().toISOString();
			existingData.customerInfo = {
				id: this.customer.id,
				name: this.customer.name,
				email: this.customer.email
			};

			localStorage.setItem(storageKey, JSON.stringify(existingData));

			console.log(`✅ Saved to localStorage for ${orderData.weekId}`);
			return true;
		} catch (error) {
			console.error("❌ localStorage save failed:", error);
			return false;
		}
	}
	async manualRefresh() {
		console.log("🔄 Manual refresh requested...");

		// Force sync from cloud
		await this.syncFromCloud();

		// Refresh UI
		this.renderWeeks();
		if (this.selectedWeek) {
			this.showMealSelection();
		}
		if (this.customerPortal) {
			this.customerPortal.loadPortalData();
		}

		console.log("✅ Manual refresh complete");
	}

	handlePageVisibilityChange() {
		if (!document.hidden) {
			// Page became visible again
			console.log("📄 Page became visible");

			// DON'T clear if we're viewing a past deadline week with selections
			if (this.selectedWeek && this.selectedWeek.isDeadlinePassed) {
				const hasSelection = this.hasExistingSelection(this.selectedWeek.id);
				if (hasSelection) {
					console.log("📄 Viewing past deadline with selection - NOT clearing data");
					return; // Don't clear anything
				}
			}

			// Otherwise, clear working selections
			console.log("📄 Clearing working selections");
			this.clearAllWorkingSelections();

			// If a week is selected, refresh its display
			if (this.selectedWeek) {
				setTimeout(() => {
					this.showMealSelection();
				}, 100);
			}
		}
	}

	handleStorageQuotaExceeded() {
		alert("Storage limit reached. Old meal selections will be cleaned up.");

		// Clean up old selections (keep only last 8 weeks)
		const storageKey = `meal_selections_${this.customer.id}`;
		const data = JSON.parse(localStorage.getItem(storageKey) || "{}");

		const weekIds = Object.keys(data).filter(
			(k) => k !== "lastUpdated" && k !== "customerInfo" && k !== "dataVersion"
		);

		// Sort by date
		weekIds.sort((a, b) => {
			const dateA = data[a].savedAt || data[a].submissionTime;
			const dateB = data[b].savedAt || data[b].submissionTime;
			return new Date(dateB) - new Date(dateA);
		});

		// Keep only last 8 weeks
		const toKeep = weekIds.slice(0, 8);
		const cleanData = {
			dataVersion: this.dataVersion,
			lastUpdated: new Date().toISOString(),
			customerInfo: data.customerInfo
		};

		toKeep.forEach((weekId) => {
			cleanData[weekId] = data[weekId];
		});

		localStorage.setItem(storageKey, JSON.stringify(cleanData));

		this.syncLogger.log("storage_quota_exceeded", {
			originalCount: weekIds.length,
			keptCount: toKeep.length,
			removedCount: weekIds.length - toKeep.length
		});
	}

	async saveToGoogleSheetsClean(orderData) {
		return new Promise((resolve) => {
			try {
				console.log(`📊 Saving to Google Sheets via form submission:`, orderData);

				const form = document.createElement("form");
				form.method = "POST";
				form.action = this.googleSheets.webAppUrl;
				form.target = "meal_iframe_" + Date.now();
				form.style.display = "none";

				const actionField = document.createElement("input");
				actionField.type = "hidden";
				actionField.name = "action";
				actionField.value = "saveOrder";
				form.appendChild(actionField);

				const dataField = document.createElement("input");
				dataField.type = "hidden";
				dataField.name = "data";
				dataField.value = JSON.stringify(orderData);
				form.appendChild(dataField);

				// Create iframe
				const iframe = document.createElement("iframe");
				iframe.name = form.target;
				iframe.style.display = "none";
				document.body.appendChild(iframe);
				document.body.appendChild(form);

				// Submit
				form.submit();

				// Cleanup and resolve
				setTimeout(() => {
					if (document.body.contains(form)) document.body.removeChild(form);
					if (document.body.contains(iframe)) document.body.removeChild(iframe);
					resolve({ success: true });
				}, 2000);

				console.log("✅ Meal order form submitted");
			} catch (error) {
				console.error("❌ Form submission failed:", error);
				resolve({ success: false, error: error.message });
			}
		});
	}

	async fallbackFormSubmissionClean(orderData) {
		return new Promise((resolve) => {
			try {
				console.log("📝 Using form submission fallback");

				// Create form
				const form = document.createElement("form");
				form.method = "POST";
				form.action = this.googleSheets.webAppUrl;
				form.target = "hidden_iframe_" + Date.now(); // Unique iframe name
				form.style.display = "none";

				// Add data fields
				const actionField = document.createElement("input");
				actionField.type = "hidden";
				actionField.name = "action";
				actionField.value = "saveOrder";
				form.appendChild(actionField);

				const dataField = document.createElement("input");
				dataField.type = "hidden";
				dataField.name = "data";
				dataField.value = JSON.stringify(orderData);
				form.appendChild(dataField);

				// Create unique iframe
				const iframe = document.createElement("iframe");
				iframe.name = form.target;
				iframe.style.display = "none";
				iframe.style.width = "0";
				iframe.style.height = "0";
				iframe.style.border = "none";

				// Handle iframe load (success or failure)
				let resolved = false;
				const resolveOnce = (result) => {
					if (!resolved) {
						resolved = true;
						// Cleanup
						if (document.body.contains(form)) document.body.removeChild(form);
						if (document.body.contains(iframe)) document.body.removeChild(iframe);
						resolve(result);
					}
				};

				iframe.onload = () => {
					console.log("✅ Form submission completed");
					resolveOnce({ success: true });
				};

				iframe.onerror = (error) => {
					console.log("❌ Form submission failed:", error);
					resolveOnce({ success: false, error: "Form submission failed" });
				};

				// Timeout after 10 seconds
				setTimeout(() => {
					console.log("⏰ Form submission timeout");
					resolveOnce({ success: false, error: "Timeout" });
				}, 10000);

				// Execute submission
				document.body.appendChild(iframe);
				document.body.appendChild(form);
				form.submit();
			} catch (error) {
				console.error("❌ Form submission setup failed:", error);
				resolve({ success: false, error: error.message });
			}
		});
	}

	// ADD TO EXISTING MEALS FUNCTIONALITY
	async addToExistingMeals() {
		console.log("Adding to existing meals...");

		const weekId = this.selectedWeek.id;
		const currentMeals = this.mealQuantities[weekId] || {};
		const currentOptions = this.mealOptions[weekId] || {};

		// Get existing saved data
		const existingSelection = this.weeklySelections[weekId];
		if (
			!existingSelection ||
			(!existingSelection.meals && !existingSelection.proteinBalls)
		) {
			alert("No existing selection found to add to.");
			return;
		}

		// Convert current selector state to meal data format
		const meals = this.weeklyMenus[weekId] || this.weeklyMenus["monday-0"];
		const newMeals = this.buildNewMealsArray(currentMeals, currentOptions, meals);
		const hasNewMeals = newMeals.length > 0;

		// Check for any protein ball selections across all protein ball meals
		const proteinBallMeals = meals.filter(m => this.isProteinBallMeal(m.id));
		const hasNewProteinBalls = proteinBallMeals.some(m => {
			const sel = this.proteinBallSelections[weekId]?.[m.id];
			return sel && sel !== "none";
		});

		// Sides-only addition (August 2026) — nothing new to actually ADD to
		// the existing meal selection, so skip all the meal-combining logic
		// below entirely rather than trying to force it through a flow
		// that's built specifically around merging meals. Sides always
		// fully-replace on save (not additive), so this is just a normal
		// sides save, same as the main Save button would do.
		const sideQuantitiesForAdd = this.sidesQuantities[weekId] || {};
		const hasSidesToAdd = Object.values(sideQuantitiesForAdd).some((qty) => qty > 0);
		if (!hasNewMeals && !hasNewProteinBalls && hasSidesToAdd) {
			if (weekId.startsWith("sunday")) {
				const sidesResult = await this.saveSidesQuietly(weekId);
				if (sidesResult.success) {
					alert("Your sides selection has been saved!");
					if (this.customerPortal) {
						this.customerPortal.loadPortalData();
					}
					// Zero the extras counters back out after a successful save,
					// same as everywhere else on the page, so re-opening this
					// week doesn't look like the extras are still "selected"
					// when they've already gone through.
					this.sidesQuantities[weekId] = {};
					if (document.querySelector("[data-sides-grid]")) {
						this.renderSidesGrid();
					}
					// Scroll back to the top so they land on their order
					// summary, same as every other save path on this page.
					setTimeout(() => {
						window.scrollTo({ top: 0, behavior: "smooth" });
					}, 500);
				} else {
					alert(`Could not save your sides: ${sidesResult.error || "unknown error"}. Please try again.`);
				}
			}
			return;
		}

		// Check if we have ANYTHING to add (meals OR protein balls OR sides)
		if (!hasNewMeals && !hasNewProteinBalls && !hasSidesToAdd) {
			alert("Please select at least one meal, protein ball option, or side to add.");
			return;
		}

		// Combine with existing meals
		const combinedMeals = [...(existingSelection.meals || []), ...newMeals];

		// Handle protein balls - combine existing + new into array
		const existingPBArray = existingSelection.proteinBallsArray ||
			(existingSelection.proteinBalls ? [existingSelection.proteinBalls] : []);

		const newPBArray = [];
		if (hasNewProteinBalls) {
			proteinBallMeals.forEach(meal => {
				const sel = this.proteinBallSelections[weekId]?.[meal.id];
				if (!sel || sel === "none" || !meal.options?.[sel]) return;
				const selectedOption = meal.options[sel];
				newPBArray.push({
					id: meal.id,
					name: meal.name,
					selectedOption: sel,
					optionLabel: selectedOption.label,
					price: selectedOption.price || 0,
					shopifyHandle: meal.shopifyHandle
				});
			});
		}

		// Merge: keep existing entries not being replaced, add new ones
		const mergedPBArray = [...existingPBArray];
		newPBArray.forEach(newPB => {
			const existingIdx = mergedPBArray.findIndex(pb => pb.id === newPB.id);
			if (existingIdx >= 0) {
				mergedPBArray[existingIdx] = newPB;
			} else {
				mergedPBArray.push(newPB);
			}
		});

		const finalProteinBalls = mergedPBArray.length > 0 ? mergedPBArray[0] : null;
		const finalProteinBallsArray = mergedPBArray;

		// Show confirmation
		const totalNewMeals = newMeals.reduce((sum, meal) => sum + meal.quantity, 0);
		const totalExistingMeals = (existingSelection.meals || []).reduce(
			(sum, meal) => sum + meal.quantity,
			0
		);
		const totalCombinedMeals = totalNewMeals + totalExistingMeals;

		
		// Friendly redemption reminder
		if (this.pendingMealRedemptions > 0) {
			const upcomingWeek = this.getUpcomingWeekForRedemption();
			const isUpcomingWeek = upcomingWeek && upcomingWeek.weekGroup === this.selectedWeek.weekGroup;
			
			if (isUpcomingWeek) {
				const baseDate = this.getDeliveryDateFromWeekId(weekId);
				const weeklyTotal = this.calculateWeeklyTotal(baseDate);
				
				let projectedSunday = weeklyTotal.sunday;
				let projectedWednesday = weeklyTotal.wednesday;
				if (weekId.includes('sunday')) projectedSunday = totalNewMeals + (weeklyTotal.sunday || 0);
				else projectedWednesday = totalNewMeals + (weeklyTotal.wednesday || 0);
				const projectedTotal = projectedSunday + projectedWednesday;
				
				const planSize = this.mealPlan?.mealsPerWeek || 0;
				const expectedTotal = planSize + this.pendingMealRedemptions;
				const remaining = expectedTotal - projectedTotal;
				
				if (remaining > 0) {
					const mealWord = remaining === 1 ? 'meal' : 'meals';
					const confirmed = confirm(
						`Just a reminder 🎁 — you have ${this.pendingMealRedemptions} redeemed ` +
						`${this.pendingMealRedemptions === 1 ? 'meal' : 'meals'} for this week.\n\n` +
						`You currently have ${projectedTotal} meals selected across both days. ` +
						`You could select ${remaining} more ${mealWord} to use your reward — ` +
						`or the chef will choose for you.\n\n` +
						`Would you like to go back and select more meals, ` +
						`or continue saving your current selection?`
					);
					if (!confirmed) return;
				}
			}
		}

		let confirmMessage = "";

		if (hasNewMeals && hasNewProteinBalls) {
			confirmMessage = `Add ${totalNewMeals} meal${
				totalNewMeals > 1 ? "s" : ""
			} + protein balls to your existing selection?\n\n`;
		} else if (hasNewMeals) {
			confirmMessage = `Add ${totalNewMeals} meal${
				totalNewMeals > 1 ? "s" : ""
			} to your existing ${totalExistingMeals} meal${
				totalExistingMeals > 1 ? "s" : ""
			}?\n\n`;
		} else {
			confirmMessage = `Add protein balls to your existing selection?\n\n`;
		}

		confirmMessage += `New meal total: ${totalCombinedMeals} meals for ${this.selectedWeek.display}\n\nContinue?`;

		const confirmed = confirm(confirmMessage);
		if (!confirmed) return;

		// DISABLE BUTTONS DURING SAVE
		const addBtn = document.querySelector("[data-add-to-meals]");
		const saveBtn = document.querySelector("[data-save-order]");

		let originalAddText = "";
		let originalSaveText = "";

		if (addBtn) {
			originalAddText = addBtn.textContent;
			addBtn.textContent = "➕ Adding...";
			addBtn.disabled = true;
		}

		if (saveBtn) {
			originalSaveText = saveBtn.textContent;
			saveBtn.disabled = true;
		}

		try {
			// Save to Google Sheets and localStorage
			await this.saveAddOnMeals(
				weekId,
				newMeals,
				combinedMeals,
				finalProteinBalls,
				finalProteinBallsArray,
				hasNewProteinBalls
			);

			// Sides ride along here too, same as the main Save button
			if (hasSidesToAdd && weekId.startsWith("sunday")) {
				const sidesResult = await this.saveSidesQuietly(weekId);
				if (!sidesResult.success) {
					alert(`Your meals were added, but your sides did not save (${sidesResult.error || "unknown error"}). Please try saving your sides again.`);
				}
			}
		} catch (error) {
			console.error("Error adding meals:", error);
			alert("Your added meals did NOT save. Please try again.\n\nIf this keeps happening, please contact us before the Tuesday 4 PM deadline so we can make sure your order is in.");
		} finally {
			// RE-ENABLE BUTTONS
			if (addBtn) {
				addBtn.textContent = originalAddText;
				addBtn.disabled = false;
			}

			if (saveBtn) {
				saveBtn.disabled = false;
			}
		}
	}

	buildNewMealsArray(currentMeals, currentOptions, meals) {
		const newMeals = [];

		for (const [mealId, quantity] of Object.entries(currentMeals)) {
			if (quantity > 0) {
				const meal = meals.find((m) => m.id.toLowerCase() === mealId.toLowerCase());
				if (meal) {
					const selectedOptions = currentOptions[mealId] || {};

					let totalAddOnCost = 0;
					const optionDetails = {};

					if (meal.options) {
						Object.entries(selectedOptions).forEach(([optionKey, isSelected]) => {
							if (isSelected && meal.options[optionKey]) {
								const option = meal.options[optionKey];
								optionDetails[optionKey] = {
									label: option.label,
									price: option.price || 0,
									isSelected: true
								};
								totalAddOnCost += (option.price || 0) * quantity;
							}
						});
					}

					newMeals.push({
						id: meal.id,
						name: meal.name,
						description: meal.description,
						quantity: quantity,
						shopifyHandle: meal.shopifyHandle,
						selectedOptions: selectedOptions,
						optionDetails: optionDetails,
						totalAddOnCost: totalAddOnCost
					});
				}
			}
		}
		return newMeals;
	}

	async saveAddOnMeals(
		weekId,
		newMeals,
		combinedMeals,
		finalProteinBalls,
		finalProteinBallsArray,
		hasNewProteinBalls) {
			
		const orderDataForSheets = {
			customerId: this.customer.id,
			customerEmail: this.customer.email,
			customerName: this.customer.name.trim(),
			weekId: weekId,
			deliveryDay: weekId.startsWith('sunday') ? 'Sunday' : 'Wednesday', 
			deliveryDate: this.selectedWeek.deliveryDate.toISOString(),
			deliveryDisplay: this.selectedWeek.display,
			submissionTime: new Date().toISOString(),
			meals: newMeals,
			proteinBalls: hasNewProteinBalls ? finalProteinBalls : null,
			proteinBallsArray: finalProteinBallsArray.length > 0 ? finalProteinBallsArray : null,
			saveAttemptId: `addon_${Date.now()}_${Math.random()
				.toString(36)
				.substr(2, 5)}`,
			isAddition: true,
			isReplacement: false
		};
		console.log("📝 Saving as ADDITION (will add to existing)");

		const combinedOrderData = {
			...orderDataForSheets,
			meals: combinedMeals, 
			proteinBalls: finalProteinBalls, 
			proteinBallsArray: finalProteinBallsArray.length > 0 ? finalProteinBallsArray : null,
			isReplacement: true 
		};

		// Railway FIRST — if it fails, throw so the caller shows an error and
		// nothing else (localStorage, Sheets backup / confirmation email) runs.
		console.log('💾 Saving combined meals to Railway...');
		const railwayResult = await this.railwayAPI.saveSelection(combinedOrderData);
		if (!railwayResult || !railwayResult.success) {
			console.error('❌ Railway addition save failed:', railwayResult && railwayResult.error);
			throw new Error((railwayResult && railwayResult.error) || "Railway save failed");
		}
		console.log('✅ Railway addition save: SUCCESS');

		try {
			await this.saveToLocalStorage(combinedOrderData);
		} catch (localError) {
			console.error("❌ localStorage error:", localError);
		}

		try {
			// Google Sheets backup — a failure here doesn't affect the order
			if (!this.googleSheets) {
				this.googleSheets = new GoogleSheetsIntegration();
			}
			const sheetsResult = await this.saveToGoogleSheetsClean(orderDataForSheets);
			if (sheetsResult.success) {
				console.log("✅ Successfully saved addition to Google Sheets");
			} else {
				console.error("❌ Google Sheets save failed (order is saved in Railway):", sheetsResult.error);
			}
		} catch (error) {
			console.error("❌ Google Sheets save failed (order is saved in Railway):", error);
		}
		
		this.updateLocalState(weekId, combinedOrderData);

		// Clear current selection
		this.mealQuantities[weekId] = {};
		this.mealOptions[weekId] = {};
		this.proteinBallSelections[weekId] = {};

		const totalNewMeals = newMeals.reduce((sum, meal) => sum + meal.quantity, 0);

		let successMessage = "";
		if (totalNewMeals > 0 && hasNewProteinBalls) {
			successMessage = `${totalNewMeals} meals + protein balls added`;
		} else if (totalNewMeals > 0) {
			successMessage = `${totalNewMeals} meals added`;
		} else {
			successMessage = "Protein balls added";
		}

		this.handleSuccessfulSave(successMessage, false);
	}

	// ERROR HANDLING AND USER FEEDBACK
	handleGoogleSheetsFailure(saveId, orderData, error) {
		try {
			console.log(`🚨 [${saveId}] Handling Google Sheets failure`);

			// Store failure for admin review
			const alertData = {
				type: "GOOGLE_SHEETS_FAILURE",
				saveAttemptId: saveId,
				customer: {
					id: orderData.customerId,
					email: orderData.customerEmail,
					name: orderData.customerName
				},
				weekId: orderData.weekId,
				deliveryDisplay: orderData.deliveryDisplay,
				totalMeals: orderData.meals.reduce((sum, meal) => sum + meal.quantity, 0),
				error: error.message,
				timestamp: new Date().toISOString(),
				mealDetails: orderData.meals.map((meal) => ({
					name: meal.name,
					quantity: meal.quantity,
					addOnCost: meal.totalAddOnCost || 0
				}))
			};

			// Store alert in localStorage for admin review
			const alertKey = `critical_alerts`;
			let alerts = [];
			try {
				const existing = localStorage.getItem(alertKey);
				alerts = existing ? JSON.parse(existing) : [];
			} catch (e) {
				alerts = [];
			}

			alerts.push(alertData);
			if (alerts.length > 50) {
				alerts = alerts.slice(-50);
			}

			localStorage.setItem(alertKey, JSON.stringify(alerts));
			console.log(`📧 [${saveId}] Alert stored for admin review`);
		} catch (alertError) {
			console.error(`❌ [${saveId}] Failed to create alert:`, alertError);
		}
	}

	showUserWarning() {
		try {
			// Remove any existing warning
			const existingWarning = document.getElementById("save-warning");
			if (existingWarning) {
				existingWarning.remove();
			}

			// Show a subtle warning to the user
			const warningHtml = `
			<div id="save-warning" style="
			  background: #fff3cd; 
			  color: #856404; 
			  padding: 15px; 
			  border-radius: 8px; 
			  margin: 15px 0;
			  border: 2px solid #ffeaa7;
			  font-size: 14px;
			  line-height: 1.4;
			">
			  <strong>⚠️ Important:</strong> Your meal selection has been saved to your device. We're experiencing a temporary technical issue with our main system, but your order is secure. Our team will ensure your selections are processed correctly for delivery.
			</div>`;

			// Add warning after the order summary
			const summarySection = document.querySelector("[data-order-summary]");
			if (summarySection) {
				summarySection.insertAdjacentHTML("afterend", warningHtml);

				// Remove warning after 15 seconds
				setTimeout(() => {
					const warning = document.getElementById("save-warning");
					if (warning) warning.remove();
				}, 15000);
			}

			console.log("⚠️ User warning displayed");
		} catch (error) {
			console.error("Failed to show user warning:", error);
		}
	}

	getUpcomingWeekForRedemption() {
		const now = new Date();
		const availableWeeks = this.getAvailableWeeks();
		
		// Find weeks that are not past deadline, sorted soonest first
		const activeWeeks = availableWeeks
			.filter(w => !w.isDeadlinePassed)
			.sort((a, b) => a.deliveryDate - b.deliveryDate);
		
		if (activeWeeks.length === 0) return null;
		
		// Get the week group of the soonest active week
		const upcomingGroup = activeWeeks[0].weekGroup;
		
		// Return the Sunday of that group (first delivery of the week)
		return activeWeeks.find(w => w.weekGroup === upcomingGroup && w.id.startsWith('sunday'))
			|| activeWeeks.find(w => w.weekGroup === upcomingGroup);
	}

	renderRedemptionBanner() {
		// Remove existing banner
		const existing = document.getElementById('redemption-banner');
		if (existing) existing.remove();

		if (!this.pendingMealRedemptions || this.pendingMealRedemptions <= 0) return;
		if (!this.selectedWeek) return;

		// Only show on the upcoming week
		const upcomingWeek = this.getUpcomingWeekForRedemption();
		if (!upcomingWeek || upcomingWeek.weekGroup !== this.selectedWeek.weekGroup) return;

		const mealWord = this.pendingMealRedemptions === 1 ? 'meal' : 'meals';
		
		const banner = document.createElement('div');
		banner.id = 'redemption-banner';
		banner.style.cssText = `
			background: #F0F7F1;
			border: 1.5px solid #A8D5B5;
			border-radius: 10px;
			padding: 14px 18px;
			margin-bottom: 20px;
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
		`;
		banner.innerHTML = `
			<div style="display:flex; align-items:center; gap:10px;">
				<span style="font-size:20px;">🎁</span>
				<div>
					<div style="font-size:14px; font-weight:600; color:#2D6A4F;">
						You have ${this.pendingMealRedemptions} redeemed ${mealWord} for this week!
					</div>
					<div style="font-size:12px; color:#52875F; margin-top:2px;">
						Select ${this.pendingMealRedemptions} extra ${mealWord} below to use your reward — 
						or they'll be assigned by the chef.
					</div>
				</div>
			</div>
			<button onclick="document.getElementById('redemption-banner').style.display='none'" 
				style="background:none; border:none; color:#52875F; font-size:18px; cursor:pointer; 
					padding:4px 8px; border-radius:6px; flex-shrink:0;"
				title="Dismiss">✕</button>
		`;

		// Insert at top of meal section
		const mealsGrid = document.querySelector('[data-meals-grid]');
		if (mealsGrid) {
			mealsGrid.parentNode.insertBefore(banner, mealsGrid);
		}
	}

	// STATE MANAGEMENT HELPERS
	updateLocalState(weekId, orderData) {
		this.weeklySelections[weekId] = {
			hasSelection: true,
			meals: orderData.meals,
			submissionTime: orderData.submissionTime,
			totals: this.calculateOrderTotals(orderData.meals),
			proteinBalls: orderData.proteinBalls || null,
			proteinBallsArray: orderData.proteinBallsArray || null
		};

		// Update protein ball selections in memory
		if (orderData.proteinBalls) {
			if (!this.proteinBallSelections[weekId]) {
				this.proteinBallSelections[weekId] = {};
			}
			// Use saved meal ID as key (supports both old meal-30 and new prb### IDs)
			const pbId = orderData.proteinBalls.id || "meal-30";
			this.proteinBallSelections[weekId][pbId] = orderData.proteinBalls.selectedOption;
		}
		// Also handle proteinBallsArray for multiple protein ball types
		if (orderData.proteinBallsArray && orderData.proteinBallsArray.length > 0) {
			if (!this.proteinBallSelections[weekId]) {
				this.proteinBallSelections[weekId] = {};
			}
			orderData.proteinBallsArray.forEach(pb => {
				const pbId = pb.id || "meal-30";
				this.proteinBallSelections[weekId][pbId] = pb.selectedOption;
			});
		}
	}

	calculateOrderTotals(meals) {
		let baseMeals = 0;
		let totalAddOnCost = 0;
		let addOnDetails = [];

		meals.forEach((meal) => {
			baseMeals += meal.quantity;

			if (meal.totalAddOnCost > 0) {
				totalAddOnCost += meal.totalAddOnCost;

				Object.values(meal.optionDetails || {}).forEach((option) => {
					if (option.isSelected && option.price > 0) {
						addOnDetails.push({
							mealName: meal.name,
							optionName: option.label,
							quantity: meal.quantity,
							unitPrice: option.price,
							totalPrice: option.price * meal.quantity
						});
					}
				});
			}
		});

		return {
			baseMeals,
			totalAddOnCost,
			addOnDetails,
			grandTotal: totalAddOnCost
		};
	}

	handleSuccessfulSave(successMessage, isNewSave = true) {
		if (isNewSave) {
			alert(
				"✅ Your meal selection has been saved!\n\n" +
					successMessage +
					" for " +
					this.selectedWeek.display
			);
		} else {
			alert("✅ Meals added successfully!");
		}

		// CLEAR THE CURRENT SELECTIONS - Fresh start after save
		this.clearAllWorkingSelections();
		this.renderWeeks();
		this.updateOrderSummary();

		if (this.customerPortal) {
			this.customerPortal.loadPortalData();
		}

		setTimeout(() => {
			window.scrollTo({
				top: 0,
				behavior: "smooth"
			});
		}, 500);
	}
	// CLEAR CURRENT SELECTIONS - Reset UI to clean state
	clearAllWorkingSelections() {
		// DON'T clear if viewing a past deadline week
		if (this.selectedWeek && this.selectedWeek.isDeadlinePassed) {
			const hasSelection = this.hasExistingSelection(this.selectedWeek.id);
			if (hasSelection) {
				console.log(
					"🛡️ Protected: Not clearing while viewing past deadline with selection"
				);
				return;
			}
		}

		// Clear ALL working selections across all weeks
		this.mealQuantities = {};
		this.mealOptions = {};
		this.proteinBallSelections = {};

		// Sides now live on the same page/save as everything else, so they
		// need to zero out after a save just like meals do — otherwise a
		// customer who saved sides once would see them still "selected"
		// even though the save already went through, which is confusing
		// when everything else on the page resets (September 2026).
		this.sidesQuantities = {};

		if (Math.random() < 0.1) {
			this.domCache = null;
		}

		// If there's a selected week, ensure its working state is clean
		if (this.selectedWeek) {
			const weekId = this.selectedWeek.id;
			this.mealQuantities[weekId] = {};
			this.mealOptions[weekId] = {};
			this.proteinBallSelections[weekId] = {};
			this.sidesQuantities[weekId] = {};
		}

		// Force UI to reflect clean state
		this.enforceCleanUIState();

		// Re-render the sides grid so its quantity counters visibly reset
		// to 0 immediately, same as meal cards do via enforceCleanUIState().
		if (this.selectedWeek && document.querySelector("[data-sides-grid]")) {
			this.renderSidesGrid();
		}

		console.log("🧹 Cleared ALL working selections - complete clean slate");
	}
	handlePageVisibilityChange() {
		if (!document.hidden) {
			// Page became visible again - ensure clean state
			console.log("📄 Page became visible - ensuring clean state");
			this.clearAllWorkingSelections();

			// If a week is selected, refresh its display
			if (this.selectedWeek) {
				setTimeout(() => {
					this.showMealSelection();
				}, 100);
			}
		}
	}
	async loadExistingSelections() {
		try {
			console.log("🔄 Loading meal selections...");

			// Check if we should sync from cloud
			const shouldSync = this.shouldSyncFromCloud();

			if (shouldSync) {
				console.log("☁️ Syncing from cloud...");
				await this.syncFromCloud();
			} else {
				console.log("📂 Loading from localStorage (cached)...");
				this.loadFromLocalStorage();
			}

			// ⚠️ ADD THIS DEBUGGING
			console.log("📊 After loading, weeklySelections:", this.weeklySelections);
			Object.keys(this.weeklySelections).forEach((weekId) => {
				if (
					weekId !== "lastUpdated" &&
					weekId !== "customerInfo" &&
					weekId !== "dataVersion"
				) {
					const weekData = this.weeklySelections[weekId];
					console.log(`  ${weekId}:`, {
						hasMeals: weekData.meals?.length > 0,
						mealCount: weekData.meals?.length || 0,
						hasProteinBalls: !!weekData.proteinBalls,
						proteinBallOption: weekData.proteinBalls?.selectedOption
					});
				}
			});

			// Always start with clean working state
			this.mealQuantities = {};
			this.mealOptions = {};
			this.proteinBallSelections = {};

			console.log("✅ Loaded", Object.keys(this.weeklySelections).length, "weeks");
			this.renderWeeks();
		} catch (error) {
			console.error("❌ Load error:", error);
			// Fallback to localStorage
			this.loadFromLocalStorage();
		}
	}
	shouldSyncFromCloud() {
		const storageKey = `meal_selections_${this.customer.id}`;
		const lastSyncKey = `last_sync_${this.customer.id}`;

		// Always sync if no local data
		const hasLocalData = localStorage.getItem(storageKey);
		if (!hasLocalData) {
			console.log("No local data found, will sync from cloud");
			return true;
		}

		// Check last sync time
		const lastSync = localStorage.getItem(lastSyncKey);
		if (!lastSync) {
			console.log("Never synced before, will sync from cloud");
			return true;
		}

		// Sync if last sync was more than 5 minutes ago
		const lastSyncTime = new Date(lastSync);
		const now = new Date();
		const minutesSinceSync = (now - lastSyncTime) / (1000 * 60);

		if (minutesSinceSync > 5) {
			console.log(
				`Last sync was ${minutesSinceSync.toFixed(
					1
				)} minutes ago, will sync from cloud`
			);
			return true;
		}

		console.log(
			`Last sync was ${minutesSinceSync.toFixed(1)} minutes ago, using cache`
		);
		return false;
	}
	loadFromLocalStorage() {
		try {
			const storageKey = `meal_selections_${this.customer.id}`;
			const stored = localStorage.getItem(storageKey);

			if (!stored) {
				console.log("📂 No localStorage data");
				this.weeklySelections = {};
				this.mealQuantities = {};
				this.mealOptions = {};
				this.proteinBallSelections = {};
				return;
			}

			const data = JSON.parse(stored);
			console.log("📂 Raw localStorage data:", data);

			// Reset state
			this.weeklySelections = {};
			this.mealQuantities = {};
			this.mealOptions = {};
			this.proteinBallSelections = {};

			Object.entries(data).forEach(([weekId, weekData]) => {
				if (["lastUpdated", "customerInfo", "dataVersion"].includes(weekId)) return;

				console.log(`📂 Processing ${weekId}:`, {
					hasMeals: weekData.meals?.length > 0,
					mealCount: weekData.meals?.length || 0,
					meals: weekData.meals,
					hasProteinBalls: !!weekData.proteinBalls
				});

				// Store basic selection data
				this.weeklySelections[weekId] = {
					hasSelection: true,
					meals: weekData.meals || [],
					submissionTime: weekData.submissionTime,
					proteinBalls: weekData.proteinBalls || null,
					deliveryDate: weekData.deliveryDate,
					deliveryDisplay: weekData.deliveryDisplay,
					savedAt: weekData.savedAt
				};

				// Reconstruct mealQuantities
				if (weekData.meals && weekData.meals.length > 0) {
					this.mealQuantities[weekId] = {};
					weekData.meals.forEach(meal => {
						this.mealQuantities[weekId][meal.id] = meal.quantity;
					});
				}

				// Reconstruct mealOptions
				if (weekData.meals && weekData.meals.length > 0) {
					this.mealOptions[weekId] = {};
					weekData.meals.forEach(meal => {
						if (meal.optionDetails && Object.keys(meal.optionDetails).length > 0) {
							this.mealOptions[weekId][meal.id] = {};
							Object.entries(meal.optionDetails).forEach(([optionKey, optionData]) => {
								this.mealOptions[weekId][meal.id][optionKey] = optionData.isSelected;
							});
						}
					});
				}

				// Reconstruct protein ball selections
				if (weekData.proteinBalls) {
					if (!this.proteinBallSelections[weekId]) {
						this.proteinBallSelections[weekId] = {};
					}
					const pbId = weekData.proteinBalls.id || "meal-30";
					this.proteinBallSelections[weekId][pbId] = weekData.proteinBalls.selectedOption;
				}
				// Also handle proteinBallsArray for multiple protein ball types
				if (weekData.proteinBallsArray && weekData.proteinBallsArray.length > 0) {
					if (!this.proteinBallSelections[weekId]) {
						this.proteinBallSelections[weekId] = {};
					}
					weekData.proteinBallsArray.forEach(pb => {
						const pbId = pb.id || "meal-30";
						this.proteinBallSelections[weekId][pbId] = pb.selectedOption;
					});
				}
			});

			console.log(
				"✅ Loaded from localStorage:",
				Object.keys(this.weeklySelections).length,
				"weeks"
			);
			console.log("   mealQuantities:", this.mealQuantities);
			console.log("   mealOptions:", this.mealOptions);
			console.log("   proteinBallSelections:", this.proteinBallSelections);
			
		} catch (error) {
			console.error("❌ localStorage load error:", error);
			this.weeklySelections = {};
			this.mealQuantities = {};
			this.mealOptions = {};
			this.proteinBallSelections = {};
		}
	}
	async loadFromRailway() {
		try {
			console.log('📥 Loading selections from Railway...');
			
			const response = await fetch(
				`${this.railwayAPI.baseUrl}/meal-selection/${this.customer.id}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json'
					}
				}
			);
			
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			const data = await response.json();
			console.log('✅ Railway load successful:', data);
			
			// Check if Railway has any data
			const hasRailwayData = data.success && 
								data.selections && 
								Object.keys(data.selections).length > 0;
			
			if (hasRailwayData) {
				// Railway has data - use it
				console.log(`✅ Found ${Object.keys(data.selections).length} weeks in Railway`);
				this.reconstructStateFromRailway(data.selections);
				return { success: true, source: 'railway', selections: data.selections };
			} else {
				// No Railway data - try Google Sheets fallback
				console.log('⚠️ No Railway data found, checking Google Sheets...');
				return await this.loadFromGoogleSheetsFallback();
			}
			
		} catch (error) {
			console.error('❌ Railway load failed:', error);
			// Railway failed - try Google Sheets fallback
			console.log('⚠️ Railway error, checking Google Sheets...');
			return await this.loadFromGoogleSheetsFallback();
		}
	}

	async loadPendingRedemptions() {
		if (!this.customer.id || this.customer.isGuest) return;
		try {
			const response = await fetch(`${this.railwayAPI.baseUrl}/loyalty/balance/${this.customer.id}`);
			const data = await response.json();
			if (data.success && data.pendingRedemptions) {
				// Sum up meal quantities from pending redemptions
				this.pendingMealRedemptions = data.pendingRedemptions.reduce((sum, r) => {
					return sum + (r.mealsQuantity || 0);
				}, 0);
				console.log(`🎁 Pending meal redemptions: ${this.pendingMealRedemptions}`);
				// A free order of protein balls (one 8-pack) waiting for the next
				// Sunday delivery — shown as a note on the protein ball Extras cards.
				this.pendingProteinBallRedemption = data.pendingRedemptions.some(r => r.includeProteinBalls);
				if (document.querySelector("[data-sides-grid] .side-card")) this.renderSidesGrid();
			}
		} catch (e) {
			console.warn('Could not load pending redemptions:', e);
		}
	}

	async loadFromGoogleSheetsFallback() {
		try {
			console.log('📥 Loading from Google Sheets (fallback)...');
			
			// Load from localStorage (which was populated from Google Sheets previously)
			const storedData = localStorage.getItem('mealSelections');
			if (!storedData) {
				console.log('ℹ️ No Google Sheets data found');
				return { success: true, source: 'none', selections: {} };
			}
			
			const parsed = JSON.parse(storedData);
			console.log('✅ Loaded from Google Sheets:', Object.keys(parsed).length, 'weeks');
			
			// Reconstruct state from Google Sheets data
			this.reconstructStateFromGoogleSheets(parsed);
			
			// OPTIONAL: Auto-migrate to Railway
			if (this.autoMigrateToRailway) {
				await this.migrateGoogleSheetsToRailway(parsed);
			}
			
			return { success: true, source: 'google-sheets', selections: parsed };
			
		} catch (error) {
			console.error('❌ Google Sheets fallback failed:', error);
			return { success: false, source: 'none', selections: {} };
		}
	}
	async syncFromCloud() {
		try {
			console.log("☁️ Fetching from Railway...");
			this.syncStatus.showSyncing();

			const result = await this.railwayAPI.loadSelections(this.customer.id, 4);

			if (result.success && result.selections) {
				// Apply Railway data
				this.weeklySelections = {};

				Object.entries(result.selections).forEach(([weekId, weekData]) => {
					if (["lastUpdated", "customerInfo", "dataVersion"].includes(weekId)) return;

					this.weeklySelections[weekId] = {
						hasSelection: weekData.hasSelections || false,
						meals: weekData.meals?.meals || [],
						submissionTime: weekData.submissionTime,
						proteinBalls: null, // Parse from meals if needed
						deliveryDate: weekData.deliveryDate,
						deliveryDisplay: weekData.deliveryDisplay || this.getDisplayForDate(weekData.deliveryDate),
						savedAt: weekData.createdAt,
						deadlinePassed: weekData.deadlinePassed || false,
						isEditable: weekData.isEditable !== false
					};
				});

				// Save to localStorage
				this.saveAllToLocalStorage(result.selections);

				// Update last sync time
				const lastSyncKey = `last_sync_${this.customer.id}`;
				localStorage.setItem(lastSyncKey, new Date().toISOString());

				this.syncStatus.showSuccess("Synced from Railway!");
				console.log("✅ Synced from Railway:", Object.keys(this.weeklySelections).length, "weeks");
			} else {
				// Fallback to localStorage
				console.log("No Railway data, using localStorage");
				this.loadFromLocalStorage();
				this.syncStatus.hide();
			}
		} catch (error) {
			console.error("❌ Railway sync failed:", error);
			// Fallback to localStorage
			this.loadFromLocalStorage();
			this.syncStatus.showOffline();
		}
	}
	async migrateGoogleSheetsToRailway(googleSheetsData) {
		console.log('🔄 Auto-migrating Google Sheets data to Railway...');
		
		let migrated = 0;
		let failed = 0;
		
		for (const [weekId, selection] of Object.entries(googleSheetsData)) {
			try {
				// Convert Google Sheets format to Railway format
				const orderData = {
					customerId: this.customer.id,
					customerEmail: this.customer.email,
					customerName: this.customer.name,
					weekId: weekId,
					deliveryDay: weekId.startsWith('sunday') ? 'Sunday' : 'Wednesday',
					deliveryDate: selection.deliveryDate,
					deliveryDisplay: selection.deliveryDisplay,
					submissionTime: selection.submissionTime || new Date().toISOString(),
					meals: selection.meals || [],
					proteinBalls: selection.proteinBalls || null,
					isReplacement: true
				};
				
				// Save to Railway
				const result = await this.railwayAPI.saveSelection(orderData);
				
				if (result.success) {
					migrated++;
					console.log(`✅ Migrated ${weekId} to Railway`);
				} else {
					failed++;
					console.error(`❌ Failed to migrate ${weekId}`);
				}
				
			} catch (error) {
				failed++;
				console.error(`❌ Error migrating ${weekId}:`, error);
			}
		}
		
		console.log(`🔄 Migration complete: ${migrated} succeeded, ${failed} failed`);
	}

	reconstructStateFromRailway(selections) {
		for (const [weekId, selection] of Object.entries(selections)) {
			// Initialize nested objects
			if (!this.mealQuantities[weekId]) this.mealQuantities[weekId] = {};
			if (!this.mealOptions[weekId]) this.mealOptions[weekId] = {};
			if (!this.proteinBallSelections[weekId]) this.proteinBallSelections[weekId] = {};
			
			// Reconstruct mealQuantities and mealOptions
			if (selection.meals && Array.isArray(selection.meals)) {
				selection.meals.forEach(meal => {
					this.mealQuantities[weekId][meal.id] = meal.quantity;
					
					const optionDetails = meal.optionDetails || meal.OptionDetails;
					if (optionDetails && Object.keys(optionDetails).length > 0) {
						this.mealOptions[weekId][meal.id] = {};
						for (const [optionKey, optionData] of Object.entries(optionDetails)) {
							// Handle both isSelected and IsSelected
							const isSelected = optionData.isSelected ?? optionData.IsSelected ?? false;
							this.mealOptions[weekId][meal.id][optionKey] = isSelected;
						}
					}
				});
			}
			
			// Reconstruct protein balls
			if (selection.proteinBalls && selection.proteinBalls.selectedOption !== 'none') {
				if (!this.proteinBallSelections[weekId]) {
					this.proteinBallSelections[weekId] = {};
				}
				const pbId = selection.proteinBalls.id || "meal-30";
				this.proteinBallSelections[weekId][pbId] = selection.proteinBalls.selectedOption;
			}
			// Also handle proteinBallsArray
			if (selection.proteinBallsArray && selection.proteinBallsArray.length > 0) {
				if (!this.proteinBallSelections[weekId]) {
					this.proteinBallSelections[weekId] = {};
				}
				selection.proteinBallsArray.forEach(pb => {
					const pbId = pb.id || "meal-30";
					this.proteinBallSelections[weekId][pbId] = pb.selectedOption;
				});
			}
			
			// Store in weeklySelections
			this.weeklySelections[weekId] = {
				hasSelection: selection.hasSelections,
				submissionTime: selection.submissionTime,
				meals: selection.meals || [],
				proteinBalls: selection.proteinBalls,
				deliveryDate: selection.deliveryDate,
				deliveryDay: selection.deliveryDay,
				mealCount: selection.mealCount
			};
		}
		
		console.log('✅ State reconstructed from Railway');
	}

	reconstructStateFromGoogleSheets(data) {
		// This is similar to your existing loadExistingSelections logic
		for (const [weekId, selection] of Object.entries(data)) {
			if (!this.mealQuantities[weekId]) this.mealQuantities[weekId] = {};
			if (!this.mealOptions[weekId]) this.mealOptions[weekId] = {};
			if (!this.proteinBallSelections[weekId]) this.proteinBallSelections[weekId] = {};
			
			// Reconstruct from Google Sheets format
			if (selection.meals && Array.isArray(selection.meals)) {
				selection.meals.forEach(meal => {
					this.mealQuantities[weekId][meal.id] = meal.quantity;
					
					if (meal.optionDetails) {
						this.mealOptions[weekId][meal.id] = {};
						for (const [optionKey, isSelected] of Object.entries(meal.optionDetails)) {
							this.mealOptions[weekId][meal.id][optionKey] = isSelected;
						}
					}
				});
			}
			
			if (selection.proteinBalls) {
				const pbId = selection.proteinBalls.id || "meal-30";
				this.proteinBallSelections[weekId][pbId] = selection.proteinBalls.selectedOption;
			}
			if (selection.proteinBallsArray && selection.proteinBallsArray.length > 0) {
				selection.proteinBallsArray.forEach(pb => {
					const pbId = pb.id || "meal-30";
					this.proteinBallSelections[weekId][pbId] = pb.selectedOption;
				});
			}
			
			this.weeklySelections[weekId] = selection;
		}
		
		console.log('✅ State reconstructed from Google Sheets');
	}

	saveAllToLocalStorage(selections) {
		try {
			const storageKey = `meal_selections_${this.customer.id}`;

			const saveData = {
				...selections,
				dataVersion: this.dataVersion,
				lastUpdated: new Date().toISOString(),
				customerInfo: {
					id: this.customer.id,
					name: this.customer.name,
					email: this.customer.email
				}
			};

			localStorage.setItem(storageKey, JSON.stringify(saveData));
			console.log("✅ Saved to localStorage");
		} catch (error) {
			console.error("❌ localStorage save error:", error);
		}
	}

	loadFromLocal() {
		try {
			const storageKey = `meal_selections_${this.customer.id}`;
			const stored = localStorage.getItem(storageKey);

			if (!stored) {
				console.log("📂 No local data found");
				return null;
			}

			const data = JSON.parse(stored);
			console.log("📂 Loaded local data:", Object.keys(data).length, "entries");

			return data;
		} catch (error) {
			console.error("❌ Error loading from local:", error);
			return null;
		}
	}

	async loadFromCloud() {
		try {
			console.log("☁️ Fetching data from Google Sheets...");

			// Use POST to avoid CORS
			const response = await fetch(this.googleSheets.webAppUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded"
				},
				body: new URLSearchParams({
					action: "getSelections",
					data: JSON.stringify({
						customerId: this.customer.id,
						customerEmail: this.customer.email
					})
				})
			});

			const result = await response.json();
			console.log("☁️ Cloud data received:", result);

			return result;
		} catch (error) {
			console.warn("☁️ Could not load from cloud:", error.message);
			throw error;
		}
	}

	mergeSelectionData(localData, cloudData) {
		console.log("🔀 === MERGE START ===");

		if (!cloudData || Object.keys(cloudData).length === 0) {
			return { selections: localData, hasChanges: false };
		}

		// CLOUD IS SOURCE OF TRUTH (it already has base + additions combined)
		const merged = {};
		let hasChanges = false;

		Object.keys(cloudData).forEach((weekId) => {
			if (
				weekId === "lastUpdated" ||
				weekId === "customerInfo" ||
				weekId === "dataVersion"
			) {
				return;
			}

			const cloudWeek = cloudData[weekId];
			const localWeek = localData ? localData[weekId] : null;

			// Always use cloud data (it's already the final reconstructed state)
			merged[weekId] = cloudWeek;

			if (!localWeek) {
				console.log(`  ✨ ${weekId}: New from cloud`);
				hasChanges = true;
			} else {
				// Check if different
				const cloudTime = new Date(
					cloudWeek.savedAt || cloudWeek.submissionTime
				).getTime();
				const localTime = new Date(
					localWeek.savedAt || localWeek.submissionTime
				).getTime();

				const cloudMealCount = cloudWeek.meals ? cloudWeek.meals.length : 0;
				const localMealCount = localWeek.meals ? localWeek.meals.length : 0;

				if (cloudTime !== localTime || cloudMealCount !== localMealCount) {
					console.log(
						`  🔄 ${weekId}: Updated from cloud (cloud: ${cloudMealCount} meals at ${new Date(
							cloudTime
						).toISOString()})`
					);
					hasChanges = true;
				}
			}
		});

		console.log("🔀 === MERGE END ===", {
			hasChanges: hasChanges,
			totalWeeks: Object.keys(merged).length
		});

		return {
			selections: merged,
			hasChanges: hasChanges
		};
	}

	detectStaleWeekData(data) {
		// Check if stored data has week dates that don't match current config
		const currentWeekDate = this.weekConfig.currentWeek.sunday.date;

		if (data["sunday-0"]) {
			const storedDate = data["sunday-0"].deliveryDate;
			if (storedDate && !storedDate.includes(currentWeekDate)) {
				return true; // Data is from old week structure
			}
		}
		return false;
	}
}
/*
function addNextWeek() {
	const newWeek = {
		id: "2025-09-22", // Change this to the next Monday's date
		mondayDate: "2025-09-22",
		thursdayDate: "2025-09-25",
		mondayDisplay: "Monday September 22",
		thursdayDisplay: "Thursday September 25",
		deadline: "2025-09-17T16:00:00",
		deadlineDisplay: "Wed 9/17 at 4:00 PM"
	};

	if (window.mealSelector) {
		return window.mealSelector.addNewWeek(newWeek);
	}
	return false;
}

// NEW: Utility to convert old data format (run once if needed)
function convertLegacyData() {
	const customers = ["customer1", "customer2"]; // Add actual customer IDs

	customers.forEach((customerId) => {
		const storageKey = `meal_selections_${customerId}`;
		const data = localStorage.getItem(storageKey);

		if (data) {
			try {
				const parsed = JSON.parse(data);
				const converted = {};

				// Convert old keys to new date-based keys
				Object.entries(parsed).forEach(([key, value]) => {
					if (key === "monday-0") {
						converted["monday-2025-09-08"] = value;
					} else if (key === "thursday-0") {
						converted["thursday-2025-09-11"] = value;
					} else if (key === "monday-1") {
						converted["monday-2025-09-15"] = value;
					} else if (key === "thursday-1") {
						converted["thursday-2025-09-18"] = value;
					} else {
						converted[key] = value; // Keep metadata
					}
				});

				localStorage.setItem(storageKey, JSON.stringify(converted));
				console.log(`Converted data for ${customerId}`);
			} catch (e) {
				console.error(`Failed to convert data for ${customerId}:`, e);
			}
		}
	});
}

function debugWeekConfiguration() {
	const selector = window.mealSelector;

	console.log("=== WEEK CONFIGURATION DEBUG ===");

	// Check available weeks
	const availableWeeks = selector.getAvailableWeeks();
	console.log(
		"Available weeks:",
		availableWeeks.map((w) => ({
			id: w.id,
			display: w.display
		}))
	);

	// Check menu keys
	console.log("Menu data keys:", Object.keys(selector.weeklyMenus));

	// Check for mismatches
	availableWeeks.forEach((week) => {
		const hasMenu = selector.weeklyMenus[week.id];
		console.log(
			`Week ${week.id} (${week.display}): ${
				hasMenu ? "✅ HAS MENU" : "❌ MISSING MENU"
			}`
		);

		if (hasMenu) {
			console.log(`  - ${hasMenu.length} meals available`);
		}
	});

	return {
		availableWeeks: availableWeeks.length,
		menuKeys: Object.keys(selector.weeklyMenus).length,
		mismatches: availableWeeks.filter((w) => !selector.weeklyMenus[w.id]).length
	};
}
*/
function verifyWeekIdFix() {
	const selector = window.mealSelector;

	console.log("=== VERIFYING WEEK ID FIX ===");

	const availableWeeks = selector.getAvailableWeeks();
	console.log("Generated week IDs:");
	availableWeeks.forEach((week) => {
		const hasMenu = selector.weeklyMenus[week.id] ? "✅" : "❌";
		console.log(`${hasMenu} ${week.id} (${week.display})`);
	});

	console.log("\nMenu keys:");
	Object.keys(selector.weeklyMenus).forEach((key) => {
		console.log(`📋 ${key}`);
	});

	const mismatches = availableWeeks.filter((w) => !selector.weeklyMenus[w.id]);
	console.log(
		`\n${
			mismatches.length === 0
				? "✅ ALL WEEK IDS MATCH MENU KEYS!"
				: "❌ Still have mismatches:"
		}`
	);

	if (mismatches.length > 0) {
		mismatches.forEach((week) => {
			console.log(`❌ Missing menu for: ${week.id}`);
		});
	}

	return mismatches.length === 0;
}
function debugEmailData() {
	const selector = window.mealSelector;
	const portal = selector.customerPortal;

	console.log("=== EMAIL DATA DEBUG ===");
	console.log("weeklySelections keys:", Object.keys(selector.weeklySelections));

	// Check for duplicates
	Object.entries(selector.weeklySelections).forEach(([weekId, data]) => {
		if (weekId !== "lastUpdated" && weekId !== "customerInfo") {
			console.log(`${weekId}:`, {
				hasSelection: data.hasSelection,
				mealsCount: data.meals ? data.meals.length : 0,
				submissionTime: data.submissionTime,
				proteinBalls: !!data.proteinBalls
			});
		}
	});

	return selector.weeklySelections;
}

// Debug functions for admin/developer use
window.mealSelectorDebug = {
	// View sync logs
	viewLogs: () => {
		if (window.mealSelector && window.mealSelector.syncLogger) {
			const logs = window.mealSelector.syncLogger.getLogs();
			console.table(logs);
			return logs;
		}
	},

	// Export logs to file
	exportLogs: () => {
		if (window.mealSelector && window.mealSelector.syncLogger) {
			window.mealSelector.syncLogger.exportLogs();
		}
	},

	// View recent errors
	viewErrors: () => {
		if (window.mealSelector && window.mealSelector.syncLogger) {
			const logs = window.mealSelector.syncLogger.getLogs();
			const errors = logs.filter((log) => log.event.includes("error"));
			console.table(errors);
			return errors;
		}
	},

	// Clear logs
	clearLogs: () => {
		if (window.mealSelector && window.mealSelector.syncLogger) {
			window.mealSelector.syncLogger.clearLogs();
			console.log("✅ Logs cleared");
		}
	},

	// Force sync
	forceSync: async () => {
		if (window.mealSelector) {
			console.log("🔄 Forcing sync...");
			await window.mealSelector.loadExistingSelections();
			console.log("✅ Sync complete");
		}
	},

	// View current sync status
	status: () => {
		if (window.mealSelector && window.mealSelector.syncStatus) {
			return window.mealSelector.syncStatus.getCurrentStatus();
		}
	}
};

console.log("💡 Debug tools available: window.mealSelectorDebug");

// Migrate any old monday-/thursday- localStorage keys to sunday-/wednesday-
function migrateDayNamesToSundayWednesday() {
	const storageKeys = Object.keys(localStorage).filter(k => k.startsWith('meal_selections_'));
	if (storageKeys.length === 0) return;

	storageKeys.forEach(storageKey => {
		try {
			const data = JSON.parse(localStorage.getItem(storageKey) || '{}');
			let changed = false;
			const migrated = {};

			Object.keys(data).forEach(key => {
				if (key.startsWith('monday-') && key.length > 7) {
					// monday-YYYY-MM-DD → sunday-YYYY-MM-DD (subtract 1 day)
					const datePart = key.replace('monday-', '');
					const d = new Date(datePart);
					d.setDate(d.getDate() - 1);
					const newKey = 'sunday-' + d.toISOString().split('T')[0];
					migrated[newKey] = { ...data[key], deliveryDay: 'Sunday' };
					console.log(`🔄 Migrated ${key} → ${newKey}`);
					changed = true;
				} else if (key.startsWith('thursday-') && key.length > 9) {
					// thursday-YYYY-MM-DD → wednesday-YYYY-MM-DD (subtract 1 day)
					const datePart = key.replace('thursday-', '');
					const d = new Date(datePart);
					d.setDate(d.getDate() - 1);
					const newKey = 'wednesday-' + d.toISOString().split('T')[0];
					migrated[newKey] = { ...data[key], deliveryDay: 'Wednesday' };
					console.log(`🔄 Migrated ${key} → ${newKey}`);
					changed = true;
				} else {
					migrated[key] = data[key];
				}
			});

			if (changed) {
				localStorage.setItem(storageKey, JSON.stringify(migrated));
				console.log(`✅ localStorage migration complete for ${storageKey}`);
			}
		} catch(e) {
			console.error('Migration error:', e);
		}
	});
}

// INITIALIZATION
document.addEventListener("DOMContentLoaded", () => {
	const customerData = window.customerData || {};

	// Simple check - you can expand this logic
	const isNonSubscriber =
		!customerData.tags ||
		!customerData.tags.some(
			(tag) =>
				tag.toLowerCase().includes("meals per week") ||
				tag.toLowerCase().includes("vip")
		);

	if (isNonSubscriber) {
		console.log("Non-subscriber view activated");
	}
	// Only initialize if we're showing the meal selector (not login screen)
	if (document.querySelector(".weekly-meal-selector")) {
		window.mealSelector = new WeeklyMealSelector(); // Store on window
	} else {
		console.log("Login screen detected - not initializing meal selector");
	}
});